import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import TextEditor from "../documentComponents/TextEditor";
import SideBar from "../documentComponents/SideBar";
import DocumentNavBar from "../documentComponents/DocumentNavBar";
import CombineDocument from "../documentComponents/CombineDocument";
import "../documentComponents/document.css";
import {
  scrollToSection,
  updateLastUpdated,
  fetchNames,
  scrollToCursorAndCenter,
  scrollToCursorAndCenterRobust,
  scrollToCursorAndCenterSimple,
  scrollToCursorAndCenterNative,
  scrollToCursorAndCenterTop,
  generateMedADocsPath,
  generateVitaMedDriveBasePath,
  parsePatientIdSegmentFromMedADocsJsonPath,
} from "../documentComponents/documentUtilities";
import { supabaseSchema } from "../components/supabaseClient";
import { uniqueChannelTopic } from "../utils/realtimeChannel";
import {
  calculateAge,
  getPatientAge,
  replaceAgePlaceholdersInDelta,
} from "../utils/ageCalculation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import QuillCursors from "quill-cursors";
import BlotFormatter from "@enzedonline/quill-blot-formatter2";
import {
  getSupabase,
  supabasePublic,
  getSupabaseAdmin,
} from "../components/supabaseClient";
import { isImpersonating } from "../utils/impersonation";
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Button as ChakraButton,
  HStack,
  List,
  ListItem,
  Text,
  Box,
  Spinner,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  VStack,
  Textarea,
  Checkbox,
  IconButton,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  faBars,
  faChevronLeft,
  faMicrophone,
  faFile,
  faShare,
  faCopy,
  faBug,
  faComment,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import ReactModal from "react-modal";
import FormsModal from "../components/FormsModal";
import FormsButtons from "../components/FormsButtons";
import "../utils/signEditorImages"; // registers the signed-image Quill blot
import {
  tableName,
  prompts,
  bucketName_files,
  colid,
  generateDocFilename,
  getTypeFieldIndex,
  getWebSocketURL,
  navy_blue,
  data_column,
  file_reference_column,
  royal_blue,
  supabaseUrl,
  getBackendURL,
} from "../components/env";
import ShareRowModal from "../components/ShareRowModal";
import {
  mergeSharedUsersAdd,
  mergeSharedUsersRemove,
  mergeShareRoleMaps,
  normalizeShareRole,
  parseSharedUserEntry,
  resolveTenantRowAccess,
  parseOwnerShareAllEntries,
  pickMoreRestrictiveShareRole,
  roleMapFromSharedUsers,
  tenantShareAllowsDocumentEdit,
} from "../utils/shareAccess";
import ResultsTable from "../components/table/ResultsTable";
import { templates as documentTemplates } from "../assets/templates/index";
import { templates } from "../assets/templates";
import { normalizeRole, isInGroup, getLabel } from "../config/roles";
import { extractAllSubmissionLinks } from "../utils/submissionUtils";
import { prepareFormProcessingData } from "../utils/formProcessingUtils";
import ProcessFormPromptEditor from "../components/ProcessFormPromptEditor";
import DocDiffViewer from "../documentComponents/DocDiffViewer";
import ProcessReportPromptEditor from "../components/ProcessReportPromptEditor";
import { useReportProcessing } from "../hooks/useReportProcessing";
import { trackUserEvent } from "../utils/userEventTracker";
import { capitalizeDictatedSegment } from "../utils/voiceDictationFormatting";
import {
  isUsableEhrId,
  isSamePatientRow,
  samePatientDecision,
  formatPatientLabel,
} from "../utils/priorVisitMatch";
import {
  getLiveTranscriptionSetting,
  getAutoSaveSetting,
  setAutoSaveSetting,
  setLiveTranscriptionSetting,
  setLiveFactExtractionSetting,
  convertDate,
} from "../utils/utils";
import { hasValidPassword } from "../utils/passwordProtection";
import PasswordModal from "../components/PasswordModal";
import { checkCreditLimitAndUpdateUI } from "../utils/creditLimitChecker";
import {
  checkFrontendStorageJsonBlobSize,
  isProbablyJsonStoragePath,
  preflightRestrictedStorageJsonSize,
} from "../utils/storageJsonFrontendLimit";
import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import { QuillBinding } from "y-quill";
import typingDetection from "../utils/typingDetection";
import activeDocumentTracker from "../utils/activeDocumentTracker";
import globalPresenceManager from "../utils/globalPresenceManager";
import {
  analyzeTextDuplication,
  logDuplicationDebug,
} from "../utils/duplicationDebug.js";
import { createVersioningSystem } from "../documentComponents/versioningSystem";
import RightSideBar from "../components/RightSideBar";
import { useDocumentOperations } from "../hooks/useDocumentOperations";
import { useCollaboration } from "../hooks/useCollaboration";
import AudioRecorder from "./AudioRecorder";
import CollaborationDebugPanel from "../components/CollaborationDebugPanel";
import { convertScheduledTime } from "../utils/scheduleTimeDisplay.js";
import {
  hydrateScheduleRowsTimezoneSource,
  fetchViewerIanaTimezone,
} from "../utils/scheduleRowTimezone.js";

// Register the cursor module before using it
Quill.register("modules/cursors", QuillCursors);
// Register blot formatter so images can be drag-resized
Quill.register("modules/blotFormatter", BlotFormatter);

// Add a debug log after the imports to verify templates are loaded
console.log("Available templates:", Object.keys(documentTemplates));

const getDefaultTemplateTypeForSchema = (schema) => {
  if (schema === "STUDENT") return "ST";
  if (schema === "SOC_WORK" || schema === "MISC") return "SO";
  return null;
};

/**
 * EditableDocumentPage Component
 *
 * This component renders an editable document page with a text editor, sidebar, and navigation.
 * It handles real-time synchronization using Supabase channels and saves the full document content.
 */
export default function EditableDocumentPage({
  setShowPersistentComponent,
  setStateData,
  setSession,
  setLink,
  setCondFunc,
  setOverride,
  setRefId,
  onOpenAIAssistant,
  isAIAssistantOpen = false,
  aiAssistantData = null,
}) {
  const editorRef = useRef(); // Reference to the TextEditor component
  const [safeShareMode, setSafeShareMode] = useState(false);
  const [safeShareMethod, setSafeShareMethod] = useState("mask"); // "mask", "redact", or "hash"
  const [originalContents, setOriginalContents] = useState(null);
  const [isDeidentifying, setIsDeidentifying] = useState(false);
  const location = useLocation(); // React Router location for passing state
  const navigate = useNavigate(); // React Router navigation
  const { uuid, documentId: urlDocumentId } = useParams(); // Get patient ID + optional doc ID from URL
  const [searchParams] = useSearchParams(); // For join-via-link (?docId=...)
  const joinDocId = searchParams.get("docId"); // Present when user follows a share link
  const toast = useToast(); // Toast for notifications

  // Extract state from location, falling back to sessionStorage on refresh
  const _ssKey = `edp_state_${uuid || location.pathname}`;
  const _rawState = (() => {
    if (location.state && Object.keys(location.state).length > 0) {
      // Persist key fields so a page refresh can recover them
      try {
        const persist = {};
        const s = location.state;
        const nameField = prompts[0][0];
        if (s.filePath) persist.filePath = s.filePath;
        if (s[nameField]) persist[nameField] = s[nameField];
        if (s.name) persist.name = s.name;
        if (s.date_) persist.date_ = s.date_;
        if (s.id) persist.id = s.id;
        if (s.EHR_ID) persist.EHR_ID = s.EHR_ID;
        if (s[colid]) persist[colid] = s[colid];
        if (s.time_) persist.time_ = s.time_;
        if (s.type) persist.type = s.type;
        if (s.isCombined) persist.isCombined = s.isCombined;
        if (s.scheduleId) persist.scheduleId = s.scheduleId;
        if (s.isMagicNotes) persist.isMagicNotes = s.isMagicNotes;
        if (s.driveTitle) persist.driveTitle = s.driveTitle;
        if (s.user_id) persist.user_id = s.user_id;
        if (s.shared_users) persist.shared_users = s.shared_users;
        sessionStorage.setItem(_ssKey, JSON.stringify(persist));
      } catch { /* ignore */ }
      return location.state;
    }
    // Refresh: location.state is lost — restore from sessionStorage
    try {
      const saved = sessionStorage.getItem(_ssKey);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return {};
  })();
  const {
    filePath: passedFilePath,
    [prompts[0][0]]: patientName,
    name,
    date_,
    id,
    EHR_ID: ehrIdFromState,
    [colid]: emailFromState,
    time_,
    type,
    isCombined = false,
    createNew = false,
    useMostRecentMatch = false,
    scheduleId,
    aiGeneratedContent,
    pasteAIGeneratedContent,
    isMagicNotes: _isMagicNotesState = false,
    driveTitle = null,
    user_id: rowOwnerId = null,
    /** From schedule row when navigating from PatientPage — used until patient fetch fills shared_users */
    shared_users: scheduleSharedUsersFromState = null,
    // Payload to auto-trigger "Use Doc as Template" mode on mount
    // (set when navigating from PatientPage or SchedulesTable)
    docAsTemplatePayload = null,
    skipLargeStorageJsonFetch = false,
  } = _rawState;

  const ehrIdForDocTitle =
    ehrIdFromState != null && String(ehrIdFromState).trim() !== ""
      ? String(ehrIdFromState).trim()
      : parsePatientIdSegmentFromMedADocsJsonPath(passedFilePath) || "";
  const emailForDocTitle =
    emailFromState != null && String(emailFromState).trim() !== ""
      ? String(emailFromState).trim()
      : "";

  /** Drop doc-as-template payload from history so refresh / remount does not re-open the diff modal */
  const stripDocAsTemplateNavigationState = useCallback(() => {
    try {
      localStorage.removeItem("notesai_docAsTemplate_payload");
    } catch (e) {
      /* ignore */
    }
    const st = location.state;
    if (!st?.docAsTemplatePayload) return;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: { ...st, docAsTemplatePayload: undefined } },
    );
  }, [
    location.pathname,
    location.search,
    location.hash,
    location.state,
    navigate,
  ]);

  // isMagicNotes is true when explicitly set OR when arriving via a share link
  const isMagicNotes = _isMagicNotesState || !!joinDocId;
  const [userRole, setUserRole] = useState(null);

  // Use name for combined documents, patientName for individual documents
  const extractedName = isCombined ? name : patientName;

  // Fallback to today's date if date_ is missing
  const effectiveDate =
    date_ && date_.trim() !== ""
      ? date_
      : new Date().toISOString().split("T")[0];

  // Use schedule ID from location state for active document tracking
  // This is the schedule ID that matches the schedules table
  const validId = scheduleId || uuid || id || "unknown";

  // Helper function to format time to HH:MM (24h)
  const formatTimeToHHMM = (timeStr) => {
    if (!timeStr) return "";
    // Handle "H:MM AM/PM" or "HH:MM:SS AM/PM" (12h format stored by old modal versions)
    const ampmMatch = timeStr.match(
      /^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i,
    );
    if (ampmMatch) {
      let h = parseInt(ampmMatch[1], 10);
      const m = ampmMatch[2];
      const meridiem = ampmMatch[3].toUpperCase();
      if (meridiem === "PM" && h !== 12) h += 12;
      if (meridiem === "AM" && h === 12) h = 0;
      return `${String(h).padStart(2, "0")}:${m}`;
    }
    // Handle "HH:MM:SS±TZ" or "HH:MM:SS" or "HH:MM" (24h format)
    const timeWithoutTZ = timeStr.split(/[+-]/)[0];
    const timeParts = timeWithoutTZ.split(":");
    return timeParts.length >= 2
      ? `${timeParts[0]}:${timeParts[1]}`
      : timeWithoutTZ;
  };

  // Use current time for document content, but keep original time for navbar title
  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  // Fix patient type early to prevent wrong template loading
  const roleDefaultTemplateType =
    getLabel(userRole, 'templateType') ||
    getDefaultTemplateTypeForSchema(supabaseSchema);
  const documentTypeDefaultReadyKey = type || userRole || supabaseSchema || "";
  const knownTemplateTypes = new Set(["NP", "NS", "FFP", "FFS", "SO", "ST", "SOC", "STU"]);
  let correctedType =
    roleDefaultTemplateType && !knownTemplateTypes.has(type)
      ? roleDefaultTemplateType
      : type || roleDefaultTemplateType || "NP";
  // Only correct specific patients who are known to be NP but have wrong type in database
  if (
    (extractedName === "Nori, Riham" || extractedName === "Riham, Nori") &&
    (correctedType === "FFP" || correctedType === "FFS")
  ) {
    console.log(
      "🔧 Early type correction: changing",
      correctedType,
      "to NP for patient:",
      extractedName,
    );
    correctedType = "NP";
  }

  // Fetch patient's scheduled time from database if time_ is not provided or is from old document
  const [patientScheduledTime, setPatientScheduledTime] = useState(null);

  // Use patient scheduled time if available, otherwise use passed time (same rules as schedule table)
  const originalNavbarTime = formatTimeToHHMM(time_);
  const navbarTime = patientScheduledTime || originalNavbarTime;
  const documentTime = getCurrentTime();

  // Component state
  const [time, setTime] = useState(""); // Document time
  const [sections, setSections] = useState([]); // Document sections for sidebar

  // Debug sections state changes (only for combined documents)
  useEffect(() => {
    if (isCombined && sections.length > 0) {
      console.log(
        "🔍 Sections state updated for combined document:",
        sections.length,
        sections.map((s) => s.title),
      );
    }
  }, [sections, isCombined]);
  const [activeSection, setActiveSection] = useState(null); // Currently active section
  const [isSidebarVisible, setIsSidebarVisible] = useState(false); // Sidebar visibility toggle - default to collapsed
  
  // Comments and Highlights States
  const [comments, setComments] = useState([]);
  const commentsRef = useRef([]); // Ref to always access current comments in callbacks
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectedText, setSelectedText] = useState("");
  const [commentFloatPosition, setCommentFloatPosition] = useState(null);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [newCommentText, setNewCommentText] = useState("");
  const commentIndexUpdateRef = useRef(null); // Ref to hold the comment index update function
  const [selectedHighlightColor, setSelectedHighlightColor] = useState('#FEF08A');
  
  // Available highlight colors for comments
  const HIGHLIGHT_COLORS = ['#FEF08A', '#BFDBFE', '#BBF7D0', '#FBCFE'];
  const [showCommentsSidebar, setShowCommentsSidebar] = useState(false);
  const [commentBubblePositions, setCommentBubblePositions] = useState([]);
  const [activeCommentId, setActiveCommentId] = useState(null);
  // Yellow highlight for commented text (document text is black, so it reads well).
  const COMMENT_HIGHLIGHT_COLOR = '#ffeb3b';
  // Tracks the temporary highlight shown while a comment is being composed, so the
  // selection stays visibly marked after the editor loses focus. Removed on cancel.
  const pendingCommentHighlightRef = useRef(null);

  const delayedScanExecuted = useRef(false); // Flag to prevent multiple delayed scans
  const quillRefs = useRef([]); // References to Quill instances
  const containerRefs = useRef([]); // References to editor containers
  const [pages, setPages] = useState([0]); // Pagination state
  const isPaginatingRef = useRef(false); // Flag to prevent text-change during pagination
  const [documentId, setDocumentId] = useState(); // Document ID from Supabase
  const clientId = useRef(Date.now().toString()); // Unique client ID for this session

  const [patients, setPatients] = useState([]); // List of patients for the right sidebar
  const [isPatientListOpen, setIsPatientListOpen] = useState(false); // Patient list drawer state

  // Versioning system state
  const [versioningSystem, setVersioningSystem] = useState(null);
  /** { label, isOutOfDate, isStaleVersion, savedNumber, loading } from VersioningSystem */
  const [documentVersionStatus, setDocumentVersionStatus] = useState(null);

  // Save state indicators
  const [isSaving, setIsSaving] = useState(false); // Saving in progress
  const [saveSuccess, setSaveSuccess] = useState(true); // Save success status

  // Collaboration debug panel state
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);

  // WebSocket collaboration state
  const [isConnected, setIsConnected] = useState(true); // Start as connected to avoid brief offline flash
  const [connectionStatus, setConnectionStatus] = useState("connecting"); // Track actual connection status
  const bindingRef = useRef(null); // Yjs-Quill binding reference
  // Provider is now managed by useCollaboration hook
  // const [provider, setProvider] = useState(null); // WebSocket provider - REMOVED, now from hook
  // Initialize userColors from globalPresenceManager cache so avatars have colors immediately
  const [userColors, setUserColors] = useState(() => {
    const cachedColors = globalPresenceManager.getCachedUserColors();
    if (cachedColors.size > 0) {
      console.log(
        "🎨 Initialized userColors from cache:",
        cachedColors.size,
        "colors",
      );
    }
    return cachedColors;
  });

  // Add this at the top with other state declarations
  const [isUnmounting, setIsUnmounting] = useState(false);

  // State for join-via-link (?docId=...) — title resolved async from documents table
  const [joinDocTitle, setJoinDocTitle] = useState(null);
  // Store text-change handlers for cleanup
  const textChangeHandlersRef = useRef([]);
  const hasPastedAIContentRef = useRef(false);
  const filePathRef = useRef(null); // Mirrors filePath state for use in Quill image handler
  /** Skip downloading existing JSON (e.g. navigation after oversized prompt or PatientPage "Create new"). */
  const skipLargeStorageJsonFetchRef = useRef(false);
  /** User chose "Create new" on oversized-file dialog — next fetch treats storage as missing. */
  const forceOversizeNewDocumentRef = useRef(false);
  const lastEditorSelectionRef = useRef(null); // Last non-null selection, used for image insertion after toolbar focus steal
  /** Paste + document drag/drop listeners (page 0); cleared on unmount */
  const quillImageDomCleanupRef = useRef(null);
  /** Always points at the current handleSaveDoc — used from event handlers
   *  registered in initializeQuill, which captures stale closures otherwise. */
  const handleSaveDocRef = useRef(null);
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [isLoadingDocument, setIsLoadingDocument] = useState(true); // Track document loading state
  const [hasRenderedInitialDocument, setHasRenderedInitialDocument] =
    useState(false);
  const [highlightedUser, setHighlightedUser] = useState(null);

  // Track if component is mounted to prevent accessing provider after unmount
  const isMountedRef = useRef(true);

  // Initialize mounted ref on mount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Update userColors from cache when globalPresenceManager updates
  // This ensures we get colors for users as soon as they're available
  useEffect(() => {
    const updateColorsFromCache = () => {
      const cachedColors = globalPresenceManager.getCachedUserColors();
      if (cachedColors.size > 0) {
        setUserColors((prevColors) => {
          // Merge cached colors with existing colors
          const newColors = new Map(prevColors);
          let hasNewColors = false;
          cachedColors.forEach((color, userId) => {
            if (!newColors.has(userId) || newColors.get(userId) !== color) {
              newColors.set(userId, color);
              hasNewColors = true;
            }
          });
          if (hasNewColors) {
            return newColors;
          }
          return prevColors;
        });
      }
    };

    // Subscribe to presence updates to get color updates
    const unsubscribe = globalPresenceManager.subscribe(() => {
      updateColorsFromCache();
    });

    // Also check cache immediately on mount
    updateColorsFromCache();

    return () => {
      unsubscribe();
    };
  }, []); // Run on mount only - subscription handles updates

  // Multi-selection state for multi-highlighting functionality
  const [multiSelections, setMultiSelections] = useState([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [showMultiSelectIndicator, setShowMultiSelectIndicator] =
    useState(false);

  // Drag and drop state for cut-and-paste functionality
  const [isDragging, setIsDragging] = useState(false);
  const [dragData, setDragData] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);

  // Synchronization locks to prevent race conditions
  const contentLoadingLock = useRef(false);
  const websocketSetupLock = useRef(false);
  const [contentLoadingComplete, setContentLoadingComplete] = useState(false);
  const contentLoadAttempts = useRef(0);
  const maxContentLoadAttempts = 3;
  const contentStabilityCheck = useRef(null);

  // Content stability check to prevent premature operations
  const checkContentStability = (callback, delay = 1000) => {
    if (contentStabilityCheck.current) {
      clearTimeout(contentStabilityCheck.current);
    }

    contentStabilityCheck.current = setTimeout(() => {
      const editor = editorRef.current?.getEditor(0);
      if (editor && editor.getLength() > 1) {
        // Additional check: ensure content is actually rendered in DOM
        const lines = editor.getLines();
        const hasRenderedContent =
          lines &&
          lines.length > 0 &&
          lines.some(
            (line) =>
              line.domNode &&
              line.domNode.innerText &&
              line.domNode.innerText.trim() !== "",
          );

        if (hasRenderedContent) {
          console.log("🔍 Content stability check passed, executing callback");
          callback();
        } else {
          console.log(
            "🔍 Content stability check failed, content not rendered in DOM",
          );
          // Retry with shorter delay
          setTimeout(() => {
            const editor = editorRef.current?.getEditor(0);
            if (editor && editor.getLength() > 1) {
              const lines = editor.getLines();
              const hasRenderedContent =
                lines &&
                lines.length > 0 &&
                lines.some(
                  (line) =>
                    line.domNode &&
                    line.domNode.innerText &&
                    line.domNode.innerText.trim() !== "",
                );
              if (hasRenderedContent) {
                console.log(
                  "🔍 Content stability check retry passed, executing callback",
                );
                callback();
              } else {
                console.log(
                  "🔍 Content stability check retry failed, executing callback anyway",
                );
                callback(); // Execute anyway to prevent infinite waiting
              }
            }
          }, 500);
        }
      } else {
        console.log("🔍 Content stability check failed, content not ready");
        // Retry once more with a shorter delay
        setTimeout(() => {
          const editor = editorRef.current?.getEditor(0);
          if (editor && editor.getLength() > 1) {
            console.log(
              "🔍 Content stability check retry passed, executing callback",
            );
            callback();
          } else {
            console.log(
              "🔍 Content stability check retry failed, executing callback anyway",
            );
            callback(); // Execute anyway to prevent infinite waiting
          }
        }, 500);
      }
    }, delay);
  };

  // Recording state - exactly like PatientPage
  const [condition, setCondition] = useState(false);

  // Password modal state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [isTemplatePromptOpen, setIsTemplatePromptOpen] = useState(false);
  const [storageOversizeModalOpen, setStorageOversizeModalOpen] =
    useState(false);
  const [storageOversizeMessage, setStorageOversizeMessage] = useState("");
  const [shouldUseTemplate, setShouldUseTemplate] = useState(false);
  const [isWaitingForTemplateDecision, setIsWaitingForTemplateDecision] =
    useState(false);
  const [loadedTemplateType, setLoadedTemplateType] = useState("");

  // Process form prompt editor modal state
  const [isPromptEditorOpen, setIsPromptEditorOpen] = useState(false);
  const [currentPatientType, setCurrentPatientType] = useState("");
  const [promptEditorInitialData, setPromptEditorInitialData] = useState(null);

  // Appointment version schedule status (shown in VersionHistoryModal)
  const [appointmentVersionInfo] = useState(null);

  // Doc-as-template diff viewer state
  const [isDiffViewerOpen, setIsDiffViewerOpen] = useState(false);
  const [isGeneratingDiff, setIsGeneratingDiff] = useState(false);
  const [diffOriginalText, setDiffOriginalText] = useState("");
  const [diffNewText, setDiffNewText] = useState("");
  const [isDocProcessingLocked, setIsDocProcessingLocked] = useState(false);
  /** Shared with versioning init effect — eager creation from doc-as-template avoids null VS */
  const versioningInstanceRef = useRef(null);
  /** Canonical plain text at start of doc-as-template (must match apply for offset edits) */
  const docTemplateCanonSnapshotRef = useRef(null);

  // Process report prompt editor modal state
  const {
    isReportPromptEditorOpen,
    setIsReportPromptEditorOpen,
    currentReportType,
    reportPromptEditorInitialData,
    handleProcessReport,
    handleReportProcessingRun,
  } = useReportProcessing({
    onRun: ({
      fullPrompt,
      currentReportFullText,
      currentReportFileName,
      currentReportType,
      currentReportFileNames,
    }) => {
      const initialMessages = [
        {
          id: Date.now().toString(),
          type: "user",
          content: fullPrompt,
          timestamp: new Date(),
        },
      ];
      if (onOpenAIAssistant) {
        onOpenAIAssistant(
          actualPatientId ||
            (validId !== "unknown" ? validId : null) ||
            filePath,
          patientData || userInfo ? [patientData || userInfo] : null,
          currentReportFullText || "",
          initialMessages,
          ["patient_info", "uploaded_files"],
          null,
          null,
          false,
          currentReportFileName || null,
          currentReportType || null,
          currentReportFileNames || null,
          effectiveDate,
        );
      } else {
        toast({
          title: "Error",
          description: "AI Assistant is not available",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    },
    toast,
  });

  // Forms and Reports state - using shared FormsModal component
  const [isFormsModalOpen, setIsFormsModalOpen] = useState(false);
  const [hasForms, setHasForms] = useState(false);
  const [hasReports, setHasReports] = useState(false);
  // Track if patient has qualifying forms (for disabling Process form button)
  const [hasQualifyingForms, setHasQualifyingForms] = useState(false);
  const [isCheckingQualifyingForms, setIsCheckingQualifyingForms] =
    useState(true);

  // Reports modal state (same as PatientPage)
  const [currentFolderPath, setCurrentFolderPath] = useState("");
  const [previousFolderPath, setPreviousFolderPath] = useState("");
  const [currentFolderFiles, setCurrentFolderFiles] = useState([]);
  const [fileClassifications, setFileClassifications] = useState({});
  const [modalIsOpen, setModalIsOpen] = useState(false);

  // Share row (entity) with other users — tenant roles only
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareSchedule, setShareSchedule] = useState(null);
  const [shareModalUsers, setShareModalUsers] = useState([]);
  const [shareModalLoading, setShareModalLoading] = useState(false);
  const [shareModalAlreadySharedIds, setShareModalAlreadySharedIds] = useState(
    new Set(),
  );
  const [shareModalCurrentUserId, setShareModalCurrentUserId] = useState(null);
  const [shareModalRemovingId, setShareModalRemovingId] = useState(null);
  const [shareModalSharedUserRoles, setShareModalSharedUserRoles] = useState(
    {},
  );
  const [shareModalUpdatingRoleId, setShareModalUpdatingRoleId] =
    useState(null);
  const [shareBadgeCount, setShareBadgeCount] = useState(0);
  const [isOwnRow, setIsOwnRow] = useState(false);
  const [tenantShareAllOwnerIds, setTenantShareAllOwnerIds] = useState([]);
  const [tenantShareAllOwnerRoles, setTenantShareAllOwnerRoles] = useState({});
  /** Authoritative user_id + shared_users for the schedule row (validId); avoids wrong row when fetching patient by name only. */
  const [scheduleRowAccessSnapshot, setScheduleRowAccessSnapshot] =
    useState(null);
  const [tenantRowAccess, setTenantRowAccess] = useState({
    level: "none",
    canEditDocument: false,
    canEditScheduleFields: false,
    canDeleteRow: false,
    canShareRow: false,
  });

  // Session user id is required for tenant share resolution; must run even when
  // rowOwnerId is missing from navigation (common for shared editors opening a link).
  useEffect(() => {
    const syncSessionUser = async () => {
      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user?.id) {
        setShareModalCurrentUserId(session.user.id);
        if (rowOwnerId != null && String(rowOwnerId).trim() !== "") {
          setIsOwnRow(session.user.id === rowOwnerId);
        }
      }
    };
    syncSessionUser();
  }, [rowOwnerId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!shareModalCurrentUserId || !isInGroup(userRole, 'tenantSchema')) {
        if (!cancelled) {
          setTenantShareAllOwnerIds([]);
          setTenantShareAllOwnerRoles({});
        }
        return;
      }
      try {
        const backendUrl = getBackendURL();
        const res = await fetch(
          `${backendUrl}/api/shared-all-owner-ids/?user_id=${encodeURIComponent(shareModalCurrentUserId)}`,
        );
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && Array.isArray(json.owner_ids))
          setTenantShareAllOwnerIds(json.owner_ids);
        if (res.ok && json.owner_roles && typeof json.owner_roles === "object")
          setTenantShareAllOwnerRoles(json.owner_roles);
      } catch (_) {
        if (!cancelled) {
          setTenantShareAllOwnerIds([]);
          setTenantShareAllOwnerRoles({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userRole, shareModalCurrentUserId]);

  // --- Comments and Highlights Business Logic ---

  const fetchComments = useCallback(async () => {
    if (!validId || validId === "unknown") return;
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase
        .from("document_comments")
        .select("*")
        .eq("appointment_id", validId)
        .order("created_at", { ascending: true });
      if (!error && data) {
        setComments(data);
      }
    } catch (e) {
      console.error("Error fetching comments:", e);
    }
  }, [validId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Keep commentsRef in sync with comments state
  useEffect(() => {
    commentsRef.current = comments;
  }, [comments]);

  // Real-time synchronization for comments
  useEffect(() => {
    if (!validId || validId === "unknown") return;
    let subscription;
    (async () => {
      const supabase = await getSupabase();
      // Unique topic per mount — a fixed `comments:<id>` topic returns an
      // already-subscribed channel on remount (removeChannel is async) or when
      // another view subscribes to the same id, and .on() then throws an
      // uncaught "…after subscribe()" error that crashes the page.
      subscription = supabase
        .channel(uniqueChannelTopic(`comments:${validId}`))
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: supabaseSchema,
            table: "document_comments",
            filter: `appointment_id=eq.${validId}`,
          },
          () => {
            fetchComments();
          }
        )
        .subscribe();
    })();
    return () => {
      if (subscription) {
        getSupabase().then(supabase => supabase.removeChannel(subscription));
      }
    };
  }, [validId, fetchComments]);

  const updateBubblePositions = useCallback(() => {
    const currentComments = commentsRef.current;
    const newPositions = [];
    currentComments.forEach((comment) => {
      if (comment.status !== 'open' || !comment.selection_range) return;
      let { index, length, pageIndex } = comment.selection_range;
      const quill = quillRefs.current[pageIndex || 0];
      if (quill) {
        try {
          if (comment.highlight_text) {
            const currentText = quill.getText(index, length);
            if (currentText !== comment.highlight_text) {
              const fullText = quill.getText();
              let searchIdx = fullText.indexOf(comment.highlight_text);
              let closestDistance = Infinity;
              let bestIndex = -1;
              while (searchIdx !== -1) {
                const distance = Math.abs(searchIdx - index);
                if (distance < closestDistance) {
                  closestDistance = distance;
                  bestIndex = searchIdx;
                }
                searchIdx = fullText.indexOf(comment.highlight_text, searchIdx + 1);
              }
              if (bestIndex !== -1) {
                const newRange = { ...comment.selection_range, index: bestIndex, length: comment.highlight_text.length };
                comment.selection_range = newRange;
                index = bestIndex;
                length = comment.highlight_text.length;
                
                // Format the new range to make sure it is highlighted
                quill.formatText(bestIndex, comment.highlight_text.length, 'background', comment.highlight_color || '#FEF08A');
                
                (async () => {
                  try {
                    const supabase = await getSupabase();
                    await supabase
                      .from('document_comments')
                      .update({ selection_range: newRange })
                      .eq('id', comment.id);
                  } catch (err) {
                    console.error("Error updating comment index in DB:", err);
                  }
                })();
              }
            }
          }
          const bounds = quill.getBounds(index, length || 1);
          if (bounds) {
            const editorEl = quill.container;
            const containerWidth = editorEl.clientWidth || 800;
            
            // Bubble and card positions are relative to the portal target container
            // (containerRefs.current[pageIdx] which has position:relative).
            // quill.getBounds() returns coordinates relative to the quill container,
            // which IS the containerRef element — so we use bounds directly, no offsetTop needed.
            const bubbleTop = bounds.top - 24;
            const bubbleLeft = Math.max(10, Math.min(containerWidth - 30, bounds.left + bounds.width - 12));
            
            const cardWidth = 260;
            const cardLeft = Math.max(10, Math.min(containerWidth - cardWidth - 10, bounds.left + (bounds.width - cardWidth) / 2));
            const cardTop = bounds.top > 130 ? bounds.top - 130 : bounds.top + bounds.height + 8;

            newPositions.push({
              id: comment.id,
              comment: comment,
              top: bubbleTop,
              left: bubbleLeft,
              cardTop: cardTop,
              cardLeft: cardLeft
            });
          }
        } catch (e) {
          // ignore
        }
      }
    });
    setCommentBubblePositions(newPositions);
  }, []);

  useEffect(() => {
    updateBubblePositions();
    // Use a slower 2s interval to avoid excessive re-renders interfering with scroll
    const interval = setInterval(updateBubblePositions, 2000);
    
    const attachedQuills = [];
    quillRefs.current.forEach((quill) => {
      if (quill) {
        quill.on('text-change', updateBubblePositions);
        attachedQuills.push(quill);
      }
    });
    
    return () => {
      clearInterval(interval);
      attachedQuills.forEach((quill) => {
        quill.off('text-change', updateBubblePositions);
      });
    };
  }, [comments, updateBubblePositions]);

  // Selection change listener to detect highlighted text for commenting
  useEffect(() => {
    // Keep references to attached handlers so we can remove them properly
    const attachedHandlers = [];

    const attachListeners = () => {
      quillRefs.current.forEach((quill, idx) => {
        if (quill && !quill._commentSelectionHandler) {
          const handler = (range, oldRange, source) => {
            if (range && range.length > 0) {
              const text = quill.getText(range.index, range.length);
              setSelectedRange({ index: range.index, length: range.length, pageIndex: idx });
              setSelectedText(text);
              
              try {
                const bounds = quill.getBounds(range.index, range.length);
                const editorEl = quill.container;
                const containerWidth = editorEl.clientWidth || 800;
                
                // Position is relative to the portal container (same coordinate space as updateBubblePositions)
                const inputWidth = 260;
                const floatLeft = Math.max(10, Math.min(containerWidth - inputWidth - 10, bounds.left + (bounds.width - inputWidth) / 2));
                const floatTop = bounds.top > 130 ? bounds.top - 130 : bounds.top + bounds.height + 8;
                
                setCommentFloatPosition({ top: floatTop, left: floatLeft });
              } catch (e) {
                console.error("Error getting selection bounds:", e);
              }
            } else {
              // Clear float position unless focused inside comments sidebar
              // or the floating new-comment input itself.
              setTimeout(() => {
                const activeEl = document.activeElement;
                const insideComments = activeEl && (activeEl.closest('.comments-sidebar') || activeEl.closest('.comment-float-btn') || activeEl.closest('.comment-float-input'));
                if (!insideComments) {
                  setCommentFloatPosition(null);
                  // Exit comment mode when the selection is dismissed by other
                  // means (click-away, etc.) so a stale isAddingComment flag
                  // doesn't re-open the New Comment box on every later highlight.
                  setIsAddingComment(false);
                }
              }, 200);
            }
          };
          quill._commentSelectionHandler = handler;
          quill.on('selection-change', handler);
          attachedHandlers.push({ quill, handler });
        }
      });
    };

    // Attach immediately and poll for new quill instances (pages added dynamically)
    attachListeners();
    const interval = setInterval(attachListeners, 2000);

    return () => {
      clearInterval(interval);
      attachedHandlers.forEach(({ quill, handler }) => {
        quill.off('selection-change', handler);
        delete quill._commentSelectionHandler;
      });
    };
  }, [quillRefs]);

  /**
   * Update comment indices when text is inserted or deleted
   * This ensures comment bubbles stay aligned with their highlighted text
   */
  const updateCommentIndicesOnTextChange = useCallback(async (delta, pageIndex) => {
    const currentComments = commentsRef.current;
    if (!currentComments || currentComments.length === 0) return;
    
    // Calculate the net index change at each position
    // Positive = text inserted, Negative = text deleted
    let positionOffset = 0;
    const commentUpdates = [];
    
    currentComments.forEach((comment) => {
      if (!comment.selection_range || comment.selection_range.pageIndex !== pageIndex) {
        return; // Skip comments on different pages or without selection_range
      }
      
      const commentIndex = comment.selection_range.index;
      const commentLength = comment.selection_range.length || 0;
      
      // Calculate how this text change affects this comment's position
      let newIndex = commentIndex;
      let newLength = commentLength;
      
      // Process delta ops to find insertions/deletions before this comment
      if (delta.ops) {
        let currentPos = 0;
        
        for (const op of delta.ops) {
          if (op.retain !== undefined) {
            // Skip retain characters
            if (typeof op.retain === 'number') {
              currentPos += op.retain;
            }
          } else if (op.insert !== undefined && typeof op.insert === 'string') {
            // Text inserted at currentPos
            const insertLength = op.insert.length;
            if (currentPos < commentIndex) {
              // Insertion is before this comment - shift comment right
              newIndex += insertLength;
            } else if (currentPos >= commentIndex && currentPos < commentIndex + commentLength) {
              // Insertion is within this comment's highlight - shift end right
              newLength += insertLength;
            }
            currentPos += insertLength;
          } else if (op.delete !== undefined) {
            // Text deleted at currentPos
            const deleteLength = op.delete;
            if (currentPos < commentIndex) {
              // Deletion is before this comment - shift comment left
              newIndex = Math.max(0, newIndex - Math.min(deleteLength, commentIndex - currentPos));
            } else if (currentPos >= commentIndex && currentPos < commentIndex + commentLength) {
              // Deletion is within this comment's highlight
              const charsDeletedInHighlight = Math.min(deleteLength, commentIndex + commentLength - currentPos);
              newLength = Math.max(0, newLength - charsDeletedInHighlight);
            }
            // Note: we don't advance currentPos for delete ops (they don't consume chars in new content)
          }
        }
      }
      
      // If the index or length changed, mark for update
      if (newIndex !== commentIndex || newLength !== commentLength) {
        commentUpdates.push({
          id: comment.id,
          selection_range: {
            ...comment.selection_range,
            index: newIndex,
            length: newLength
          }
        });
      }
    });
    
    // Batch update comments with new indices
    if (commentUpdates.length > 0) {
      try {
        const supabase = await getSupabase();
        for (const update of commentUpdates) {
          await supabase
            .from('document_comments')
            .update({ selection_range: update.selection_range })
            .eq('id', update.id);
        }
        console.log(`Updated indices for ${commentUpdates.length} comments`);
        // Directly update local state with new indices to keep commentsRef in sync
        setComments(prevComments => {
          const updatedComments = prevComments.map(comment => {
            const update = commentUpdates.find(u => u.id === comment.id);
            return update ? { ...comment, selection_range: update.selection_range } : comment;
          });
          return updatedComments;
        });
      } catch (error) {
        console.warn('Error updating comment indices:', error);
      }
    }
  }, []);

  // Keep the ref updated with the latest function reference
  useEffect(() => {
    commentIndexUpdateRef.current = updateCommentIndicesOnTextChange;
  }, [updateCommentIndicesOnTextChange]);

  const handleSaveComment = async () => {
    if (!newCommentText.trim() || !validId) return;
    try {
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      
      const { data: userData } = await supabase
        .from('users')
        .select('name')
        .eq('id', user?.id)
        .single();
      
      const userName = userData?.name || user?.email || 'Anonymous';
      
      const commentData = {
        appointment_id: validId,
        user_id: user?.id,
        user_name: userName,
        user_email: user?.email,
        content: newCommentText,
        highlight_text: selectedText,
        selection_range: selectedRange,
        highlight_color: selectedHighlightColor,
        status: 'open'
      };

      const { data, error } = await supabase
        .from('document_comments')
        .insert([commentData])
        .select();

      if (!error) {
        setIsAddingComment(false);
        setNewCommentText("");
        setSelectedHighlightColor('#FEF08A');
        setCommentFloatPosition(null);
        setShowCommentsSidebar(true);
        fetchComments();
        
        // Keep the yellow highlight on the saved comment's text (already applied
        // when comment mode opened). Null the ref so the cleanup effect doesn't strip it.
        pendingCommentHighlightRef.current = null;
        if (selectedRange && editorRef.current) {
          const quill = quillRefs.current[selectedRange.pageIndex || 0];
          if (quill) {
            quill.formatText(selectedRange.index, selectedRange.length, 'background', selectedHighlightColor);
            if (typeof handleSaveDocRef?.current === 'function') {
              handleSaveDocRef.current();
            } else if (typeof handleSaveDoc === 'function') {
              handleSaveDoc();
            }
          }
        }
      } else {
        console.error("Error saving comment:", error);
      }
    } catch (e) {
      console.error("Error saving comment:", e);
    }
  };

  const handleResolveComment = async (comment) => {
    try {
      const supabase = await getSupabase();
      const { error } = await supabase
        .from('document_comments')
        .update({ status: 'resolved' })
        .eq('id', comment.id);
      if (!error) {
        if (comment.selection_range) {
          const { index, length, pageIndex } = comment.selection_range;
          const quill = quillRefs.current[pageIndex || 0];
          if (quill) {
            let targetIndex = index;
            let targetLength = length;
            if (comment.highlight_text) {
              const text = quill.getText();
              const sub = text.substring(targetIndex, targetIndex + targetLength);
              if (sub !== comment.highlight_text) {
                let foundIdx = text.indexOf(comment.highlight_text);
                while (foundIdx !== -1) {
                  const format = quill.getFormat(foundIdx, 1);
                  if (format && format.background) {
                    targetIndex = foundIdx;
                    targetLength = comment.highlight_text.length;
                    break;
                  }
                  foundIdx = text.indexOf(comment.highlight_text, foundIdx + 1);
                }
              }
            }
            quill.formatText(targetIndex, targetLength, 'background', false);
            if (typeof handleSaveDocRef?.current === 'function') {
              handleSaveDocRef.current();
            } else if (typeof handleSaveDoc === 'function') {
              handleSaveDoc();
            }
          }
        }
        fetchComments();
        if (activeCommentId === comment.id) {
          setActiveCommentId(null);
        }
      }
    } catch (e) {
      console.error("Error resolving comment:", e);
    }
  };

  const handleDeleteComment = async (comment) => {
    try {
      const supabase = await getSupabase();
      const { error } = await supabase
        .from('document_comments')
        .delete()
        .eq('id', comment.id);
      if (!error) {
        if (comment.selection_range) {
          const { index, length, pageIndex } = comment.selection_range;
          const quill = quillRefs.current[pageIndex || 0];
          if (quill) {
            let targetIndex = index;
            let targetLength = length;
            if (comment.highlight_text) {
              const text = quill.getText();
              const sub = text.substring(targetIndex, targetIndex + targetLength);
              if (sub !== comment.highlight_text) {
                let foundIdx = text.indexOf(comment.highlight_text);
                while (foundIdx !== -1) {
                  const format = quill.getFormat(foundIdx, 1);
                  if (format && format.background) {
                    targetIndex = foundIdx;
                    targetLength = comment.highlight_text.length;
                    break;
                  }
                  foundIdx = text.indexOf(comment.highlight_text, foundIdx + 1);
                }
              }
            }
            quill.formatText(targetIndex, targetLength, 'background', false);
            if (typeof handleSaveDocRef?.current === 'function') {
              handleSaveDocRef.current();
            } else if (typeof handleSaveDoc === 'function') {
              handleSaveDoc();
            }
          }
        }
        fetchComments();
        if (activeCommentId === comment.id) {
          setActiveCommentId(null);
        }
      }
    } catch (e) {
      console.error("Error deleting comment:", e);
    }
  };

  const handleToolbarCommentClick = () => {
    if (selectedRange && selectedRange.length > 0) {
      // Apply the yellow highlight up front so the selected text stays visibly
      // marked while focus moves to the comment input. Applied 'silent' so it
      // doesn't dirty the doc; removed again if the user cancels (see effect below).
      const quill = quillRefs.current[selectedRange.pageIndex || 0];
      if (quill) {
        quill.formatText(selectedRange.index, selectedRange.length, 'background', COMMENT_HIGHLIGHT_COLOR, 'silent');
        pendingCommentHighlightRef.current = { ...selectedRange };
      }
      setIsAddingComment(true);
      setShowCommentsSidebar(true);
    } else {
      setShowCommentsSidebar(prev => !prev);
    }
  };

  // Remove the temporary highlight when the user leaves "add comment" without
  // saving (Cancel button, or click-away which clears isAddingComment). On a
  // successful save the ref is nulled first, so the highlight is kept.
  useEffect(() => {
    if (!isAddingComment && pendingCommentHighlightRef.current) {
      const { index, length, pageIndex } = pendingCommentHighlightRef.current;
      const quill = quillRefs.current[pageIndex || 0];
      if (quill) {
        quill.formatText(index, length, 'background', false, 'silent');
      }
      pendingCommentHighlightRef.current = null;
    }
  }, [isAddingComment]);

  // --- End Comments and Highlights Business Logic ---


  // Load schedule row by id so tenant share role matches DB (name-only patient fetch can return another row).
  useEffect(() => {
    let cancelled = false;
    const rowId = validId;
    const looksLikeUuid =
      typeof rowId === "string" &&
      rowId !== "unknown" &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        rowId,
      );
    if (!looksLikeUuid) {
      setScheduleRowAccessSnapshot(null);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from(tableName)
          .select("user_id, shared_users")
          .eq("id", rowId)
          .maybeSingle();
        if (cancelled) return;
        if (error || !data) {
          setScheduleRowAccessSnapshot(null);
          return;
        }
        setScheduleRowAccessSnapshot({
          user_id: data.user_id ?? null,
          shared_users: Array.isArray(data.shared_users)
            ? data.shared_users
            : null,
        });
      } catch {
        if (!cancelled) setScheduleRowAccessSnapshot(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [validId]);

  // Share document (drive / Magic Notes) — document row id + modal state.
  // filePath state must be defined before the tenant-access effect (~line 895)
  // so that effect can branch on Drive-doc paths without a TDZ error.
  const [filePath, setFilePath] = useState(passedFilePath || null);
  const [documentRowId, setDocumentRowId] = useState(null);
  const [isDriveDocOwner, setIsDriveDocOwner] = useState(false);
  const [shareDocumentModalOpen, setShareDocumentModalOpen] = useState(false);
  const [shareDocumentUsers, setShareDocumentUsers] = useState([]);
  const [shareDocumentLoading, setShareDocumentLoading] = useState(false);
  const [shareDocumentAlreadySharedIds, setShareDocumentAlreadySharedIds] =
    useState(new Set());
  const [shareDocumentCurrentUserId, setShareDocumentCurrentUserId] =
    useState(null);
  const [shareDocumentRemovingId, setShareDocumentRemovingId] = useState(null);
  const [shareDocumentLink, setShareDocumentLink] = useState("");

  const [autoSaveSetting, setAutoSaveSettingState] = useState(true);
  const [userManuallyChangedAutoSave, setUserManuallyChangedAutoSave] =
    useState(false);
  const [liveTranscriptionEnabled, setLiveTranscriptionEnabled] =
    useState(true);
  const [liveFactExtractionEnabled, setLiveFactExtractionEnabled] =
    useState(true);
  const [actualPatientId, setActualPatientId] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [viewerTimezone, setViewerTimezone] = useState("America/New_York");

  // Keep floating bubbles (chat/recording) in sync on edit-document refreshes.
  // These bubbles rely on patientContextUpdate, which is otherwise emitted from patient/schedule pages.
  useEffect(() => {
    const resolvedPatientId =
      actualPatientId ||
      id ||
      (validId && validId !== "unknown" ? validId : null);
    if (!resolvedPatientId) return;

    const fallbackPatientData =
      patientData ||
      ({
        id: resolvedPatientId,
        patient_name: extractedName || name || patientName || "",
        ehr_id: ehrIdFromState || null,
      });

    window.dispatchEvent(
      new CustomEvent("patientContextUpdate", {
        detail: {
          patientId: resolvedPatientId,
          patientData: fallbackPatientData,
        },
      }),
    );
  }, [
    actualPatientId,
    id,
    validId,
    patientData,
    extractedName,
    name,
    patientName,
    ehrIdFromState,
  ]);

  useEffect(() => {
    // Drive-opened JSON docs (filePath under New/Vita-MedDrive/) have no patient
    // row to resolve from. Owner is determined by path; collaborators are stored
    // in documents.share_grants. Resolve here so tenant-schema users (student,
    // socialworker, other) who own a Drive doc don't get stuck in viewer mode
    // when rowOwnerId/patientData are absent.
    const isDriveDoc =
      typeof filePath === "string" && filePath.startsWith("New/Vita-MedDrive/");
    if (isDriveDoc) {
      if (isDriveDocOwner) {
        setTenantRowAccess({
          level: "owner",
          canEditDocument: true,
          canEditScheduleFields: true,
          canDeleteRow: true,
          canShareRow: true,
        });
        return;
      }
      if (!shareModalCurrentUserId || !documentRowId) return;
      let cancelled = false;
      (async () => {
        try {
          const supabase = await getSupabase();
          const { data: docRow } = await supabase
            .from("documents")
            .select("share_grants")
            .eq("id", documentRowId)
            .maybeSingle();
          if (cancelled) return;
          const grants = Array.isArray(docRow?.share_grants)
            ? docRow.share_grants
            : [];
          const myGrants = grants.filter(
            (g) =>
              g &&
              String(g.user_id ?? "") === String(shareModalCurrentUserId),
          );
          const rank = { read: 1, editor: 2, admin: 3 };
          let bestRole = null;
          for (const g of myGrants) {
            const r = String(g.role || "").toLowerCase();
            if (rank[r] && (bestRole == null || rank[r] > rank[bestRole])) {
              bestRole = r;
            }
          }
          if (bestRole === "admin") {
            setTenantRowAccess({
              level: "admin",
              canEditDocument: true,
              canEditScheduleFields: false,
              canDeleteRow: true,
              canShareRow: true,
            });
          } else if (bestRole === "editor") {
            setTenantRowAccess({
              level: "editor",
              canEditDocument: true,
              canEditScheduleFields: false,
              canDeleteRow: false,
              canShareRow: false,
            });
          } else if (bestRole === "read") {
            setTenantRowAccess({
              level: "viewer",
              canEditDocument: false,
              canEditScheduleFields: false,
              canDeleteRow: false,
              canShareRow: false,
            });
          } else {
            setTenantRowAccess({
              level: "none",
              canEditDocument: false,
              canEditScheduleFields: false,
              canDeleteRow: false,
              canShareRow: false,
            });
          }
        } catch (_) {
          /* leave at current value */
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    // Never merge user_id + shared_users from different sources. dedupeSharedUsersArray
    // picks the HIGHER-privilege role on duplicates, so merging an authoritative snapshot
    // with patientData (fetched by name, which can return a different row) risks promoting
    // a viewer to admin. Pick one source and use it end-to-end.
    let rowUserIdResolved = null;
    let sharedUsersResolved = null;
    const snapOwner =
      scheduleRowAccessSnapshot?.user_id != null &&
      String(scheduleRowAccessSnapshot.user_id).trim() !== ""
        ? scheduleRowAccessSnapshot.user_id
        : null;
    if (snapOwner) {
      rowUserIdResolved = snapOwner;
      sharedUsersResolved = Array.isArray(scheduleRowAccessSnapshot?.shared_users)
        ? scheduleRowAccessSnapshot.shared_users
        : [];
    } else if (rowOwnerId && scheduleSharedUsersFromState != null) {
      rowUserIdResolved = rowOwnerId;
      sharedUsersResolved = scheduleSharedUsersFromState;
    } else if (
      patientData?.user_id != null &&
      String(patientData.user_id).trim() !== ""
    ) {
      rowUserIdResolved = patientData.user_id;
      sharedUsersResolved = Array.isArray(patientData?.shared_users)
        ? patientData.shared_users
        : [];
    } else if (rowOwnerId) {
      rowUserIdResolved = rowOwnerId;
      sharedUsersResolved = null;
    }

    if (!rowUserIdResolved) {
      console.log("🔐 [TenantAccess] Waiting for rowUserIdResolved — sources:", {
        snap: scheduleRowAccessSnapshot?.user_id,
        patient: patientData?.user_id,
        owner: rowOwnerId,
      });
      return;
    }
    if (userRole == null) {
      console.log("🔐 [TenantAccess] Waiting for userRole");
      return;
    }
    if (!isInGroup(userRole, 'tenantSchema')) {
      console.log("🔐 [TenantAccess] Non-tenant user (role:", userRole, ") → owner access");
      setTenantRowAccess({
        level: "owner",
        canEditDocument: true,
        canEditScheduleFields: true,
        canDeleteRow: true,
        canShareRow: true,
      });
      return;
    }
    if (!shareModalCurrentUserId) {
      // Don't actively set "none" — leave at initial default and wait for session to resolve.
      // Setting "none" here would trigger quill.enable(false) prematurely and cause a flash
      // of read-only before the access is fully resolved.
      console.log("🔐 [TenantAccess] Tenant user waiting for session userId");
      return;
    }
    const acc = resolveTenantRowAccess({
      viewerId: shareModalCurrentUserId,
      rowUserId: rowUserIdResolved,
      sharedUsers: sharedUsersResolved,
      shareAllOwnerIds: tenantShareAllOwnerIds,
      shareAllOwnerRoles: tenantShareAllOwnerRoles,
    });
    console.log("🔐 [TenantAccess] Resolved:", acc.level, {
      viewerId: shareModalCurrentUserId?.slice(0, 8),
      rowUserId: String(rowUserIdResolved).slice(0, 8),
      sharedUsersCount: Array.isArray(sharedUsersResolved) ? sharedUsersResolved.length : 0,
      sharedUsersResolved,
      shareAllOwnerIds: tenantShareAllOwnerIds,
    });
    setTenantRowAccess(acc);
  }, [
    shareModalCurrentUserId,
    rowOwnerId,
    patientData?.user_id,
    patientData?.shared_users,
    scheduleSharedUsersFromState,
    userRole,
    tenantShareAllOwnerIds,
    tenantShareAllOwnerRoles,
    scheduleRowAccessSnapshot,
    filePath,
    isDriveDocOwner,
    documentRowId,
  ]);

  // userRole == null → role not fetched yet; treat as read-only so save / delete
  // / template handlers don't run with stale defaults during the load window.
  const tenantDocReadOnly =
    userRole == null ||
    (isInGroup(userRole, 'tenantSchema') &&
      !tenantShareAllowsDocumentEdit(tenantRowAccess));

  useEffect(() => {
    (async () => {
      const supabase = await getSupabase();
      setViewerTimezone(await fetchViewerIanaTimezone(supabase));
    })();
  }, []);

  // Use patient data for scheduled time when available
  useEffect(() => {
    if (patientData && patientData.time_) {
      console.log(
        "🕐 Using patient scheduled time from main fetch:",
        patientData.time_,
      );
      const shown = convertScheduledTime(
        patientData.date_,
        patientData.time_,
        patientData.timezone_source ?? null,
        viewerTimezone,
      );
      setPatientScheduledTime(shown !== "N/A" ? shown : null);
    } else {
      setPatientScheduledTime(null);
    }
  }, [patientData, viewerTimezone]);

  // Fetch patient data from database
  useEffect(() => {
    const fetchPatientData = async () => {
      // Skip fetching patient data for combined documents
      if (isCombined) {
        console.log(
          "📝 Med-A Docs: Skipping patient data fetch for combined document",
        );
        return;
      }

      try {
        const supabase = await getSupabase();

        let patientRecord = null;

        if (extractedName) {
          console.log(
            "📝 Med-A Docs: Fetching patient data for name:",
            extractedName,
          );
          const { data } = await supabase
            .from(tableName)
            .select("*")
            .eq(prompts[0][0], extractedName)
            .order("created_at", { ascending: false })
            .limit(1)
            .single();
          patientRecord = data;
        } else if (
          validId &&
          validId !== "unknown" &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(validId)
        ) {
          // Fallback: fetch by row ID when name is missing (page refresh / direct link)
          console.log(
            "📝 Med-A Docs: Fetching patient data by row ID (refresh fallback):",
            validId,
          );
          const { data } = await supabase
            .from(tableName)
            .select("*")
            .eq("id", validId)
            .maybeSingle();
          patientRecord = data;
        } else if (
          uuid &&
          uuid !== "unknown" &&
          !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid)
        ) {
          // Fallback: uuid is EHR_ID from URL (not a UUID) — query by EHR_ID
          console.log(
            "📝 Med-A Docs: Fetching patient data by EHR_ID (URL param):",
            uuid,
          );
          const { data } = await supabase
            .from(tableName)
            .select("*")
            .eq("EHR_ID", uuid)
            .order("date_", { ascending: false })
            .limit(1)
            .maybeSingle();
          patientRecord = data;
        } else if (documentRowId) {
          // Fallback: look up document path via documentRowId, then find schedule row (share link / no URL id)
          console.log(
            "📝 Med-A Docs: Fetching patient data via documentRowId (share link fallback):",
            documentRowId,
          );
          const { data: docRow } = await supabase
            .from("documents")
            .select("path")
            .eq("id", documentRowId)
            .maybeSingle();
          const docPath = docRow?.path;
          if (docPath && docPath.includes("MedADocs")) {
            const pathParts = docPath.split("/");
            const dateSegment = pathParts.find((p) =>
              /^\d{4}-\d{2}-\d{2}$/.test(p),
            );
            const rawFilename = pathParts[pathParts.length - 1].replace(
              /\.json$/i,
              "",
            );
            // Long format: "HH:MM name - EHR type" → extract EHR between last " - " and last space
            const lastDashIdx = rawFilename.lastIndexOf(" - ");
            let ehrFromPath = null;
            let nameFromPath = null;
            if (lastDashIdx >= 0) {
              const afterDash = rawFilename.substring(lastDashIdx + 3).trim();
              const afterParts = afterDash.split(/\s+/);
              ehrFromPath = afterParts[0] || null;
              // Name is between time and " - "
              const beforeDash = rawFilename.substring(0, lastDashIdx);
              const nameMatch = beforeDash.match(/^\d{1,2}:\d{2}\s+(.+)/);
              nameFromPath = nameMatch ? nameMatch[1].trim() : beforeDash.trim();
            } else {
              // Simple format: "name-EHR" → EHR after last hyphen
              const lastHyphen = rawFilename.lastIndexOf("-");
              if (lastHyphen >= 0) {
                ehrFromPath = rawFilename.substring(lastHyphen + 1).trim();
                nameFromPath = rawFilename.substring(0, lastHyphen).trim();
              }
            }
            if (dateSegment && (ehrFromPath || nameFromPath)) {
              let query = supabase.from(tableName).select("*").eq("date_", dateSegment);
              if (
                ehrFromPath &&
                !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(ehrFromPath)
              ) {
                query = query.eq("EHR_ID", ehrFromPath);
              } else if (nameFromPath) {
                query = query.eq(prompts[0][0], nameFromPath);
              }
              const { data } = await query
                .order("created_at", { ascending: false })
                .limit(1)
                .maybeSingle();
              patientRecord = data;
            }
          }
        }

        if (patientRecord) {
          console.log("📝 Med-A Docs: Found patient record:", patientRecord);
          const [hydrated] = await hydrateScheduleRowsTimezoneSource(
            supabase,
            [patientRecord],
          );
          setPatientData(hydrated);
          setActualPatientId(hydrated.id);
          sessionStorage.setItem("preservedPatientId", hydrated.id);
        } else {
          console.log(
            "📝 Med-A Docs: No patient record found for:",
            extractedName || validId,
          );
        }
      } catch (error) {
        console.error("📝 Med-A Docs: Error in fetchPatientData:", error);
      }
    };

    fetchPatientData();
  }, [extractedName, isCombined, validId, uuid, documentRowId]);


  // Helper function to check if a form name is a qualifying questionnaire
  const isQualifyingFormName = (formName) => {
    if (!formName) return false;
    const lowerName = formName.toLowerCase();
    // Check for Sleep questionnaire keywords
    const isSleepForm =
      lowerName.includes("sleep") &&
      (lowerName.includes("new") ||
        lowerName.includes("patient") ||
        lowerName.includes("question"));
    // Check for Pulmonary questionnaire keywords
    const isPulmonaryForm =
      lowerName.includes("pulmonary") &&
      (lowerName.includes("new") ||
        lowerName.includes("patient") ||
        lowerName.includes("question"));
    return isSleepForm || isPulmonaryForm;
  };

  // Track isLoadingDocument changes to diagnose button-blocking issues
  useEffect(() => {
    console.log(
      "📄 EditableDocumentPage: isLoadingDocument =",
      isLoadingDocument,
      "(toolbar pointer-events:",
      isLoadingDocument
        ? "'none' — buttons blocked by CSS"
        : "'auto' — buttons accessible",
      ")",
    );
  }, [isLoadingDocument]);

  // Check for forms — same logic as PatientPage: use actualPatientId (resolved after
  // doc load) then fall back to uuid (URL param) then id (location state).
  // Intentionally excludes validId (which may be scheduleId, not the patient UUID)
  // and effectiveDate (date changes should not re-trigger the patient forms check).
  useEffect(() => {
    const patientId = actualPatientId || uuid || id;
    console.log(
      "🔍 EditableDocumentPage: forms check — patientId:",
      patientId,
      "| actualPatientId:",
      actualPatientId,
      "| uuid:",
      uuid,
      "| id:",
      id,
      "| validId (excluded):",
      validId,
      "| effectiveDate:",
      effectiveDate,
    );

    if (!patientId) {
      setHasForms(false);
      setHasQualifyingForms(false);
      setIsCheckingQualifyingForms(false);
      console.log(
        "🔍 EditableDocumentPage: No patient ID available for forms check",
      );
      return;
    }

    setIsCheckingQualifyingForms(true);
    extractAllSubmissionLinks(patientId)
      .then((formLinks) => {
        const hasForms = !!(formLinks && formLinks.length > 0);
        setHasForms(hasForms);
        setHasQualifyingForms(hasForms);
        console.log(
          "🔍 EditableDocumentPage: forms check result — patientId:",
          patientId,
          "| formCount:",
          formLinks?.length ?? 0,
          "| hasQualifyingForms:",
          hasForms,
        );
      })
      .catch((error) => {
        console.error("🔍 EditableDocumentPage: forms check error:", error);
        setHasForms(false);
        setHasQualifyingForms(false);
      })
      .finally(() => {
        setIsCheckingQualifyingForms(false);
      });
  }, [actualPatientId, uuid, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for reports — needs patientData + effectiveDate for the reports path
  useEffect(() => {
    if (!patientData) {
      setHasReports(false);
      return;
    }
    const reportsPath =
      patientData[data_column] || patientData[file_reference_column];
    if (!reportsPath || !effectiveDate) {
      setHasReports(false);
      return;
    }
    const dateSpecificPath = getDateSpecificReportsPath(
      reportsPath,
      effectiveDate,
    );
    console.log(
      `📋 Checking reports for date ${effectiveDate} at path: ${dateSpecificPath}`,
    );
    checkFilesExist(dateSpecificPath, patientData.id || validId).then(
      setHasReports,
    );
  }, [patientData, effectiveDate, validId]);

  // Set app element for react-modal (same as PatientPage)
  useEffect(() => {
    ReactModal.setAppElement(document.body);
  }, []);

  // Fetch current user's role for role-aware UI (entity name, share buttons, etc.)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const supabase = await getSupabase();
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData?.session?.user?.id || cancelled) return;
        const { data } = await supabase
          .from("users")
          .select("role")
          .eq("id", sessionData.session.user.id)
          .single();
        if (!cancelled && data?.role) setUserRole(data.role);
      } catch (e) {
        if (!cancelled)
          console.warn("EditableDocumentPage: could not fetch user role", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // "Copy from older doc" — state and logic
  const [copyFromOlderOpen, setCopyFromOlderOpen] = useState(false);
  const [copyFromOlderLoading, setCopyFromOlderLoading] = useState(false);
  const [copyFromOlderMatch, setCopyFromOlderMatch] = useState(null); // { row, filePath, delta } or null

  const openCopyFromOlderModal = async () => {
    if (tenantDocReadOnly) return;
    setCopyFromOlderMatch(null);
    setCopyFromOlderLoading(true);
    setCopyFromOlderOpen(true);

    console.log("📋 [CopyFromOlder] Opening — current entity:", {
      name: extractedName,
      ehrId: ehrIdForDocTitle,
      type: correctedType,
      date: effectiveDate,
    });

    try {
      // EHR_ID column name from prompts (source of truth for DB column names)
      const ehrIdIndex = prompts.findIndex((p) => p[0] === "EHR_ID");
      const ehrIdColName =
        ehrIdIndex !== -1 ? prompts[ehrIdIndex][0] : "EHR_ID";
      const typeColName = prompts[getTypeFieldIndex()]?.[0];

      console.log("📋 [CopyFromOlder] Column names:", {
        ehrIdColName,
        typeColName,
        nameColName: prompts[0][0],
      });

      const supabase = await getSupabase();

      // Primary: match by a REAL EHR_ID (ClassId/PatientId in the frontend,
      // EHR_ID in DB). A filler like "-" identifies nobody and would match
      // every placeholder row in the table, so it falls through to the name.
      // Either way the candidates are re-checked below with the same-patient
      // rule — matching on EHR_ID alone used to offer one patient's note on
      // another patient's chart.
      let query = supabase
        .from(tableName)
        .select("*")
        .lt("date_", effectiveDate)
        .order("date_", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(20);

      if (isUsableEhrId(ehrIdForDocTitle) && ehrIdColName) {
        query = query.eq(ehrIdColName, ehrIdForDocTitle);
        console.log(`📋 [CopyFromOlder] Matching by EHR_ID/ClassId: ${ehrIdColName} = '${ehrIdForDocTitle}'`);
      } else if (extractedName) {
        query = query.eq(prompts[0][0], extractedName);
        console.warn(`📋 [CopyFromOlder] No EHR_ID — falling back to name: '${extractedName}'`);
      } else {
        console.warn("📋 [CopyFromOlder] No identifier to search with");
        setCopyFromOlderLoading(false);
        return;
      }

      const { data: candidates, error } = await query;

      console.log("📋 [CopyFromOlder] Query result:", {
        count: candidates?.length ?? 0,
        error: error?.message,
        ehrIdFilter: ehrIdForDocTitle || "(none)",
        rows: candidates?.map((r) => ({
          date: r.date_,
          [ehrIdColName]: r[ehrIdColName],
          type: r[typeColName],
        })),
      });

      if (error || !candidates || candidates.length === 0) {
        console.log("📋 [CopyFromOlder] No candidate rows found");
        setCopyFromOlderLoading(false);
        return;
      }

      // Same-patient gate (utils/priorVisitMatch — the rule the medstudent
      // clinic and the FFP/FFS carry-forward use): the name has to match, and
      // when this doc and the candidate both carry a real EHR ID those have to
      // match too. Skipped only when this document has no name to check
      // against, which leaves the EHR_ID query as the sole identity check —
      // the old behavior.
      const currentIdentity = {
        patient_name: extractedName,
        EHR_ID: ehrIdForDocTitle,
      };
      const sameEntity = String(extractedName || "").trim()
        ? candidates.filter((row) => {
            const decision = samePatientDecision(currentIdentity, {
              patient_name: row[prompts[0][0]],
              EHR_ID: ehrIdColName ? row[ehrIdColName] : null,
            });
            if (!decision.match) {
              console.log(
                `📋 [CopyFromOlder] Skipping ${row.date_} — ${decision.reason}`,
              );
            }
            return decision.match;
          })
        : candidates;

      if (sameEntity.length === 0) {
        console.log("📋 [CopyFromOlder] No candidate row belongs to this patient");
        setCopyFromOlderLoading(false);
        return;
      }

      // Walk newest → oldest, find first row whose storage file has real content
      for (const row of sameEntity) {
        const rowId = ehrIdIndex !== -1 ? row[ehrIdColName] : row[colid];
        const rowName = row[prompts[0][0]];
        const rowYear = row.date_?.split("-")[0];

        if (!rowYear || !rowId || !rowName) {
          console.log("📋 [CopyFromOlder] Skipping row — missing fields:", {
            rowYear,
            rowId,
            rowName,
            date: row.date_,
          });
          continue;
        }

        // Use the same filename format as ViewableDocument: "${name}-${id}.json"
        const rowFileName = `${rowName}-${rowId}.json`;
        const rowFilePath = await generateMedADocsPath(
          rowYear,
          row.date_,
          rowFileName,
        );

        console.log(
          `📋 [CopyFromOlder] Checking row date=${row.date_} → path=${rowFilePath}`,
        );

        const cofPf = await preflightRestrictedStorageJsonSize(
          supabase,
          bucketName_files,
          rowFilePath,
        );
        if (cofPf.status === "too_large") {
          toast({
            title: "Previous session file too large",
            description: cofPf.message,
            status: "warning",
            duration: 8000,
            isClosable: true,
          });
          continue;
        }
        if (cofPf.status === "not_found") {
          console.log(
            `📋 [CopyFromOlder] Preflight: not found at ${rowFilePath}`,
          );
          continue;
        }

        const { data: fileBlob, error: fileError } = await supabase.storage
          .from(bucketName_files)
          .download(rowFilePath);

        if (fileError || !fileBlob) {
          console.log(
            `📋 [CopyFromOlder] File not found or error at ${rowFilePath}:`,
            fileError?.message,
          );
          continue;
        }

        if (cofPf.status !== "ok" && isProbablyJsonStoragePath(rowFilePath)) {
          const sizeCheck = checkFrontendStorageJsonBlobSize(fileBlob);
          if (!sizeCheck.ok) {
            toast({
              title: "Previous session file too large",
              description: sizeCheck.message,
              status: "warning",
              duration: 8000,
              isClosable: true,
            });
            continue;
          }
        }

        let parsed;
        try {
          parsed = JSON.parse(await fileBlob.text());
        } catch (e) {
          console.log(
            `📋 [CopyFromOlder] Failed to parse JSON at ${rowFilePath}`,
          );
          continue;
        }

        const hasContent = parsed?.delta?.ops && parsed.delta.ops.length > 1;
        console.log(
          `📋 [CopyFromOlder] File at ${rowFilePath} — ops: ${parsed?.delta?.ops?.length}, hasContent: ${hasContent}`,
        );

        if (hasContent) {
          console.log(
            `📋 [CopyFromOlder] ✅ Found populated doc on ${row.date_}`,
          );
          setCopyFromOlderMatch({
            row,
            filePath: rowFilePath,
            delta: parsed.delta,
          });
          break;
        }
      }

      console.log(
        "📋 [CopyFromOlder] Search complete — match found:",
        !!copyFromOlderMatch,
      );
    } catch (e) {
      console.error("📋 [CopyFromOlder] Unexpected error:", e);
    }

    setCopyFromOlderLoading(false);
  };

  const handleCopyFromOlderConfirm = () => {
    if (!copyFromOlderMatch) return;
    const editor = editorRef.current?.getEditor(0);
    if (!editor) return;

    // Y.Text (via QuillBinding) is the source of truth. Calling
    // editor.setContents while the binding is active lets the existing
    // Y.Text content echo back through the binding and merge with the
    // new delta — the "duplicated content" the user saw previously.
    // Replace Y.Text atomically instead; the binding syncs the editor.
    if (contentLoadingLock.current) {
      console.warn(
        "📋 [CopyFromOlder] contentLoadingLock held — aborting copy to avoid duplication race",
      );
      return;
    }
    contentLoadingLock.current = true;

    try {
      const newOps = copyFromOlderMatch.delta?.ops || [];
      const yText = collaborationProvider?.doc?.getText?.("quill");

      if (yText && collaborationProvider?.doc?.transact) {
        collaborationProvider.doc.transact(() => {
          if (yText.length > 0) {
            yText.delete(0, yText.length);
          }
          yText.applyDelta(newOps);
        });
      } else {
        // No collaboration provider yet — clear then set to avoid any
        // residual diff-based merge inside Quill.
        editor.setContents([{ insert: "\n" }], "api");
        editor.setContents(copyFromOlderMatch.delta, "api");
      }
    } catch (err) {
      console.error(
        "📋 [CopyFromOlder] Failed to apply older doc content:",
        err,
      );
    } finally {
      contentLoadingLock.current = false;
    }

    setCopyFromOlderOpen(false);
    toast({
      title: "Notes copied from previous session",
      status: "success",
      duration: 2500,
      isClosable: true,
    });
  };

  // Pre-fetch badge count for the share button
  const nameField = prompts[0][0];
  useEffect(() => {
    if (!validId) return;
    const fetchBadgeCount = async () => {
      const supabase = await getSupabase();
      const { data: row } = await supabase
        .from(tableName)
        .select("shared_users")
        .eq("id", validId)
        .maybeSingle();
      if (row?.shared_users) {
        const arr = Array.isArray(row.shared_users) ? row.shared_users : [];
        setShareBadgeCount(arr.length);
      }
    };
    fetchBadgeCount();
  }, [validId]);

  // Fetch users for Share modal (tenant roles) — same logic as SchedulesTable
  useEffect(() => {
    if (!shareModalOpen || !shareSchedule) {
      setShareModalUsers([]);
      setShareModalAlreadySharedIds(new Set());
      setShareModalSharedUserRoles({});
      return;
    }
    let cancelled = false;
    setShareModalLoading(true);
    const fetchUsersAndAlreadyShared = async () => {
      try {
        const supabase = await getSupabase();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (!userId) {
          setShareModalLoading(false);
          return;
        }
        const backendUrl = getBackendURL();
        const res = await fetch(
          `${backendUrl}/api/schema-users/?user_id=${encodeURIComponent(userId)}`,
        );
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && Array.isArray(json.users)) {
          setShareModalUsers(json.users || []);
        } else {
          const { data, error } = await supabase
            .from("users")
            .select("id, email, name")
            .order("email");
          if (cancelled) return;
          if (error) console.error("[Share modal] Fallback error:", error);
          setShareModalUsers(data || []);
        }
        const entityName = shareSchedule[nameField];
        const hasEntityName =
          entityName != null && String(entityName).trim() !== "";
        const alreadyIds = new Set();
        const roleMap = {};
        if (hasEntityName) {
          const { data: siblingRows } = await supabase
            .from(tableName)
            .select("shared_users")
            .eq("user_id", shareSchedule.user_id)
            .eq(nameField, entityName);
          if (cancelled) return;
          (siblingRows || []).forEach((row) => {
            mergeShareRoleMaps(roleMap, roleMapFromSharedUsers(row.shared_users));
            const arr = Array.isArray(row.shared_users) ? row.shared_users : [];
            arr.forEach((e) => {
              const p = parseSharedUserEntry(e);
              if (p) alreadyIds.add(p.id);
            });
          });
        } else {
          mergeShareRoleMaps(
            roleMap,
            roleMapFromSharedUsers(shareSchedule.shared_users),
          );
          const arr = Array.isArray(shareSchedule.shared_users)
            ? shareSchedule.shared_users
            : [];
          arr.forEach((e) => {
            const p = parseSharedUserEntry(e);
            if (p) alreadyIds.add(p.id);
          });
        }
        const { data: myRow } = await supabase
          .from("users")
          .select("shared_users")
          .eq("id", userId)
          .single();
        if (cancelled) return;
        const raw = myRow?.shared_users;
        if (raw != null) {
          const { entries } = parseOwnerShareAllEntries(raw);
          entries.forEach((e) => {
            const kid = String(e.id).toLowerCase();
            alreadyIds.add(kid);
            const r = normalizeShareRole(e.role);
            if (roleMap[kid] == null) roleMap[kid] = r;
            else roleMap[kid] = pickMoreRestrictiveShareRole(roleMap[kid], r);
          });
        }
        setShareModalSharedUserRoles(roleMap);
        setShareModalAlreadySharedIds(alreadyIds);
      } catch (err) {
        if (!cancelled) console.error("[Share modal] Fetch error:", err);
      } finally {
        if (!cancelled) setShareModalLoading(false);
      }
    };
    fetchUsersAndAlreadyShared();
    return () => {
      cancelled = true;
    };
  }, [
    shareModalOpen,
    shareSchedule?.id,
    shareSchedule?.user_id,
    shareSchedule?.[nameField],
  ]);

  // Fetch users and already-shared for Share document modal (drive / Magic Notes)
  useEffect(() => {
    if (!shareDocumentModalOpen || !documentRowId) {
      if (!shareDocumentModalOpen) {
        setShareDocumentUsers([]);
        setShareDocumentAlreadySharedIds(new Set());
      }
      return;
    }
    let cancelled = false;
    setShareDocumentLoading(true);
    const fetchDocumentShareData = async () => {
      try {
        const supabase = await getSupabase();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (!userId) {
          setShareDocumentLoading(false);
          return;
        }
        const backendUrl = getBackendURL();
        const res = await fetch(
          `${backendUrl}/api/schema-users/?user_id=${encodeURIComponent(userId)}`,
        );
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && Array.isArray(json.users)) {
          setShareDocumentUsers(json.users || []);
        } else {
          const { data, error } = await supabase
            .from("users")
            .select("id, email, name")
            .order("email");
          if (cancelled) return;
          if (error) console.error("[Share document] Fallback error:", error);
          setShareDocumentUsers(data || []);
        }
        const { data: docRow } = await supabase
          .from("documents")
          .select("shared_with")
          .eq("id", documentRowId)
          .single();
        if (cancelled) return;
        const arr = Array.isArray(docRow?.shared_with)
          ? docRow.shared_with
          : [];
        const sharedExceptSelf = arr.filter((id) => id !== userId);
        setShareDocumentAlreadySharedIds(new Set(sharedExceptSelf));
        setShareDocumentCurrentUserId(userId);
      } catch (err) {
        if (!cancelled) console.error("[Share document] Fetch error:", err);
      } finally {
        if (!cancelled) setShareDocumentLoading(false);
      }
    };
    fetchDocumentShareData();
    return () => {
      cancelled = true;
    };
  }, [shareDocumentModalOpen, documentRowId]);

  const handleAddSharedUserDocument = async (userToAdd) => {
    if (!documentRowId) return;
    if (userToAdd.id === shareDocumentCurrentUserId) return;
    const supabase = await getSupabase();
    const { data: docRow } = await supabase
      .from("documents")
      .select("shared_with")
      .eq("id", documentRowId)
      .single();
    const current = Array.isArray(docRow?.shared_with)
      ? docRow.shared_with
      : [];
    const withoutOwner = current.filter(
      (id) => id !== shareDocumentCurrentUserId,
    );
    const next = withoutOwner.includes(userToAdd.id)
      ? withoutOwner
      : [...withoutOwner, userToAdd.id];
    await supabase
      .from("documents")
      .update({ shared_with: next })
      .eq("id", documentRowId);
    setShareDocumentAlreadySharedIds(
      (prev) => new Set([...prev, userToAdd.id]),
    );
    toast({
      title: "Shared",
      description: `Document shared with ${userToAdd.email || userToAdd.name || "user"}.`,
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleRemoveSharedUserDocument = async (userToRemove) => {
    if (!documentRowId) return;
    setShareDocumentRemovingId(userToRemove.id);
    const supabase = await getSupabase();
    const { data: docRow } = await supabase
      .from("documents")
      .select("shared_with")
      .eq("id", documentRowId)
      .single();
    const current = Array.isArray(docRow?.shared_with)
      ? docRow.shared_with
      : [];
    const next = current.filter(
      (id) => id !== userToRemove.id && id !== shareDocumentCurrentUserId,
    );
    await supabase
      .from("documents")
      .update({ shared_with: next })
      .eq("id", documentRowId);
    setShareDocumentAlreadySharedIds((prev) => {
      const nextSet = new Set(prev);
      nextSet.delete(userToRemove.id);
      return nextSet;
    });
    setShareDocumentRemovingId(null);
    toast({
      title: "Access removed",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleAddSharedUser = async (userToAdd, roleArg) => {
    if (!shareSchedule) return;
    const role = normalizeShareRole(roleArg);
    const supabase = await getSupabase();
    const entityName = shareSchedule[nameField];
    const { data: siblingRows } = await supabase
      .from(tableName)
      .select("id, shared_users")
      .eq("user_id", shareSchedule.user_id)
      .eq(nameField, entityName);
    const rows = siblingRows || [];
    const updatesById = {};
    for (const row of rows) {
      const current = Array.isArray(row.shared_users) ? row.shared_users : [];
      const merged = mergeSharedUsersAdd(current, userToAdd.id, role);
      updatesById[row.id] = merged;
      await supabase
        .from(tableName)
        .update({ shared_users: merged })
        .eq("id", row.id);
    }
    setShareSchedule((prev) =>
      prev && updatesById[prev.id]
        ? {
            ...prev,
            shared_users: updatesById[prev.id],
          }
        : prev,
    );
    setPatientData((prev) =>
      prev && updatesById[prev.id]
        ? {
            ...prev,
            shared_users: updatesById[prev.id],
          }
        : prev,
    );
    const addId = String(userToAdd.id).toLowerCase();
    setShareModalAlreadySharedIds((prev) => new Set([...prev, addId]));
    setShareModalSharedUserRoles((prev) => ({
      ...prev,
      [addId]: role,
    }));
    setShareBadgeCount((prev) => prev + 1);
    toast({
      title: "Shared",
      description: `Shared with ${userToAdd.email || userToAdd.name || "user"}.`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleUpdateShareModalRole = async (userId, newRole) => {
    if (!shareSchedule) return;
    const role = normalizeShareRole(newRole);
    setShareModalUpdatingRoleId(userId);
    const supabase = await getSupabase();
    const entityName = shareSchedule[nameField];
    try {
      const { data: siblingRows } = await supabase
        .from(tableName)
        .select("id, shared_users")
        .eq("user_id", shareSchedule.user_id)
        .eq(nameField, entityName);
      const rows = siblingRows || [];
      const updatesShared = {};
      for (const row of rows) {
        const current = Array.isArray(row.shared_users) ? row.shared_users : [];
        const merged = mergeSharedUsersAdd(current, userId, role);
        updatesShared[row.id] = merged;
        await supabase
          .from(tableName)
          .update({ shared_users: merged })
          .eq("id", row.id);
      }
      setShareSchedule((prev) =>
        prev && updatesShared[prev.id]
          ? { ...prev, shared_users: updatesShared[prev.id] }
          : prev,
      );
      setPatientData((prev) =>
        prev && updatesShared[prev.id]
          ? { ...prev, shared_users: updatesShared[prev.id] }
          : prev,
      );
      const rid = String(userId).toLowerCase();
      setShareModalSharedUserRoles((prev) => ({ ...prev, [rid]: role }));
      toast({
        title: "Permission updated",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err?.message || "Failed to update permission.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setShareModalUpdatingRoleId(null);
    }
  };

  const handleRemoveSharedUser = async (userToRemove) => {
    if (!shareSchedule) return;
    const supabase = await getSupabase();
    const entityName = shareSchedule[nameField];
    setShareModalRemovingId(userToRemove.id);
    try {
      const { data: siblingRows } = await supabase
        .from(tableName)
        .select("id, shared_users")
        .eq("user_id", shareSchedule.user_id)
        .eq(nameField, entityName);
      const rows = siblingRows || [];
      const updatesById = {};
      for (const row of rows) {
        const current = Array.isArray(row.shared_users) ? row.shared_users : [];
        const updated = mergeSharedUsersRemove(current, userToRemove.id);
        updatesById[row.id] = updated;
        await supabase
          .from(tableName)
          .update({ shared_users: updated })
          .eq("id", row.id);
      }
      setShareSchedule((prev) =>
        prev && updatesById[prev.id]
          ? {
              ...prev,
              shared_users: updatesById[prev.id],
            }
          : prev,
      );
      setPatientData((prev) =>
        prev && updatesById[prev.id]
          ? {
              ...prev,
              shared_users: updatesById[prev.id],
            }
          : prev,
      );
      const remId = String(userToRemove.id).toLowerCase();
      setShareModalAlreadySharedIds((prev) => {
        const next = new Set(prev);
        for (const x of [...next]) {
          if (String(x).toLowerCase() === remId) next.delete(x);
        }
        return next;
      });
      setShareModalSharedUserRoles((prev) => {
        const n = { ...prev };
        delete n[remId];
        for (const k of Object.keys(n)) {
          if (k.toLowerCase() === remId) delete n[k];
        }
        return n;
      });
      setShareBadgeCount((prev) => Math.max(0, prev - 1));
      toast({
        title: "Access Removed",
        description: `Removed access for ${userToRemove.email || userToRemove.name || "user"}.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err?.message || "Failed to remove access.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setShareModalRemovingId(null);
    }
  };

  // Fetch file classifications when folder files change (same as PatientPage)
  useEffect(() => {
    const fetchFileClassifications = async () => {
      if (!currentFolderFiles || currentFolderFiles.length === 0) {
        setFileClassifications({});
        return;
      }

      try {
        const supabase = await getSupabase();
        const classifications = {};

        // Build file paths and fetch classifications
        const filePaths = currentFolderFiles
          .filter((file) =>
            /\.(pdf|csv|txt|png|jpg|jpeg|gif|bmp|webp|svg)$/i.test(file.name),
          )
          .map((file) => {
            const path = currentFolderPath
              ? `${currentFolderPath}/${file.name}`
              : file.name;
            return { path, name: file.name };
          });

        if (filePaths.length === 0) {
          setFileClassifications({});
          return;
        }

        // Fetch classifications for all files at once
        const { data, error } = await supabase
          .from("file_classification_cache")
          .select("file_path, classification")
          .in(
            "file_path",
            filePaths.map((f) => f.path),
          );

        if (error) {
          console.warn("Error fetching file classifications:", error);
          setFileClassifications({});
          return;
        }

        // Map classifications by file path
        const classificationMap = {};
        if (data) {
          data.forEach((item) => {
            classificationMap[item.file_path] = item.classification;
          });
        }

        setFileClassifications(classificationMap);
      } catch (error) {
        console.warn("Error fetching file classifications:", error);
        setFileClassifications({});
      }
    };

    if (modalIsOpen && currentFolderFiles) {
      fetchFileClassifications();
    }
  }, [currentFolderFiles, currentFolderPath, modalIsOpen]);

  // Initialize checkbox states from patient's localStorage settings
  useEffect(() => {
    const initializeCheckboxStates = async () => {
      // Use scheduleId (the real patient UUID from PatientPage) for presence tracking,
      // falling back to uuid from URL params, then id from location state
      const patientId = scheduleId || uuid || id;

      if (patientId) {
        console.log(
          "📝 Med-A Docs: Initializing checkbox states for patient ID:",
          patientId,
        );

        // Use the resolved patient ID (scheduleId is the real patient UUID)
        const actualPatientId = patientId;
        console.log(
          "📝 Med-A Docs: Using patient ID for presence tracking:",
          actualPatientId,
        );

        // Get patient's live transcription setting using actual ID
        const patientLiveTranscriptionSetting =
          getLiveTranscriptionSetting(actualPatientId);
        console.log(
          "📝 Med-A Docs: Patient live transcription setting:",
          patientLiveTranscriptionSetting,
        );

        // Default to false for password protection
        setLiveTranscriptionEnabled(false);
        setLiveFactExtractionEnabled(false);

        // Get patient's auto-save setting using actual ID
        const patientAutoSaveSetting = getAutoSaveSetting(actualPatientId);
        console.log(
          "📝 Med-A Docs: Patient auto-save setting:",
          patientAutoSaveSetting,
        );

        // Always default to false for password protection
        setAutoSaveSettingState(false);
        // Use the utility function to save to localStorage
        if (actualPatientId) {
          setAutoSaveSetting(actualPatientId, false);
        }

        // Store the actual patient ID for use in other parts of the component
        setActualPatientId(actualPatientId);
      }
    };

    initializeCheckboxStates();
  }, [scheduleId, uuid, id]); // Run when patient ID changes

  // Listen for localStorage changes to update checkboxes when patient settings change
  useEffect(() => {
    const handleStorageChange = () => {
      const patientId = actualPatientId || validId;
      if (patientId) {
        console.log(
          "📝 Med-A Docs: localStorage changed, updating checkbox states for patient:",
          patientId,
        );

        // Update live transcription checkboxes - default to false for password protection
        setLiveTranscriptionEnabled(false);
        setLiveFactExtractionEnabled(false);

        // Always default auto-save to false for password protection
        setAutoSaveSettingState(false);
      }
    };

    // Listen for storage events (when localStorage changes in other tabs/windows)
    window.addEventListener("storage", handleStorageChange);

    // Also check localStorage on focus (when user comes back to this tab)
    const handleFocus = () => {
      const patientId = actualPatientId || validId;
      if (patientId) {
        console.log(
          "📝 Med-A Docs: Window focused, checking localStorage settings for patient:",
          patientId,
        );

        // Update live transcription checkboxes - default to false for password protection
        setLiveTranscriptionEnabled(false);
        setLiveFactExtractionEnabled(false);

        // Always default auto-save to false for password protection
        setAutoSaveSettingState(false);
      }
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [validId, actualPatientId, userManuallyChangedAutoSave]);

  // Copy/cut/paste functionality for multi-selections
  const handleCopyMultiSelection = useCallback(() => {
    if (!editorRef.current || multiSelections.length === 0) return;

    const editor = editorRef.current.getEditor(0);
    if (!editor) return;

    try {
      // Get all selected content with formatting
      const selectedContents = multiSelections.map((selection) => {
        return editor.getContents(selection.index, selection.length);
      });

      // Combine all deltas into a single delta
      let combinedDelta = { ops: [] };
      selectedContents.forEach((delta, index) => {
        if (index > 0) {
          // Add a newline between selections to preserve structure
          combinedDelta.ops.push({ insert: "\n" });
        }
        combinedDelta.ops.push(...delta.ops);
      });

      // Create a temporary container to convert delta to HTML
      const tempContainer = document.createElement("div");
      tempContainer.style.position = "absolute";
      tempContainer.style.left = "-9999px";
      tempContainer.style.top = "-9999px";
      document.body.appendChild(tempContainer);

      // Use Quill's clipboard to convert delta to HTML
      const quillClipboard = editor.clipboard;
      const htmlContent = quillClipboard.convert(combinedDelta, "html");

      // Ensure we have a string, not an object
      let htmlString;
      if (typeof htmlContent === "string") {
        htmlString = htmlContent;
      } else if (htmlContent && htmlContent.outerHTML) {
        htmlString = htmlContent.outerHTML;
      } else if (htmlContent && htmlContent.innerHTML) {
        htmlString = htmlContent.innerHTML;
      } else {
        // Fallback: manually convert delta to HTML
        htmlString = convertDeltaToHTML(combinedDelta);
      }

      console.log("Combined delta for copy:", combinedDelta);
      console.log("Number of ops:", combinedDelta.ops.length);
      console.log(
        "Newline ops:",
        combinedDelta.ops.filter((op) => op.insert === "\n").length,
      );

      // Clean up temporary container
      document.body.removeChild(tempContainer);

      // Get plain text as fallback
      const selectedTexts = multiSelections.map((selection) => {
        return editor.getText(selection.index, selection.length);
      });
      const textContent = selectedTexts.join("\n");

      console.log("HTML content:", htmlString);
      console.log("Text content:", textContent);

      // Create clipboard items with both HTML and text
      const htmlBlob = new Blob([htmlString], { type: "text/html" });
      const textBlob = new Blob([textContent], { type: "text/plain" });

      navigator.clipboard
        .write([
          new ClipboardItem({
            "text/html": htmlBlob,
            "text/plain": textBlob,
          }),
        ])
        .then(() => {
          console.log(
            "Multi-selection with HTML formatting copied to clipboard",
          );
        })
        .catch((err) => {
          console.error(
            "Failed to copy rich text, falling back to plain text:",
            err,
          );
          // Fallback to plain text
          navigator.clipboard
            .writeText(textContent)
            .then(() => {
              console.log("Multi-selection copied as plain text (fallback)");
            })
            .catch((fallbackErr) => {
              console.error("Failed to copy even plain text:", fallbackErr);
            });
        });
    } catch (error) {
      console.error("Error in copy operation:", error);
      // Fallback to plain text
      const selectedTexts = multiSelections.map((selection) => {
        return editor.getText(selection.index, selection.length);
      });
      const textContent = selectedTexts.join("\n");
      navigator.clipboard
        .writeText(textContent)
        .then(() => {
          console.log("Multi-selection copied as plain text (fallback)");
        })
        .catch((fallbackErr) => {
          console.error("Failed to copy even plain text:", fallbackErr);
        });
    }
  }, [multiSelections]);
  //helps keep format
  // Helper function to manually convert delta to HTML
  const convertDeltaToHTML = (delta) => {
    let html = "";

    delta.ops.forEach((op) => {
      if (op.insert) {
        let text = op.insert;
        let attributes = op.attributes || {};

        // Handle newlines specially - convert them to <br> tags or <p> tags
        if (typeof text === "string") {
          // Replace newlines with <br> tags for HTML rendering
          text = text.replace(/\n/g, "<br>");
        }

        // Apply formatting attributes
        if (attributes.bold) {
          text = `<strong>${text}</strong>`;
        }
        if (attributes.italic) {
          text = `<em>${text}</em>`;
        }
        if (attributes.underline) {
          text = `<u>${text}</u>`;
        }
        if (attributes.strike) {
          text = `<s>${text}</s>`;
        }
        if (attributes.header) {
          const headerLevel = attributes.header;
          // Remove <br> tags for headers since they should be block elements
          text = text.replace(/<br>/g, "");
          text = `<h${headerLevel}>${text}</h${headerLevel}>`;
        }
        if (attributes.color) {
          text = `<span style="color: ${attributes.color}">${text}</span>`;
        }
        if (attributes.background) {
          text = `<span style="background-color: ${attributes.background}">${text}</span>`;
        }
        if (attributes.size) {
          text = `<span style="font-size: ${attributes.size}">${text}</span>`;
        }
        if (attributes.font) {
          text = `<span style="font-family: ${attributes.font}">${text}</span>`;
        }
        if (attributes.list) {
          // Remove <br> tags for list items since they should be block elements
          text = text.replace(/<br>/g, "");
          if (attributes.list === "ordered") {
            text = `<ol><li>${text}</li></ol>`;
          } else {
            text = `<ul><li>${text}</li></ul>`;
          }
        }

        html += text;
      }
    });

    return html;
  };

  const handleCutMultiSelection = useCallback(() => {
    if (!editorRef.current || multiSelections.length === 0) return;

    const editor = editorRef.current.getEditor(0);
    if (!editor) return;

    // First copy the text with formatting
    handleCopyMultiSelection();

    // Get Quill's history module to batch operations
    const history = editor.getModule("history");
    if (history) {
      // End any current undo group to start a new one
      history.cutoff();
    }

    // Then delete the selections (in reverse order to maintain indices)
    // Use 'user' source so Quill tracks it in history
    const sortedSelections = [...multiSelections].sort(
      (a, b) => b.index - a.index,
    );

    sortedSelections.forEach((selection) => {
      editor.deleteText(selection.index, selection.length, "user");
    });

    // End the undo group so all deletions are treated as one operation
    if (history) {
      history.cutoff();
    }

    // Clear multi-selections after cutting
    setMultiSelections([]);
  }, [multiSelections, handleCopyMultiSelection]);

  const handlePasteMultiSelection = useCallback(() => {
    if (!editorRef.current || multiSelections.length === 0) return;

    const editor = editorRef.current.getEditor(0);
    if (!editor) return;

    // Try to read rich text from clipboard first
    navigator.clipboard
      .read()
      .then((clipboardItems) => {
        let hasRichText = false;

        // Look for HTML content in clipboard
        for (const clipboardItem of clipboardItems) {
          if (clipboardItem.types.includes("text/html")) {
            hasRichText = true;
            clipboardItem.getType("text/html").then((htmlBlob) => {
              htmlBlob.text().then((htmlText) => {
                console.log("Pasting HTML:", htmlText);
                pasteRichText(htmlText);
              });
            });
            return;
          }
        }

        // If no HTML found, fall back to plain text
        if (!hasRichText) {
          navigator.clipboard.readText().then((text) => {
            console.log("Pasting plain text:", text);
            pastePlainText(text);
          });
        }
      })
      .catch((err) => {
        console.error("Failed to read from clipboard:", err);
        // Fallback to plain text
        navigator.clipboard
          .readText()
          .then((text) => {
            console.log("Pasting plain text (fallback):", text);
            pastePlainText(text);
          })
          .catch((fallbackErr) => {
            console.error("Failed to read even plain text:", fallbackErr);
          });
      });

    // Helper function to paste rich text with HTML
    const pasteRichText = (htmlContent) => {
      // Get Quill's history module to batch operations
      const history = editor.getModule("history");
      if (history) {
        history.cutoff(); // Start a new undo group
      }

      const sortedSelections = [...multiSelections].sort(
        (a, b) => b.index - a.index,
      );

      console.log("Pasting to", sortedSelections.length, "selections");
      console.log("HTML content being pasted:", htmlContent);

      sortedSelections.forEach((selection, index) => {
        editor.deleteText(selection.index, selection.length, "user");

        try {
          // Create a temporary container with the HTML content
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = htmlContent;

          // Use Quill's clipboard to convert HTML to delta
          const delta = editor.clipboard.convert(tempDiv);

          console.log(`Pasting to selection ${index}:`, delta);
          console.log("Delta ops:", delta.ops);
          console.log(
            "Newlines in delta:",
            delta.ops.filter((op) => op.insert === "\n").length,
          );

          // Insert the content with formatting
          // Note: insertContents doesn't accept source parameter, but history.cutoff() batches operations
          editor.insertContents(selection.index, delta);
        } catch (convertErr) {
          console.warn(
            "Failed to convert HTML to delta, falling back to plain text:",
            convertErr,
          );
          // Strip HTML tags and paste as plain text
          const plainText = htmlContent.replace(/<[^>]*>/g, "");
          editor.insertText(selection.index, plainText, "user");
        }
      });

      // End the undo group so all paste operations are treated as one operation
      if (history) {
        history.cutoff();
      }

      // Clear multi-selections after pasting
      setMultiSelections([]);
    };

    // Helper function to paste plain text
    const pastePlainText = (text) => {
      // Get Quill's history module to batch operations
      const history = editor.getModule("history");
      if (history) {
        history.cutoff(); // Start a new undo group
      }

      const sortedSelections = [...multiSelections].sort(
        (a, b) => b.index - a.index,
      );

      sortedSelections.forEach((selection) => {
        editor.deleteText(selection.index, selection.length, "user");
        editor.insertText(selection.index, text, "user");
      });

      // End the undo group so all paste operations are treated as one operation
      if (history) {
        history.cutoff();
      }

      // Clear multi-selections after pasting
      setMultiSelections([]);
    };
  }, [multiSelections]);

  // Multi-selection keyboard event handlers
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Cmd/Ctrl key to enable multi-select mode
      if (event.metaKey || event.ctrlKey) {
        setIsMultiSelectMode(true);
        setShowMultiSelectIndicator(true);
      }

      // Handle copy/cut/paste shortcuts
      if (event.metaKey || event.ctrlKey) {
        const editor = editorRef.current?.getEditor(0);
        if (!editor) return;

        switch (event.key.toLowerCase()) {
          case "c":
            if (multiSelections.length > 0) {
              event.preventDefault();
              handleCopyMultiSelection();
            }
            break;
          case "x":
            if (multiSelections.length > 0) {
              event.preventDefault();
              handleCutMultiSelection();
            }
            break;
          case "v":
            if (multiSelections.length > 0) {
              event.preventDefault();
              handlePasteMultiSelection();
            }
            break;
        }
      }

      // Escape key to clear all selections
      if (event.key === "Escape") {
        setMultiSelections([]);
        setIsMultiSelectMode(false);
        setShowMultiSelectIndicator(false);
      }
    };

    const handleKeyUp = (event) => {
      // Disable multi-select mode when Cmd/Ctrl is released
      if (!event.metaKey && !event.ctrlKey) {
        setIsMultiSelectMode(false);
        setShowMultiSelectIndicator(false);
      }
    };

    const handleMouseUp = (event) => {
      // Ignore clicks on toolbar buttons and other UI elements
      if (
        event.target.closest("#toolbar") ||
        event.target.closest(".ql-toolbar") ||
        event.target.closest("button") ||
        event.target.closest("select") ||
        event.target.closest(".toolbar-container")
      ) {
        return;
      }

      // Check if the click was on the editor
      const editorElement = document.querySelector(".ql-editor");
      if (!editorElement || !editorElement.contains(event.target)) {
        // Clicked outside editor - clear all multi-selections
        setMultiSelections([]);
        return;
      }

      const editor = editorRef.current?.getEditor(0);
      if (!editor) return;

      const selection = editor.getSelection();

      // If not in multi-select mode, clear all multi-selections
      if (!isMultiSelectMode) {
        setMultiSelections([]);
        return;
      }

      // If no selection or empty selection, clear multi-selections
      if (!selection || selection.length === 0) {
        setMultiSelections([]);
        return;
      }

      // Add or remove selection from multi-selections
      const newSelection = {
        index: selection.index,
        length: selection.length,
        id: Date.now() + Math.random(), // Unique ID for this selection
      };

      setMultiSelections((prev) => {
        // Check if this selection overlaps with any existing selection
        const hasOverlap = prev.some(
          (sel) =>
            (newSelection.index >= sel.index &&
              newSelection.index < sel.index + sel.length) ||
            (sel.index >= newSelection.index &&
              sel.index < newSelection.index + newSelection.length),
        );

        if (hasOverlap) {
          // Remove overlapping selections
          return prev.filter(
            (sel) =>
              !(
                (newSelection.index >= sel.index &&
                  newSelection.index < sel.index + sel.length) ||
                (sel.index >= newSelection.index &&
                  sel.index < newSelection.index + newSelection.length)
              ),
          );
        } else {
          // Add new selection
          return [...prev, newSelection];
        }
      });
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    isMultiSelectMode,
    multiSelections,
    handleCopyMultiSelection,
    handleCutMultiSelection,
    handlePasteMultiSelection,
  ]);

  // Drag and drop handlers for cut-and-paste functionality
  useEffect(() => {
    const handleDragStart = (event) => {
      // Only handle drag on multi-selection highlights
      if (
        !event.target.classList.contains("multi-selection-highlight") ||
        multiSelections.length === 0
      ) {
        return;
      }

      event.preventDefault();
      setIsDragging(true);

      // Extract text from all multi-selections
      const editor = editorRef.current?.getEditor(0);
      if (!editor) return;

      const draggedTexts = [];
      multiSelections.forEach((selection) => {
        try {
          const text = editor.getText(selection.index, selection.length);
          draggedTexts.push({
            text: text,
            index: selection.index,
            length: selection.length,
            id: selection.id,
          });
        } catch (error) {
          console.warn("Could not extract text from selection:", error);
        }
      });

      setDragData(draggedTexts);

      // Create a cursor-like drag preview
      const preview = document.createElement("div");
      preview.innerHTML = "│"; // Cursor character
      preview.style.position = "absolute";
      preview.style.top = "-1000px";
      preview.style.left = "-1000px";
      preview.style.fontSize = "16px";
      preview.style.fontFamily = "monospace";
      preview.style.color = "#000";
      preview.style.fontWeight = "bold";
      preview.style.zIndex = "10000";
      preview.style.pointerEvents = "none";
      document.body.appendChild(preview);
      setDragPreview(preview);

      // Set data transfer for the drag operation
      const allText = draggedTexts.map((item) => item.text).join("\n");
      event.dataTransfer.setData("text/plain", allText);
      event.dataTransfer.setData(
        "application/x-multi-selections",
        JSON.stringify(multiSelections),
      );

      event.dataTransfer.setDragImage(preview, 0, 0);
      event.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (event) => {
      if (!isDragging) return;

      event.preventDefault();
      event.dataTransfer.dropEffect = "move";

      // Show cursor at drop position
      const editorElement = document.querySelector(".ql-editor");
      if (editorElement && editorElement.contains(event.target)) {
        // Add a temporary cursor indicator
        const cursor = document.createElement("div");
        cursor.className = "drag-drop-cursor";
        cursor.innerHTML = "│";
        cursor.style.position = "absolute";
        cursor.style.fontSize = "16px";
        cursor.style.fontFamily = "monospace";
        cursor.style.color = "#2196f3";
        cursor.style.fontWeight = "bold";
        cursor.style.zIndex = "1000";
        cursor.style.pointerEvents = "none";

        // Position cursor at mouse location
        const rect = editorElement.getBoundingClientRect();
        cursor.style.left = `${event.clientX - rect.left}px`;
        cursor.style.top = `${event.clientY - rect.top}px`;

        // Remove existing cursor
        const existingCursor = document.querySelector(".drag-drop-cursor");
        if (existingCursor) {
          existingCursor.remove();
        }

        editorElement.appendChild(cursor);
      }
    };

    const handleDragLeave = (event) => {
      if (!isDragging) return;

      // Remove cursor indicator
      const cursor = document.querySelector(".drag-drop-cursor");
      if (cursor) {
        cursor.remove();
      }
    };

    const handleDrop = (event) => {
      console.log("Drop event triggered", {
        isDragging,
        dragData: dragData?.length,
      });

      // Check if we have drag data from the dataTransfer
      const draggedSelections = JSON.parse(
        event.dataTransfer.getData("application/x-multi-selections") || "[]",
      );
      const draggedText = event.dataTransfer.getData("text/plain");

      if (draggedSelections.length === 0) {
        console.log("No dragged selections found");
        return;
      }

      event.preventDefault();

      const editor = editorRef.current?.getEditor(0);
      if (!editor) {
        console.log("No editor found");
        return;
      }

      // Get drop position
      const editorElement = document.querySelector(".ql-editor");
      if (!editorElement || !editorElement.contains(event.target)) {
        console.log("Drop outside editor");
        return;
      }

      // Calculate drop index based on mouse position
      const currentSelection = editor.getSelection();
      const dropIndex = currentSelection
        ? currentSelection.index
        : editor.getLength();

      console.log("Dropping at index:", dropIndex);

      // Sort selections by index (descending) to avoid index shifting issues
      const sortedSelections = [...draggedSelections].sort(
        (a, b) => b.index - a.index,
      );

      // Remove text from original positions (in reverse order)
      sortedSelections.forEach((selection) => {
        try {
          console.log(
            "Removing text at index:",
            selection.index,
            "length:",
            selection.length,
          );
          editor.deleteText(selection.index, selection.length, "user");
        } catch (error) {
          console.warn("Could not remove text from selection:", error);
        }
      });

      // Insert text at drop position
      console.log("Inserting text:", draggedText);
      editor.insertText(dropIndex, draggedText, "user");

      // Clear multi-selections
      setMultiSelections([]);

      // Restore selection at drop location
      editor.setSelection(dropIndex, draggedText.length);

      console.log("Drop completed successfully");
    };

    const handleDragEnd = (event) => {
      setIsDragging(false);
      setDragData(null);
      if (dragPreview && document.body.contains(dragPreview)) {
        document.body.removeChild(dragPreview);
        setDragPreview(null);
      }

      // Remove cursor indicator
      const cursor = document.querySelector(".drag-drop-cursor");
      if (cursor && cursor.parentNode) {
        cursor.remove();
      }
    };

    // Add event listeners to multi-selection highlights
    const addDragListeners = () => {
      const highlights = document.querySelectorAll(
        ".multi-selection-highlight",
      );
      highlights.forEach((highlight) => {
        highlight.draggable = true;
        highlight.addEventListener("dragstart", handleDragStart);
      });
    };

    // Add drag listeners when highlights are created
    const observer = new MutationObserver(() => {
      addDragListeners();
    });

    const editorElement = document.querySelector(".ql-editor");
    if (editorElement) {
      observer.observe(editorElement, { childList: true, subtree: true });
    }

    // Add global drag listeners
    document.addEventListener("dragover", handleDragOver);
    document.addEventListener("dragleave", handleDragLeave);
    document.addEventListener("drop", handleDrop);
    document.addEventListener("dragend", handleDragEnd);

    return () => {
      observer.disconnect();
      document.removeEventListener("dragover", handleDragOver);
      document.removeEventListener("dragleave", handleDragLeave);
      document.removeEventListener("drop", handleDrop);
      document.removeEventListener("dragend", handleDragEnd);

      if (dragPreview && document.body.contains(dragPreview)) {
        document.body.removeChild(dragPreview);
      }

      const cursor = document.querySelector(".drag-drop-cursor");
      if (cursor && cursor.parentNode) {
        cursor.remove();
      }
    };
  }, [isDragging, dragData, dragPreview, multiSelections]);

  // Drag and drop functionality for multi-selections
  const handleDragStart = useCallback(
    (event) => {
      if (multiSelections.length === 0) return;

      const editor = editorRef.current?.getEditor(0);
      if (!editor) return;

      // Get all selected text
      const selectedTexts = multiSelections.map((selection) => {
        const text = editor.getText(selection.index, selection.length);
        return text;
      });

      const combinedText = selectedTexts.join("\n");

      // Set drag data
      event.dataTransfer.setData("text/plain", combinedText);
      event.dataTransfer.effectAllowed = "move";

      // Store the selections for later use
      event.dataTransfer.setData(
        "application/x-multi-selections",
        JSON.stringify(multiSelections),
      );

      console.log("Drag started with multi-selections:", multiSelections);
    },
    [multiSelections],
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  // Define checkbox handlers using useCallback to avoid ESLint issues
  const handleLiveTranscriptionChange = useCallback(
    async (e) => {
      const newValue = e.target.checked;

      // If trying to turn ON, check credit limit first
      if (newValue) {
        const creditLimitExceeded = await checkCreditLimitAndUpdateUI(
          toast,
          setLiveTranscriptionEnabled,
          setLiveFactExtractionEnabled,
          setAutoSaveSettingState,
        );
        if (creditLimitExceeded) {
          return;
        }
      }

      // If trying to turn ON and password protection is enabled, check password first
      if (newValue && !(await hasValidPassword())) {
        console.log("🔒 Password required to enable live transcription");
        setPendingAction(() => () => {
          setLiveTranscriptionEnabled(newValue);
          setLiveFactExtractionEnabled(newValue);
          const patientId = actualPatientId || validId;
          if (patientId && newValue !== undefined) {
            setLiveTranscriptionSetting(patientId, newValue);
            setLiveFactExtractionSetting(patientId, newValue);
            // Only sync auto-save if it hasn't been explicitly set
            const savedAutoSaveSetting = getAutoSaveSetting(patientId);
            if (savedAutoSaveSetting === null) {
              setAutoSaveSettingState(newValue);
              // Use the utility function to save to localStorage
              setAutoSaveSetting(patientId, newValue);
            }
          }
        });
        setIsPasswordModalOpen(true);
        return;
      }

      setLiveTranscriptionEnabled(newValue);
      setLiveFactExtractionEnabled(newValue);
      const patientId = actualPatientId || validId;
      if (patientId && newValue !== undefined) {
        setLiveTranscriptionSetting(patientId, newValue);
        setLiveFactExtractionSetting(patientId, newValue);
        // Only sync auto-save if it hasn't been explicitly set
        const savedAutoSaveSetting = getAutoSaveSetting(patientId);
        if (savedAutoSaveSetting === null) {
          setAutoSaveSettingState(newValue);
          // Use the utility function to save to localStorage
          setAutoSaveSetting(patientId, newValue);
        }
      }
    },
    [actualPatientId, validId],
  );

  const handleLiveFactExtractionChange = useCallback(
    async (e) => {
      const newValue = e.target.checked;

      // If trying to turn ON, check credit limit first
      if (newValue) {
        const creditLimitExceeded = await checkCreditLimitAndUpdateUI(
          toast,
          setLiveTranscriptionEnabled,
          setLiveFactExtractionEnabled,
          setAutoSaveSettingState,
        );
        if (creditLimitExceeded) {
          return;
        }
      }

      // If trying to turn ON and password protection is enabled, check password first
      if (newValue && !(await hasValidPassword())) {
        console.log("🔒 Password required to enable live fact extraction");
        setPendingAction(() => () => {
          setLiveFactExtractionEnabled(newValue);
          setLiveTranscriptionEnabled(newValue);
          const patientId = actualPatientId || validId;
          if (patientId && newValue !== undefined) {
            setLiveTranscriptionSetting(patientId, newValue);
            setLiveFactExtractionSetting(patientId, newValue);
            // Only sync auto-save if it hasn't been explicitly set
            const savedAutoSaveSetting = getAutoSaveSetting(patientId);
            if (savedAutoSaveSetting === null) {
              setAutoSaveSettingState(newValue);
              // Use the utility function to save to localStorage
              setAutoSaveSetting(patientId, newValue);
            }
          }
        });
        setIsPasswordModalOpen(true);
        return;
      }

      setLiveFactExtractionEnabled(newValue);
      setLiveTranscriptionEnabled(newValue);
      const patientId = actualPatientId || validId;
      if (patientId && newValue !== undefined) {
        setLiveTranscriptionSetting(patientId, newValue);
        setLiveFactExtractionSetting(patientId, newValue);
        // Only sync auto-save if it hasn't been explicitly set
        const savedAutoSaveSetting = getAutoSaveSetting(patientId);
        if (savedAutoSaveSetting === null) {
          setAutoSaveSettingState(newValue);
          // Use the utility function to save to localStorage
          setAutoSaveSetting(patientId, newValue);
        }
      }
    },
    [actualPatientId, validId],
  );

  const handleAutoSaveChange = useCallback(
    async (e) => {
      const newValue = e.target.checked;
      console.log(
        "💾 Auto-save checkbox changed:",
        autoSaveSetting,
        "->",
        newValue,
      );
      // If trying to turn ON, check credit limit first
      if (newValue) {
        const creditLimitExceeded = await checkCreditLimitAndUpdateUI(
          toast,
          setLiveTranscriptionEnabled,
          setLiveFactExtractionEnabled,
          setAutoSaveSettingState,
        );
        if (creditLimitExceeded) {
          return;
        }
      }

      // If trying to turn ON and password protection is enabled, check password first
      if (newValue && !(await hasValidPassword())) {
        console.log("🔒 Password required to enable auto-save");
        setPendingAction(() => () => {
          setAutoSaveSettingState(newValue);
          setUserManuallyChangedAutoSave(true);
          const patientId = actualPatientId || validId;
          if (patientId && newValue !== undefined) {
            // Use the utility function to save to localStorage
            setAutoSaveSetting(patientId, newValue);
          }
        });
        setIsPasswordModalOpen(true);
        return;
      }

      setAutoSaveSettingState(newValue);
      setUserManuallyChangedAutoSave(true);
      const patientId = actualPatientId || validId;
      if (patientId && newValue !== undefined) {
        // Use the utility function to save to localStorage
        setAutoSaveSetting(patientId, newValue);
      }
    },
    [actualPatientId, validId, autoSaveSetting],
  );

  // Resolve display fields — prefer location.state, fall back to patientData (DB fetch on refresh)
  const typeField = prompts[getTypeFieldIndex()][0];
  const resolvedType = type
    ? correctedType
    : patientData?.[typeField] || correctedType;
  const resolvedName = extractedName || patientData?.[prompts[0][0]] || "";
  const resolvedEhrId = ehrIdForDocTitle || patientData?.EHR_ID || "";
  const resolvedEmail = emailForDocTitle || (patientData ? patientData[colid] : "") || "";

  // User info for display purposes - use patient scheduled time if available, otherwise navbar time
  // Navbar title uses generateDocFilename(userInfo) — EHR_ID / email come from location.state, not schedule `id`
  // For NP/NS documents, preserve the original document time instead of using patient scheduled time
  // Only treat as "use original time" if the type was explicitly provided (not defaulted to NP on refresh)
  const shouldUseOriginalTime =
    !!type && (correctedType === "NP" || correctedType === "NS");
  const effectiveTime = shouldUseOriginalTime
    ? navbarTime
    : patientScheduledTime || navbarTime || "";

  // generateDocFilename (env.js) reads EHR_ID then colid — do not pass schedule row `id` as EHR
  const userInfo = {
    time_: effectiveTime || (patientData?.time_ ? formatTimeToHHMM(patientData.time_) : ""),
    date_: effectiveDate,
    [prompts[0][0]]: resolvedName,
    [colid]: resolvedEmail,
    EHR_ID: resolvedEhrId,
    [typeField]: resolvedType || "",
  };

  // Sync the active patient into the global stateData store (owned by the
  // parent via the setStateData prop). This MUST stay below the `userInfo`
  // declaration above: it previously lived ~1900 lines earlier, where its
  // dependency array evaluated `userInfo` in the temporal dead zone and
  // crashed the page on render ("Cannot access 'userInfo' before
  // initialization").
  //
  // `userInfo` is rebuilt every render, so this effect runs every render.
  // The functional update bails out when the value is unchanged; without it,
  // the patientData-less path (activePatient = userInfo, a fresh object each
  // render) would push a new reference on every render, re-render this page
  // from the parent, and loop infinitely.
  useEffect(() => {
    const activePatient = patientData || userInfo;
    if (!activePatient || !setStateData) return;
    setStateData((prev) => {
      if (prev === activePatient) return prev;
      try {
        if (JSON.stringify(prev) === JSON.stringify(activePatient)) return prev;
      } catch (e) {
        // Non-serializable value — fall through and update.
      }
      console.log(
        "🔄 [EditableDocumentPage] Syncing patient data to global stateData:",
        activePatient
      );
      return activePatient;
    });
  }, [patientData, userInfo, setStateData]);

  // Debug: Log userInfo construction to understand the mismatch
  // console.log("🔍 DEBUGGING USERINFO CONSTRUCTION:");
  // console.log("   originalNavbarTime:", originalNavbarTime);
  // console.log("   navbarTime:", navbarTime);
  // console.log("   documentTime:", documentTime);
  // console.log("   patientScheduledTime:", patientScheduledTime);
  // console.log("   shouldUseOriginalTime:", shouldUseOriginalTime);
  // console.log("   effectiveTime:", effectiveTime);
  // console.log("   extractedName:", extractedName);
  // console.log("   validId (schedule ID for tracking):", validId);
  // console.log("   scheduleId from location.state:", scheduleId);
  // console.log("   EHR_ID for filename:", ehrIdForDocTitle);
  // console.log("   patientData:", patientData);
  // console.log("   correctedType:", correctedType);
  // console.log("   userInfo:", userInfo);

  // Handle combined documents differently
  let displayTitle;
  if (isMagicNotes) {
    // For Magic Notes (Vita-Med Drive files), use the user-given title (or resolved join title)
    displayTitle = driveTitle || joinDocTitle || "Untitled";
  } else if (isCombined) {
    // For combined documents, use the filename format to match console output
    displayTitle = `${effectiveDate}-combined`;
  } else {
    // For individual documents, generate filename normally
    displayTitle = generateDocFilename(userInfo);
  }

  // File path and naming logic
  const storageFileName = isCombined
    ? `${effectiveDate}-combined.json`
    : `${displayTitle}.json`;
  const year = effectiveDate ? effectiveDate.split("-")[0] : "UnknownYear";

  // Keep filePathRef in sync so Quill image handler can access current filePath
  // (filePath state is declared near documentRowId so it's available to the
  // tenant-access effect that resolves Drive-doc ownership).
  useEffect(() => {
    filePathRef.current = filePath;
  }, [filePath]);

  // Mirror handleSaveDoc into a ref so initializeQuill-registered handlers
  // always invoke the latest callback (avoids stale-closure saves).
  useEffect(() => {
    handleSaveDocRef.current = handleSaveDoc;
  });

  // Generate file path with schema support if not already provided
  useEffect(() => {
    const generatePath = async () => {
      if (passedFilePath) {
        console.log(
          "📄 [FilePath] Using passedFilePath from location.state:",
          passedFilePath,
        );
        setFilePath(passedFilePath);
        return;
      }
      // Path will be set by the join-doc effect when arriving via share link
      if (joinDocId) return;

      // On refresh location.state is lost. If the URL has a documentId, look up the
      // correct file path from the documents table instead of regenerating it (which
      // would use today's date / wrong type and produce a mismatched path).
      if (urlDocumentId) {
        console.log(
          "📄 [FilePath] No passedFilePath — looking up path by URL documentId:",
          urlDocumentId,
        );
        try {
          const supabase = await getSupabase();
          const { data: docRow, error: docErr } = await supabase
            .from("documents")
            .select("path")
            .eq("id", urlDocumentId)
            .maybeSingle();
          console.log("📄 [FilePath] documents table lookup result:", {
            docRow,
            docErr,
          });
          if (docRow?.path) {
            console.log(
              "📄 [FilePath] Resolved path from documents table:",
              docRow.path,
            );
            setFilePath(docRow.path);
            return;
          }
        } catch (e) {
          console.warn("📄 [FilePath] documents table lookup failed:", e);
        }
      }

      console.log("📄 [FilePath] Generating path from scratch:", {
        year,
        effectiveDate,
        storageFileName,
      });
      const path = await generateMedADocsPath(
        year,
        effectiveDate,
        storageFileName,
      );
      console.log("📄 [FilePath] Generated path:", path);
      setFilePath(path);
    };

    generatePath();
  }, [
    passedFilePath,
    joinDocId,
    urlDocumentId,
    year,
    effectiveDate,
    storageFileName,
  ]);

  // Handle join-via-link: fetch doc by id, add current user to shared_with (never add owner), set filePath
  useEffect(() => {
    if (!joinDocId) return;

    const handleJoin = async () => {
      try {
        const supabase = await getSupabase();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) {
          toast({
            title: "Please sign in",
            description: "You must be signed in to open a shared document.",
            status: "warning",
            duration: 4000,
            isClosable: true,
          });
          return;
        }

        let doc = null;
        let fetchError = null;
        let clientForUpdate = supabase;
        const { data: docData, error: docError } = await supabase
          .from("documents")
          .select("id, path, shared_with")
          .eq("id", joinDocId)
          .maybeSingle();
        if (docError) {
          fetchError = docError;
          const publicClient = supabasePublic();
          const { data: publicDoc } = await publicClient
            .from("documents")
            .select("id, path, shared_with")
            .eq("id", joinDocId)
            .maybeSingle();
          if (publicDoc) {
            doc = publicDoc;
            clientForUpdate = publicClient;
          }
        } else {
          doc = docData;
        }

        if (!doc) {
          const msg = fetchError?.message || "Not found";
          const code = fetchError?.code || "";
          console.error("Join doc: document not found or no access", {
            joinDocId,
            error: fetchError,
            code,
          });
          toast({
            title: "Document not found",
            description:
              code === "PGRST116"
                ? "The link may be invalid or the document was deleted."
                : msg.includes("406") || code
                  ? `Server returned: ${code || msg}. The document may be in a different workspace or you may not have access.`
                  : msg,
            status: "error",
            duration: 6000,
            isClosable: true,
          });
          return;
        }

        const isDrivePath =
          doc.path && doc.path.startsWith("New/Vita-MedDrive/");
        let isOwner = false;
        if (isDrivePath) {
          const pathParts = doc.path.split("/");
          const ownerFolder = pathParts[3];
          const { data: userData } = await supabase
            .from("users")
            .select("name")
            .eq("id", session.user.id)
            .single();
          const userName =
            userData?.name || session.user.email?.split("@")[0] || "";
          isOwner = ownerFolder === userName;
        }

        const existing = Array.isArray(doc.shared_with) ? doc.shared_with : [];
        const withoutOwner = existing.filter((id) => id !== session.user.id);
        if (!isOwner && !withoutOwner.includes(session.user.id)) {
          await clientForUpdate
            .from("documents")
            .update({ shared_with: [...withoutOwner, session.user.id] })
            .eq("id", doc.id);
        } else if (isOwner && existing.includes(session.user.id)) {
          await clientForUpdate
            .from("documents")
            .update({ shared_with: withoutOwner })
            .eq("id", doc.id);
        }

        // Derive title from path filename
        const title = doc.path
          .split("/")
          .pop()
          .replace(/\.json$/, "");
        setJoinDocTitle(title);
        setFilePath(doc.path);
        setDocumentRowId(doc.id);
      } catch (err) {
        const msg = err?.message || String(err);
        console.error("Join doc error:", msg, err);
        toast({
          title: "Could not open shared document",
          description:
            msg ||
            "An unexpected error occurred. Try again or ask the owner to re-share the link.",
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      }
    };

    handleJoin();
  }, [joinDocId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve document row id by path when on drive doc (not join link)
  useEffect(() => {
    if (!isMagicNotes || joinDocId || !filePath) return;
    let cancelled = false;
    (async () => {
      try {
        const supabase = await getSupabase();
        const { data } = await supabase
          .from("documents")
          .select("id")
          .eq("path", filePath)
          .maybeSingle();
        if (!cancelled && data?.id) setDocumentRowId(data.id);
      } catch (e) {
        if (!cancelled)
          console.warn(
            "EditableDocumentPage: could not fetch document id by path",
            e,
          );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isMagicNotes, joinDocId, filePath]);

  // Determine if current user is the drive doc owner (path under their folder) — hide Share for shared users
  useEffect(() => {
    if (
      !filePath ||
      !documentRowId ||
      !filePath.startsWith("New/Vita-MedDrive/")
    ) {
      setIsDriveDocOwner(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const supabase = await getSupabase();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user || cancelled) return;
        const { data: userData } = await supabase
          .from("users")
          .select("name")
          .eq("id", session.user.id)
          .single();
        if (cancelled) return;
        const name =
          userData?.name || session.user.email?.split("@")[0] || "User";
        const schema = supabaseSchema || "public";
        const base = generateVitaMedDriveBasePath(schema);
        const userRootPath = `${base}${name}`;
        const isOwner =
          filePath === userRootPath || filePath.startsWith(userRootPath + "/");
        if (!cancelled) setIsDriveDocOwner(isOwner);
      } catch (e) {
        if (!cancelled) setIsDriveDocOwner(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filePath, documentRowId]);

  // Log file information only when file path changes
  useEffect(() => {
    if (filePath) {
      // Use console.info so this still appears even when debug console.log is muted.
      console.info("📄 EditableDocumentPage file info:", {
        fileName: storageFileName,
        patientName: extractedName,
        filePath,
      });
    }
  }, [filePath, storageFileName, extractedName]);

  // Auto-trigger "Use Doc as Template" when navigated from PatientPage/SchedulesTable.
  // Strip payload from history immediately so location.state cannot survive refresh/remount
  // and re-trigger the modal. Dedupe with sessionStorage + location.key for React Strict Mode.
  useEffect(() => {
    if (!documentLoaded || !contentLoadingComplete) return;
    const payload = location.state?.docAsTemplatePayload;
    if (!payload) return;

    const dedupeKey = `notesai_doctpl_${location.key}`;
    if (sessionStorage.getItem(dedupeKey)) {
      if (location.state?.docAsTemplatePayload) {
        stripDocAsTemplateNavigationState();
      }
      return;
    }
    sessionStorage.setItem(dedupeKey, "1");

    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: { ...(location.state || {}), docAsTemplatePayload: undefined },
      },
    );

    handleDocAsTemplateRun({
      editablePrompt: payload.editablePrompt,
      useStoredFullText: payload.useStoredFullText,
      forceProcessForms: payload.forceProcessForms,
      patientRow: payload.patientRow || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when doc ready; handleDocAsTemplateRun is stable enough for this flow
  }, [
    documentLoaded,
    contentLoadingComplete,
    location.key,
    location.state?.docAsTemplatePayload,
    navigate,
    location.pathname,
    location.search,
    location.hash,
    stripDocAsTemplateNavigationState,
  ]);

  // **Helper Functions (must be defined before hooks that use them)**

  /**
   * Update global presence from WebSocket awareness states
   * This function must be defined before useCollaboration hook
   */
  const updateGlobalPresenceFromAwareness = (awarenessStates) => {
    // Check if component is still mounted before processing
    if (!isMountedRef.current || isUnmounting) {
      return;
    }

    try {
      // Import global presence manager dynamically to avoid circular dependencies
      import("../utils/globalPresenceManager")
        .then(({ default: globalPresenceManager }) => {
          // Re-check if component is still mounted after async import
          if (!isMountedRef.current || isUnmounting) {
            return;
          }

          if (globalPresenceManager && globalPresenceManager.currentUser) {
            console.log(
              "🔄 Updating global presence from WebSocket awareness...",
            );

            const visitDayForPresence =
              effectiveDate && String(effectiveDate).trim().length >= 10
                ? String(effectiveDate).trim().slice(0, 10)
                : "";

            // Get all current user IDs from awareness states
            const awarenessUserIds = new Set();
            const awarenessStatesMap = new Map();

            // First pass: collect all awareness states and user IDs, and extract/cache colors
            const colorsToCache = new Map();
            for (const [clientId, state] of awarenessStates) {
              if (state.user && state.user.id) {
                awarenessUserIds.add(state.user.id);
                awarenessStatesMap.set(state.user.id, state);

                // Extract and cache user colors from awareness states
                if (state.user.color) {
                  colorsToCache.set(state.user.id, state.user.color);
                }

                console.log(`📊 Awareness state for ${state.user.name}:`, {
                  is_typing: state.is_typing,
                  cursor_active: state.cursor_active,
                  clientId: clientId,
                  color: state.user.color,
                });

                // Track collaborator activity for versioning system
                if (state.user.id !== globalPresenceManager.currentUser.id) {
                  console.log("🔍 Processing collaborator for versioning:", {
                    collaboratorId: state.user.id,
                    collaboratorName: state.user.name,
                    currentUserId: globalPresenceManager.currentUser.id,
                    isTyping: state.is_typing,
                    cursorActive: state.cursor_active,
                    hasVersioningSystem: !!versioningSystem,
                  });

                  // Another user is active - track their activity for versioning
                  if (versioningSystem) {
                    // ALWAYS track collaborator activity when they're present, regardless of current state
                    // This ensures we capture all users who have been active since the last version

                    // Track as edit activity if they're actively typing or have cursor
                    if (state.is_typing || state.cursor_active) {
                      if (versioningSystem.trackCollaboratorEdit) {
                        console.log("👥 Tracking collaborator edit activity:", {
                          userId: state.user.id,
                          userName: state.user.name,
                          isTyping: state.is_typing,
                          cursorActive: state.cursor_active,
                        });
                        versioningSystem.trackCollaboratorEdit(
                          state.user.id,
                          state.user.name,
                        );
                      }
                    }

                    // ALWAYS track presence activity for any collaborator who is present
                    // This is the key fix - we track ALL collaborators, not just active ones
                    if (versioningSystem.trackCollaboratorPresence) {
                      console.log(
                        "👥 Tracking collaborator presence activity (always):",
                        {
                          userId: state.user.id,
                          userName: state.user.name,
                          isTyping: state.is_typing,
                          cursorActive: state.cursor_active,
                        },
                      );
                      versioningSystem.trackCollaboratorPresence(
                        state.user.id,
                        state.user.name,
                      );
                    }

                    // Additional tracking for session presence
                    // This ensures we don't miss users who were active but are no longer typing
                    if (versioningSystem.trackCollaboratorPresence) {
                      console.log(
                        "👥 Tracking collaborator session presence:",
                        { userId: state.user.id, userName: state.user.name },
                      );
                      versioningSystem.trackCollaboratorPresence(
                        state.user.id,
                        state.user.name,
                      );
                    }
                  } else {
                    console.log(
                      "⚠️ Versioning system not available for collaborator tracking",
                    );
                  }
                } else {
                  console.log(
                    "🔍 Skipping current user for collaborator tracking:",
                    {
                      userId: state.user.id,
                      userName: state.user.name,
                    },
                  );
                }
              }
            }

            console.log(`📊 Total awareness users: ${awarenessUserIds.size}`);
            console.log(`📊 Awareness user IDs:`, Array.from(awarenessUserIds));

            // Cache all extracted colors in globalPresenceManager
            // This ensures colors from edit-doc pages are available to PatientPage
            if (colorsToCache.size > 0) {
              globalPresenceManager.cacheUserColors(colorsToCache);
              console.log("🎨 Cached user colors from awareness states:", {
                count: colorsToCache.size,
                colors: Array.from(colorsToCache.entries()).map(
                  ([userId, color]) => ({
                    userId,
                    color,
                    userName:
                      awarenessStatesMap.get(userId)?.user?.name || "Unknown",
                  }),
                ),
              });
            }

            // Yjs often omits cursor_active; infer "active" from cursor payload so avatars match live collab.
            const awarenessImpliesActiveCursor = (state) => {
              if (!state) return false;
              if (state.cursor_active === true) return true;
              const c = state.cursor;
              if (!c) return false;
              if (typeof c.anchor === "number" || typeof c.head === "number")
                return true;
              if (c.anchor?.index !== undefined || c.head?.index !== undefined)
                return true;
              if (typeof c.index === "number") return true;
              return false;
            };

            const patientIdForPresence = uuid || validId;
            const isOnThisDocument = (userState) =>
              !!userState &&
              !!filePath &&
              String(userState.document_path || "") === String(filePath);

            // Upsert EVERY collaborator in Yjs awareness (overrides global-presence from Supabase alone).
            // Previously only source===websocket-awareness was updated, so joiners stayed "inactive" blue.
            let hasChanges = false;
            for (const [userId, awarenessState] of awarenessStatesMap) {
              if (!awarenessState?.user?.id) continue;

              // While in grace period, skip awareness upserts for this user. Yjs can briefly
              // still list a client after disconnect; clearing isStale here races with
              // updateActiveUsers() and drops the avatar in ~1–2s. Real rejoin clears stale
              // via useCollaboration awareness "added" -> cancelGracePeriod, then the next
              // poll runs this loop with isUserStale false.
              if (globalPresenceManager.isUserStale(userId)) {
                continue;
              }

              const isTyping = awarenessState.is_typing === true;
              const cursorActive =
                awarenessImpliesActiveCursor(awarenessState) ||
                awarenessState.cursor_active === true;

              const existingColor =
                globalPresenceManager.getCachedUserColor(userId);
              const colorToUse =
                awarenessState.user.color || existingColor;
              const prev = globalPresenceManager.activeUsers.get(userId);

              globalPresenceManager.activeUsers.set(userId, {
                ...prev,
                user_id: userId,
                name: awarenessState.user.name,
                email: awarenessState.user.email || "Guest",
                is_typing: isTyping,
                cursor_active: cursorActive,
                last_activity: new Date().toISOString(),
                document_path: filePath,
                patient_id: patientIdForPresence,
                patient_name: extractedName,
                schedule_date: visitDayForPresence,
                source: "websocket-awareness",
                isStale: false,
                disconnectedAt: undefined,
              });

              if (colorToUse) {
                globalPresenceManager.cacheUserColor(userId, colorToUse);
              }
              hasChanges = true;
            }

            const currentPresenceId = globalPresenceManager.currentUser?.id;
            // Remote users left Yjs awareness for this doc — grace period (skip local user).
            for (const [userId, userState] of globalPresenceManager.activeUsers) {
              if (currentPresenceId && userId === currentPresenceId) continue;
              if (awarenessUserIds.has(userId)) continue;
              if (userState.source !== "websocket-awareness") continue;
              if (!isOnThisDocument(userState)) continue;
              if (userState.isStale) continue;

              console.log(
                `⏳ User ${userState.name} no longer in awareness, starting grace period`,
              );
              const existingColor =
                globalPresenceManager.getCachedUserColor(userId);
              if (existingColor) {
                globalPresenceManager.cacheUserColor(userId, existingColor);
              }
              globalPresenceManager.startGracePeriod(userId, {
                ...userState,
                document_path: filePath,
                patient_id: patientIdForPresence,
                schedule_date:
                  userState.schedule_date || visitDayForPresence,
              });
              hasChanges = true;
            }

            // Only notify subscribers if there were actual changes
            if (hasChanges) {
              globalPresenceManager.notifySubscribers();
              console.log("🔔 Notified subscribers of changes");
            }

            // Debug logging for typing status
            const allUsers = Array.from(
              globalPresenceManager.activeUsers.values(),
            );
            const typingUsers = allUsers.filter(
              (user) => user.is_typing === true,
            );
            console.log(
              "🔌 Global presence manager updated from WebSocket awareness:",
              {
                totalUsers: allUsers.length,
                typingUsers: typingUsers.length,
                typingUsersList: typingUsers.map((u) => ({
                  name: u.name,
                  is_typing: u.is_typing,
                })),
                allUsers: allUsers.map((u) => ({
                  name: u.name,
                  is_typing: u.is_typing,
                  cursor_active: u.cursor_active,
                })),
              },
            );

            // Additional debug: Show awareness states vs global presence states
            console.log("🔍 Awareness vs Global Presence Comparison:");
            for (const [clientId, state] of awarenessStates) {
              if (state.user && state.user.id) {
                const globalUser = globalPresenceManager.activeUsers.get(
                  state.user.id,
                );
                console.log(`  - ${state.user.name} (${state.user.id}):`, {
                  awareness: {
                    is_typing: state.is_typing,
                    cursor_active: state.cursor_active,
                  },
                  global: {
                    is_typing: globalUser?.is_typing,
                    cursor_active: globalUser?.cursor_active,
                  },
                  match: {
                    typing: state.is_typing === globalUser?.is_typing,
                    cursor: state.cursor_active === globalUser?.cursor_active,
                  },
                });
              }
            }

            // Show users who are NOT in awareness but still in global presence
            console.log("🔍 Users in Global Presence but NOT in Awareness:");
            for (const [
              userId,
              userState,
            ] of globalPresenceManager.activeUsers) {
              if (
                userState.source === "websocket-awareness" &&
                !awarenessUserIds.has(userId)
              ) {
                console.log(
                  `  - ${userState.name} (${userId}): is_typing=${userState.is_typing}, cursor_active=${userState.cursor_active}`,
                );
              }
            }
          }
        })
        .catch((error) => {
          console.error("❌ Error importing global presence manager:", error);
        });
    } catch (error) {
      console.error("❌ Error updating global presence from awareness:", error);
    }
  };

  // **Custom Hooks**

  // Document operations hook
  const {
    scanForSections: scanForSectionsFromHook,
    handleSaveDoc: handleSaveDocFromHook,
    handleRemoveDuplicates: handleRemoveDuplicatesFromHook,
    updateDocumentTable,
    getCurrentUser,
    fetchUpdatedDocument,
  } = useDocumentOperations({
    editorRef,
    filePath,
    sections,
    setSections,
    setActiveSection,
    extractedName,
    validId,
    effectiveDate,
    correctedType,
    isCombined,
    createNew,
    patientScheduledTime,
    navbarTime,
    name,
    userInfo,
    clientId,
    setDocumentLoaded,
    setContentLoadingComplete,
    setTime,
    setIsSaving,
    setSaveSuccess,
    isUnmounting,
    setIsUnmounting,
    checkContentStability,
    delayedScanExecuted,
    contentLoadingLock,
    contentLoadAttempts,
    maxContentLoadAttempts,
    toast,
  });

  // Collaboration hook
  const {
    provider: collaborationProvider,
    bindingRef: collaborationBindingRef,
    versioningSystem: collaborationVersioningSystem,
  } = useCollaboration({
    editorRef,
    filePath: safeShareMode ? null : filePath,
    validId,
    actualPatientId, // Pass actual patient UUID for presence tracking
    extractedName,
    documentLoaded,
    contentLoadingComplete,
    correctedType,
    setIsConnected,
    setConnectionStatus,
    setUserColors,
    setHighlightedUser,
    highlightedUser, // Pass highlightedUser state to hook
    setVersioningSystem,
    handleSaveDoc: handleSaveDocFromHook,
    updateGlobalPresenceFromAwareness,
    websocketSetupLock,
    versioningSystem,
    setIsLoadingDocument,
    isLoadingDocument,
    scheduleDate: effectiveDate,
  });

  // Apply visual highlighting to multi-selections (both local and remote)
  // This must be after useCollaboration hook to access collaborationProvider
  const applyMultiSelectionHighlighting = useCallback(() => {
    // Check if component is still mounted
    if (!isMountedRef.current || isUnmounting) return;

    if (!editorRef.current) return;

    const editor = editorRef.current.getEditor(0);
    if (!editor) {
      console.log("Editor not ready for multi-selection highlighting");
      return;
    }

    // Remove existing multi-selection highlights (both local and remote)
    const existingHighlights = document.querySelectorAll(
      ".multi-selection-highlight, .remote-multi-selection-highlight",
    );
    existingHighlights.forEach((highlight) => highlight.remove());

    // Apply highlighting to local selections
    multiSelections.forEach((selection, index) => {
      try {
        const bounds = editor.getBounds(selection.index, selection.length);
        if (bounds) {
          const highlight = document.createElement("div");
          highlight.className = "multi-selection-highlight";
          highlight.style.position = "absolute";
          highlight.style.left = `${bounds.left}px`;
          highlight.style.top = `${bounds.top}px`;
          highlight.style.width = `${bounds.width}px`;
          highlight.style.height = `${bounds.height}px`;
          highlight.style.backgroundColor = `rgba(33, 150, 243, 0.3)`;
          highlight.style.border = "none";
          highlight.style.borderRadius = "0";
          highlight.style.pointerEvents = "auto";
          highlight.style.zIndex = "1000";
          highlight.style.cursor = "move";
          highlight.setAttribute("data-selection-id", selection.id);
          highlight.draggable = true;

          const editorContainer = editor.container;
          if (editorContainer) {
            editorContainer.appendChild(highlight);
          }
        }
      } catch (error) {
        console.warn("Could not highlight selection:", error);
      }
    });

    // Apply highlighting to remote selections from other users
    const provider = collaborationProvider;
    if (provider && provider.awareness && isMountedRef.current) {
      // Re-check provider is still valid before accessing awareness
      if (!provider.awareness) return;

      let states;
      try {
        states = provider.awareness.getStates();
      } catch (error) {
        console.warn("Error getting awareness states:", error);
        return;
      }

      // Get current user ID to exclude own selections
      (async () => {
        try {
          // Re-check if component is still mounted and provider is still valid
          if (!isMountedRef.current || !provider || !provider.awareness) {
            return;
          }

          const supabaseInstance = await getSupabase();
          const {
            data: { session },
          } = await supabaseInstance.auth.getSession();
          const currentUserId = session?.user?.id;

          // Re-check again after async operation
          if (!isMountedRef.current || !provider || !provider.awareness) {
            return;
          }

          states.forEach((state, clientId) => {
            // Skip current user's selections (already handled above)
            if (state.user && state.user.id === currentUserId) return;

            // Get remote user's multi-selections
            const remoteSelections = state.multi_selections || [];
            if (remoteSelections.length === 0) return;

            const userColor = state.user?.color || "#2196f3";
            const userName = state.user?.name || "Unknown";

            // Apply highlighting for each remote selection
            remoteSelections.forEach((selection) => {
              try {
                const bounds = editor.getBounds(
                  selection.index,
                  selection.length,
                );
                if (bounds) {
                  const highlight = document.createElement("div");
                  highlight.className = "remote-multi-selection-highlight";
                  highlight.style.position = "absolute";
                  highlight.style.left = `${bounds.left}px`;
                  highlight.style.top = `${bounds.top}px`;
                  highlight.style.width = `${bounds.width}px`;
                  highlight.style.height = `${bounds.height}px`;
                  highlight.style.backgroundColor = `${userColor}40`; // Add transparency
                  highlight.style.border = `2px solid ${userColor}`;
                  highlight.style.borderRadius = "2px";
                  highlight.style.pointerEvents = "none"; // Remote selections are not interactive
                  highlight.style.zIndex = "999";
                  highlight.setAttribute("data-user-id", state.user.id);
                  highlight.setAttribute("data-user-name", userName);
                  highlight.setAttribute("data-selection-id", selection.id);
                  highlight.title = `${userName}'s selection`;

                  const editorContainer = editor.container;
                  if (editorContainer) {
                    editorContainer.appendChild(highlight);
                  }
                }
              } catch (error) {
                console.warn(
                  `Could not highlight remote selection for ${userName}:`,
                  error,
                );
              }
            });
          });
        } catch (error) {
          console.warn(
            "Error applying remote multi-selection highlights:",
            error,
          );
        }
      })();
    }
  }, [multiSelections, collaborationProvider]);

  // Sync multi-selections to WebSocket awareness for collaboration
  // This must be after useCollaboration hook to access collaborationProvider
  useEffect(() => {
    // Check if component is still mounted
    if (!isMountedRef.current || isUnmounting) return;

    const provider = collaborationProvider;
    if (!provider || !provider.awareness) return;

    // Double-check provider is still valid before accessing
    try {
      // Convert multi-selections to a format that can be serialized
      const serializedSelections = multiSelections.map((sel) => ({
        index: sel.index,
        length: sel.length,
        id: sel.id,
      }));

      // Update awareness state with multi-selections
      provider.awareness.setLocalStateField(
        "multi_selections",
        serializedSelections,
      );

      console.log(
        "🔄 Synced multi-selections to awareness:",
        serializedSelections,
      );
    } catch (error) {
      // Provider might have been cleaned up, ignore the error
      if (isMountedRef.current) {
        console.warn("Error syncing multi-selections to awareness:", error);
      }
    }
  }, [multiSelections, collaborationProvider, isUnmounting]);

  // Apply formatting to all multi-selections
  // This must be after applyMultiSelectionHighlighting is defined
  const applyFormatToMultiSelections = useCallback(
    (format) => {
      if (!editorRef.current || multiSelections.length === 0) {
        console.log(
          "applyFormatToMultiSelections: No multi-selections or editor not available",
        );
        return;
      }

      const editor = editorRef.current.getEditor(0);
      if (!editor) {
        console.log("applyFormatToMultiSelections: Editor not available");
        return;
      }

      console.log(
        `applyFormatToMultiSelections: Applying format to ${multiSelections.length} selections:`,
        format,
      );

      // Store current selection to restore later
      const currentSelection = editor.getSelection();

      // Apply formatting to each multi-selection
      multiSelections.forEach((selection, index) => {
        try {
          console.log(
            `applyFormatToMultiSelections: Processing selection ${index + 1}/${multiSelections.length}:`,
            {
              index: selection.index,
              length: selection.length,
            },
          );

          // For bold, italic, underline, strike - check if already formatted and toggle
          if (
            format.bold !== undefined ||
            format.italic !== undefined ||
            format.underline !== undefined ||
            format.strike !== undefined
          ) {
            // Get current format at the start of the selection
            // Ensure we have a valid range to check
            const checkIndex = Math.max(
              0,
              Math.min(selection.index, editor.getLength() - 1),
            );
            const checkLength = Math.max(
              1,
              Math.min(selection.length, editor.getLength() - checkIndex),
            );

            const currentFormat = editor.getFormat(checkIndex, checkLength);
            console.log(
              `applyFormatToMultiSelections: Current format for selection ${index + 1}:`,
              currentFormat,
            );

            // Create format object with toggled values
            const formatToApply = {};

            if (format.bold !== undefined) {
              formatToApply.bold = !currentFormat.bold;
              console.log(
                `applyFormatToMultiSelections: Toggling bold from ${currentFormat.bold} to ${formatToApply.bold}`,
              );
            }
            if (format.italic !== undefined) {
              formatToApply.italic = !currentFormat.italic;
              console.log(
                `applyFormatToMultiSelections: Toggling italic from ${currentFormat.italic} to ${formatToApply.italic}`,
              );
            }
            if (format.underline !== undefined) {
              formatToApply.underline = !currentFormat.underline;
              console.log(
                `applyFormatToMultiSelections: Toggling underline from ${currentFormat.underline} to ${formatToApply.underline}`,
              );
            }
            if (format.strike !== undefined) {
              formatToApply.strike = !currentFormat.strike;
              console.log(
                `applyFormatToMultiSelections: Toggling strike from ${currentFormat.strike} to ${formatToApply.strike}`,
              );
            }

            // Ensure we have valid indices
            const applyIndex = Math.max(
              0,
              Math.min(selection.index, editor.getLength() - 1),
            );
            const applyLength = Math.max(
              0,
              Math.min(selection.length, editor.getLength() - applyIndex),
            );

            if (applyLength > 0 && Object.keys(formatToApply).length > 0) {
              console.log(
                `applyFormatToMultiSelections: Applying format to selection ${index + 1} at index ${applyIndex}, length ${applyLength}:`,
                formatToApply,
              );
              editor.formatText(applyIndex, applyLength, formatToApply, "user");
              console.log(
                `applyFormatToMultiSelections: Successfully applied format to selection ${index + 1}`,
              );
            } else {
              console.warn(
                `applyFormatToMultiSelections: Skipping selection ${index + 1} - invalid range or no format to apply`,
              );
            }
          } else {
            // For other formats (size, color, etc.), apply directly
            const applyIndex = Math.max(
              0,
              Math.min(selection.index, editor.getLength() - 1),
            );
            const applyLength = Math.max(
              0,
              Math.min(selection.length, editor.getLength() - applyIndex),
            );

            if (applyLength > 0) {
              console.log(
                `applyFormatToMultiSelections: Applying format directly to selection ${index + 1}:`,
                format,
              );
              editor.formatText(applyIndex, applyLength, format, "user");
            }
          }
        } catch (error) {
          console.error(
            `applyFormatToMultiSelections: Error applying format to selection ${index + 1}:`,
            error,
            selection,
          );
        }
      });

      // Restore original selection
      if (currentSelection) {
        editor.setSelection(currentSelection.index, currentSelection.length);
      }

      // Update highlighting after formatting
      setTimeout(() => {
        applyMultiSelectionHighlighting();
      }, 100);
    },
    [multiSelections, applyMultiSelectionHighlighting],
  );

  // Update highlighting when selections change
  // This must be after applyMultiSelectionHighlighting is defined
  useEffect(() => {
    // Check if component is still mounted
    if (!isMountedRef.current || isUnmounting) return;

    // Add a small delay to ensure editor is ready
    const timer = setTimeout(() => {
      if (isMountedRef.current && !isUnmounting) {
        applyMultiSelectionHighlighting();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [applyMultiSelectionHighlighting, isUnmounting]);

  // Listen for remote multi-selection changes from WebSocket
  // This must be after applyMultiSelectionHighlighting is defined
  useEffect(() => {
    const handleMultiSelectionsChanged = () => {
      // Check if component is still mounted before updating
      if (isMountedRef.current && !isUnmounting) {
        // Trigger highlighting update when remote multi-selections change
        applyMultiSelectionHighlighting();
      }
    };

    window.addEventListener(
      "multi-selections-changed",
      handleMultiSelectionsChanged,
    );

    // Lock/unlock editor when another collaborator triggers AI doc processing.
    // We don't call editor.enable() to avoid Quill re-renders stripping formatting;
    // the remote user's locked state is surfaced via their awareness, and we rely on
    // the Yjs binding to keep their doc in sync.  A future improvement could show a
    // read-only overlay for remote users too, but for now just log it.
    const handleRemoteDocLock = (e) => {
      if (!isMountedRef.current || isUnmounting) return;
      if (isDocProcessingLocked) return; // local owner — already handled
      console.log(`🔒 Remote doc lock changed: locked=${e.detail?.locked}`);
    };
    window.addEventListener(
      "ai-doc-processing-lock-changed",
      handleRemoteDocLock,
    );

    return () => {
      window.removeEventListener(
        "multi-selections-changed",
        handleMultiSelectionsChanged,
      );
      window.removeEventListener(
        "ai-doc-processing-lock-changed",
        handleRemoteDocLock,
      );
    };
  }, [applyMultiSelectionHighlighting, isUnmounting, isDocProcessingLocked]);

  // **Helper Functions**

  /** Calculate age from date of birth as of a specific date */
  const calculateAge = (dob, asOfDate = null) => {
    if (!dob) return "";

    try {
      const birthDate = new Date(dob);
      // Use the scheduled date if provided, otherwise use today
      const referenceDate = asOfDate ? new Date(asOfDate) : new Date();

      // Check if the dates are valid
      if (isNaN(birthDate.getTime())) {
        console.warn("Invalid date of birth:", dob);
        return "";
      }
      if (isNaN(referenceDate.getTime())) {
        console.warn("Invalid reference date:", asOfDate);
        return "";
      }

      console.log(
        "📅 Age calculation: DOB:",
        dob,
        "As of date:",
        asOfDate || "today",
        "→ Reference:",
        referenceDate.toISOString().split("T")[0],
      );

      let age = referenceDate.getFullYear() - birthDate.getFullYear();
      const monthDiff = referenceDate.getMonth() - birthDate.getMonth();

      // If birthday hasn't occurred yet in the reference year, subtract 1
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && referenceDate.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      console.log("📅 Calculated age:", age);
      return age.toString();
    } catch (error) {
      console.error("Error calculating age:", error);
      return "";
    }
  };

  /** Toggle sidebar visibility */
  const toggleSidebar = () => {
    setIsSidebarVisible((prev) => !prev);
  };

  /** Toggle patient list drawer */
  const togglePatientList = () => {
    setIsPatientListOpen((prev) => !prev);
  };

  /** Initialize Quill editor for a specific page */
  const initializeQuill = (index) => {
    const existingQuill = quillRefs.current[index];
    const container = containerRefs.current[index];
    console.log(
      `🔧 [initializeQuill] index=${index} container=${!!container} existingQuill=${!!existingQuill} (guard will ${!container || existingQuill ? "SKIP" : "PROCEED"})`,
    );
    if (!container || existingQuill) return;

    // Custom image upload handler — defined here so it's set at Quill init time
    const customImageHandler = function () {
      console.log("🖼️ [Image] customImageHandler fired (custom, not default)");
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", "image/*");
      input.click();
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) {
          console.log("🖼️ [Image] No file selected");
          return;
        }
        console.log(
          "🖼️ [Image] File selected:",
          file.name,
          "size:",
          file.size,
          "type:",
          file.type,
        );

        const currentFilePath = filePathRef.current;
        console.log("🖼️ [Image] Current filePath for dir:", currentFilePath);
        const dir = currentFilePath
          ? currentFilePath.substring(0, currentFilePath.lastIndexOf("/"))
          : "New/MedADocs/images";
        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const imagePath = `${dir}/img_${timestamp}_${safeName}`;
        console.log("🖼️ [Image] Uploading to path:", imagePath);

        try {
          const supabase = await getSupabase();
          const { error } = await supabase.storage
            .from(bucketName_files)
            .upload(imagePath, file, { upsert: true, contentType: file.type });

          if (error) {
            console.error("🖼️ [Image] Upload failed:", error.message);
            return;
          }
          console.log("🖼️ [Image] Upload succeeded");

          const { data: urlData } = supabase.storage
            .from(bucketName_files)
            .getPublicUrl(imagePath);
          const publicUrl = urlData?.publicUrl;
          console.log("🖼️ [Image] Public URL:", publicUrl);

          if (!publicUrl) {
            console.error("🖼️ [Image] Could not get public URL");
            return;
          }

          const quillInstance = quillRefs.current[0];
          // Use saved selection — clicking the toolbar button steals focus so getSelection() returns null
          const range = lastEditorSelectionRef.current || {
            index: 0,
            length: 0,
          };
          console.log("🖼️ [Image] Inserting embed at range:", range);
          quillInstance.insertEmbed(range.index, "image", publicUrl, "user");
          quillInstance.setSelection(range.index + 1, 0, "user");
          console.log(
            "🖼️ [Image] Embed inserted. Delta ops:",
            JSON.stringify(
              quillInstance.getContents().ops.filter((op) => op.insert?.image),
            ),
          );
        } catch (err) {
          console.error("🖼️ [Image] Error:", err);
        }
      };
    };

    const quill = new Quill(containerRefs.current[index], {
      theme: "snow",
      modules: {
        toolbar:
          index === 0
            ? {
                container: "#toolbar",
                handlers: {
                  image: customImageHandler,
                  header: function (value) {
                    // Apply the heading format
                    this.quill.format("header", value, "user");
                    // When switching to a heading, clear any inline size so heading CSS takes effect
                    if (value) {
                      const range = this.quill.getSelection();
                      if (range) {
                        this.quill.formatText(
                          range.index,
                          Math.max(range.length, 1),
                          "size",
                          false,
                          "user",
                        );
                      }
                    }
                  },
                },
              }
            : false,
        cursors: {
          transformOnTextChange: true,
          selectionChangeSource: "user", // Only user actions update cursor in awareness
          hideDelayMs: 5000, // Increased from 3000ms to 5000ms for longer visibility
          hideSpeedMs: 300, // Increased from 200ms to 300ms for smoother fade
          selectionOnChange: null,
        },
        history: {
          delay: 2000,
          maxStack: 500,
          userOnly: true,
        },
        blotFormatter: {},
      },
    });

    // Match Quill enable effect so new instances are correct if init runs after load finished
    // (effect deps do not fire when only quillRefs are populated).
    // userRole == null means role not fetched yet — keep editor disabled to
    // avoid a brief edit window for viewers who refresh the page.
    const allowEditForInit =
      userRole != null &&
      (!isInGroup(userRole, 'tenantSchema') ||
        tenantShareAllowsDocumentEdit(tenantRowAccess));
    quill.enable(!isLoadingDocument && allowEditForInit);

    // Intercept image pastes (Ctrl+V) + file drag/drop — Quill default would use data: URLs (base64)
    // (merged from frontend/src/pages/tmp/EditableDocumentPage.js)
    if (index === 0) {
      const pasteImageHandler = async (e) => {
        const items = Array.from(e.clipboardData?.items || []);
        const imageItem = items.find((item) => item.type.startsWith("image/"));
        if (!imageItem) return; // Not an image paste — let Quill handle it
        e.preventDefault();
        e.stopImmediatePropagation();
        const file = imageItem.getAsFile();
        if (!file) return;
        const currentFilePath = filePathRef.current;
        const dir = currentFilePath
          ? currentFilePath.substring(0, currentFilePath.lastIndexOf("/"))
          : "New/MedADocs/images";
        const timestamp = Date.now();
        const ext = imageItem.type.split("/")[1] || "png";
        const imagePath = `${dir}/img_${timestamp}_paste.${ext}`;
        try {
          const supabase = await getSupabase();
          const { error } = await supabase.storage
            .from(bucketName_files)
            .upload(imagePath, file, {
              upsert: true,
              contentType: imageItem.type,
            });
          if (error) {
            console.error("🖼️ [Image/Paste] Upload failed:", error.message);
            return;
          }
          const { data: urlData } = supabase.storage
            .from(bucketName_files)
            .getPublicUrl(imagePath);
          const publicUrl = urlData?.publicUrl;
          if (!publicUrl) return;
          const range = quill.getSelection(true) || { index: 0, length: 0 };
          quill.insertEmbed(range.index, "image", publicUrl, "user");
          quill.setSelection(range.index + 1, 0, "user");
        } catch (err) {
          console.error("🖼️ [Image/Paste] Error:", err);
        }
      };

      const quillDropBoundsEl = quill.container || quill.root;
      const isDropOnThisEditor = (e) =>
        quillDropBoundsEl && quillDropBoundsEl.contains(e.target);

      const IMAGE_FILE_EXT_RE = /\.(png|jpe?g|gif|webp|bmp|svg|heic|avif)$/i;
      const isImageFile = (f) => {
        if (!f) return false;
        if (f.type && f.type.startsWith("image/")) return true;
        if (f.name && IMAGE_FILE_EXT_RE.test(f.name)) return true;
        return false;
      };

      const extFromImageFile = (file) => {
        if (file.type && file.type.includes("/")) {
          const p = file.type.split("/")[1];
          return p === "jpeg" ? "jpg" : p;
        }
        const m = file.name && file.name.match(/\.([a-z0-9]+)$/i);
        if (m)
          return m[1].toLowerCase() === "jpeg" ? "jpg" : m[1].toLowerCase();
        return "png";
      };

      const collectImageFilesFromDataTransfer = (dt) => {
        if (!dt) return [];
        const out = [];
        const seen = new Set();
        const add = (f) => {
          if (!isImageFile(f)) return;
          const key = `${f.name}-${f.size}-${f.lastModified}`;
          if (seen.has(key)) return;
          seen.add(key);
          out.push(f);
        };
        if (dt.files?.length) {
          for (let i = 0; i < dt.files.length; i++) add(dt.files[i]);
        }
        if (out.length === 0 && dt.items?.length) {
          for (let i = 0; i < dt.items.length; i++) {
            const item = dt.items[i];
            if (item.kind !== "file") continue;
            add(item.getAsFile());
          }
        }
        return out;
      };

      const dragOverImageHandler = (e) => {
        if (!isDropOnThisEditor(e)) return;
        if (
          e.dataTransfer?.types &&
          Array.from(e.dataTransfer.types).includes("Files")
        ) {
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
        }
      };

      const dropImageHandler = async (e) => {
        if (!isDropOnThisEditor(e)) return;
        const imageFiles = collectImageFilesFromDataTransfer(e.dataTransfer);
        if (imageFiles.length === 0) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        let insertIndex = quill.getSelection(true)?.index;
        if (insertIndex == null) {
          try {
            const blot = Quill.find(e.target, true);
            if (blot) insertIndex = quill.getIndex(blot);
          } catch (_) {
            /* ignore */
          }
          if (insertIndex == null) {
            insertIndex = Math.max(0, quill.getLength() - 1);
          }
        }

        const currentFilePath = filePathRef.current;
        const dir = currentFilePath
          ? currentFilePath.substring(0, currentFilePath.lastIndexOf("/"))
          : "New/MedADocs/images";

        let nextIndex = insertIndex;
        for (let i = 0; i < imageFiles.length; i++) {
          const file = imageFiles[i];
          const timestamp = Date.now() + i;
          const ext = extFromImageFile(file);
          const imagePath = `${dir}/img_${timestamp}_drop.${ext}`;
          try {
            const supabase = await getSupabase();
            const contentType =
              file.type && file.type.startsWith("image/")
                ? file.type
                : `image/${ext === "jpg" ? "jpeg" : ext}`;
            const { error } = await supabase.storage
              .from(bucketName_files)
              .upload(imagePath, file, {
                upsert: true,
                contentType,
              });
            if (error) {
              console.error("🖼️ [Image/Drop] Upload failed:", error.message);
              continue;
            }
            const { data: urlData } = supabase.storage
              .from(bucketName_files)
              .getPublicUrl(imagePath);
            const publicUrl = urlData?.publicUrl;
            if (!publicUrl) continue;
            quill.insertEmbed(nextIndex, "image", publicUrl, "user");
            nextIndex += 1;
          } catch (err) {
            console.error("🖼️ [Image/Drop] Error:", err);
          }
        }
        quill.setSelection(nextIndex, 0, "user");
      };

      quill.root.addEventListener("paste", pasteImageHandler, true);
      document.addEventListener("dragover", dragOverImageHandler, true);
      document.addEventListener("drop", dropImageHandler, true);

      // Show blot-formatter resize handles instantly on image hover, hide them
      // when the mouse leaves. The formatter activates on click of an <img>, so
      // we dispatch a synthetic click on mouseover. The overlay is created
      // synchronously inside that click, so we can attach the dismissal listener
      // immediately afterwards. While a resize handle is being dragged the
      // cursor can travel outside the image bounds — skip dismissal in that
      // case so the drag isn't aborted mid-resize.
      let hoverImgTarget = null;
      let overlayLeaveCleanup = null;
      let isResizing = false;
      const detachOverlayLeave = () => {
        if (overlayLeaveCleanup) {
          overlayLeaveCleanup();
          overlayLeaveCleanup = null;
        }
      };
      const attachOverlayLeaveDismiss = () => {
        detachOverlayLeave();
        const overlay = quill.container.querySelector(
          ".blot-formatter__overlay",
        );
        if (!overlay) return;
        const onOverlayLeave = () => {
          if (isResizing) return;
          const formatter = quill.getModule("blotFormatter");
          if (formatter && typeof formatter.hide === "function") {
            formatter.hide();
          }
          hoverImgTarget = null;
          detachOverlayLeave();
        };
        overlay.addEventListener("mouseleave", onOverlayLeave);
        overlayLeaveCleanup = () => {
          overlay.removeEventListener("mouseleave", onOverlayLeave);
        };
      };
      const onImgMouseOver = (e) => {
        if (isResizing) return;
        // Don't trigger the formatter while the user is mid-drag (e.g. drag-
        // selecting text): the synthetic click would cancel their selection.
        if (e.buttons) return;
        const t = e.target;
        if (!t || t.tagName !== "IMG") return;
        if (hoverImgTarget === t) return;
        hoverImgTarget = t;
        t.click();
        attachOverlayLeaveDismiss();
      };
      const onResizePointerDown = (e) => {
        const tgt = e.target;
        if (
          tgt &&
          tgt.classList &&
          tgt.classList.contains("blot-formatter__resize-handle")
        ) {
          isResizing = true;
        }
      };
      const onResizePointerUp = () => {
        if (!isResizing) return;
        isResizing = false;
        // If the pointer ended outside the overlay, fire the deferred dismiss.
        const overlay = quill.container.querySelector(
          ".blot-formatter__overlay",
        );
        if (!overlay || !overlay.matches(":hover")) {
          const formatter = quill.getModule("blotFormatter");
          if (formatter && typeof formatter.hide === "function") {
            formatter.hide();
          }
          hoverImgTarget = null;
          detachOverlayLeave();
        }
        // Persist the new image size. The blot-formatter writes the new width
        // attribute inside its own pointerup handler; defer so Quill's
        // MutationObserver has flushed the change before we save.
        setTimeout(() => {
          try {
            quill.update();
          } catch (_) {
            /* ignore */
          }
          const saveFn = handleSaveDocRef.current;
          if (typeof saveFn === "function") saveFn();
        }, 0);
      };
      quill.root.addEventListener("mouseover", onImgMouseOver);
      document.addEventListener("pointerdown", onResizePointerDown, true);
      document.addEventListener("pointerup", onResizePointerUp, true);

      // Highlight images that are inside the user's text selection — browsers
      // don't apply the native ::selection wash to images, so we toggle a class
      // ourselves whenever the document selection changes.
      const onDocSelectionChange = () => {
        const sel = window.getSelection();
        const hasSelection =
          sel && sel.rangeCount > 0 && !sel.isCollapsed;
        const range = hasSelection ? sel.getRangeAt(0) : null;
        const roots = (containerRefs.current || []).filter(Boolean);
        for (const root of roots) {
          const imgs = root.querySelectorAll("img");
          for (const img of imgs) {
            if (!range) {
              img.classList.remove("is-text-selected");
              continue;
            }
            const imgRange = document.createRange();
            try {
              imgRange.selectNode(img);
            } catch (_) {
              continue;
            }
            // Overlap test: selection ends after image starts AND
            // selection starts before image ends.
            const endsAfterStart = range.compareBoundaryPoints(
              Range.START_TO_END,
              imgRange,
            );
            const startsBeforeEnd = range.compareBoundaryPoints(
              Range.END_TO_START,
              imgRange,
            );
            if (endsAfterStart > 0 && startsBeforeEnd < 0) {
              img.classList.add("is-text-selected");
            } else {
              img.classList.remove("is-text-selected");
            }
          }
        }
      };
      document.addEventListener("selectionchange", onDocSelectionChange);

      quillImageDomCleanupRef.current = () => {
        quill.root.removeEventListener("paste", pasteImageHandler, true);
        document.removeEventListener("dragover", dragOverImageHandler, true);
        document.removeEventListener("drop", dropImageHandler, true);
        quill.root.removeEventListener("mouseover", onImgMouseOver);
        document.removeEventListener("pointerdown", onResizePointerDown, true);
        document.removeEventListener("pointerup", onResizePointerUp, true);
        document.removeEventListener("selectionchange", onDocSelectionChange);
        detachOverlayLeave();
      };
    }

    // Store handler reference for cleanup
    const textChangeHandler = (delta, oldDelta, source) => {
      // Check if component is still mounted and editorRef exists
      if (!isMountedRef.current || isUnmounting || !editorRef.current) {
        return;
      }
      if (!editorRef.current.getEditor) {
        return;
      }
      if (!isPaginatingRef.current) {
        handleTextChange(delta, oldDelta, source, index);
      }
      // Update comment indices when text changes
      if (source === "user" && delta.ops && commentIndexUpdateRef.current) {
        commentIndexUpdateRef.current(delta, index);
      }
      // Delete Supabase-stored images that were removed from the document
      if (source === "user" && oldDelta?.ops) {
        const urlPrefix = `${supabaseUrl}/storage/v1/object/public/${bucketName_files}/`;
        const prevUrls = oldDelta.ops
          .filter(
            (op) =>
              typeof op.insert?.image === "string" &&
              op.insert.image.startsWith(urlPrefix),
          )
          .map((op) => op.insert.image);
        if (prevUrls.length > 0) {
          const currentUrls = new Set(
            quill
              .getContents()
              .ops.filter((op) => typeof op.insert?.image === "string")
              .map((op) => op.insert.image),
          );
          const removed = prevUrls.filter((url) => !currentUrls.has(url));
          removed.forEach(async (url) => {
            const storagePath = url.slice(urlPrefix.length);
            try {
              const supabase = await getSupabase();
              const { error } = await supabase.storage
                .from(bucketName_files)
                .remove([storagePath]);
              if (error)
                console.error(
                  "🖼️ [Image/Delete] Storage delete failed:",
                  error.message,
                );
            } catch (err) {
              console.error("🖼️ [Image/Delete] Error:", err);
            }
          });
        }
      }
    };
    quill.on("text-change", textChangeHandler);
    // Store handler for cleanup
    textChangeHandlersRef.current.push({
      quill,
      handler: textChangeHandler,
      type: "text-change",
    });

    quill.on("selection-change", (range, oldRange, source) => {
      // Save last known cursor position so image handler can use it after toolbar focus steal
      if (range !== null) {
        lastEditorSelectionRef.current = range;
      }

      handleSelectionChange(range, oldRange, source, index);

      // Update cursor position in awareness for collaboration
      // Use provider from collaboration hook
      // Check if component is still mounted before accessing provider
      if (!isMountedRef.current || isUnmounting) return;

      const provider = collaborationProvider;
      if (provider && provider.awareness && range) {
        // Double-check provider is still valid before accessing
        try {
          console.log("📍 Updating cursor position in awareness:", {
            index: range.index,
            length: range.length,
            source: source,
          });
          // Use the Yjs format that matches what we're seeing in the console
          // Store as Yjs positions using createRelativePosition
          const yText = provider.doc.getText("quill");
          const anchorPos = yText.createRelativePosition(range.index);
          const headPos = yText.createRelativePosition(
            range.index + (range.length || 0),
          );

          provider.awareness.setLocalStateField("cursor", {
            anchor: anchorPos,
            head: headPos,
          });
        } catch (error) {
          // Provider might have been cleaned up, ignore the error
          if (isMountedRef.current) {
            console.log("❌ Error creating Yjs cursor positions:", error);
          }
        }
      } else {
        // console.log('❌ Cannot update cursor position - provider or range not available');
      }
    });

    // Add format change listener to detect when headers are applied/removed
    const formatChangeHandler = (delta, oldDelta, source) => {
      // Check if component is still mounted
      if (!isMountedRef.current || isUnmounting) {
        return;
      }
      if (source === "user" && delta.ops) {
        // Check if any format changes occurred
        const hasFormatChange = delta.ops.some(
          (op) =>
            op.attributes &&
            (op.attributes.header !== undefined ||
              op.attributes.bold !== undefined),
        );
        if (hasFormatChange) {
          // Small delay to ensure format is applied
          setTimeout(() => {
            if (isMountedRef.current && !isUnmounting) {
              scanForSections();
            }
          }, 100);
        }
      }
    };
    quill.on("text-change", formatChangeHandler);
    // Store handler for cleanup
    textChangeHandlersRef.current.push({
      quill,
      handler: formatChangeHandler,
      type: "text-change-format",
    });

    // Log toolbar module status for debugging
    if (index === 0) {
      const toolbarModule = quill.getModule("toolbar");
      console.log(
        "🖼️ [Image] Toolbar module at init:",
        toolbarModule ? "found" : "null",
        "handlers:",
        JSON.stringify(Object.keys(toolbarModule?.handlers || {})),
      );
    }

    quillRefs.current[index] = quill;
    // Register ref after loading may have already cleared — re-sync enable immediately
    // so we do not rely only on the useEffect that may have run with zero quills.
    // Lock down while userRole is still loading to avoid a viewer-can-type window.
    {
      const allowEdit =
        userRole != null &&
        (!isInGroup(userRole, 'tenantSchema') ||
          tenantShareAllowsDocumentEdit(tenantRowAccess));
      const enabled = !isLoadingDocument && allowEdit;
      if (typeof quill.enable === "function") {
        quill.enable(enabled);
      }
    }
  };

  // WebSocket collaboration setup - handled by Yjs integration

  /** Handle clicking a section in the sidebar */
  const handleSectionClick = (id) => {
    if (!editorRef.current) {
      console.warn("EditorRef is null, cannot handle section click");
      return;
    }

    for (
      let pageIndex = 0;
      pageIndex < editorRef.current.getEditorCount();
      pageIndex++
    ) {
      const editor = editorRef.current.getEditor(pageIndex);
      const section = sections.find((sec) => sec.id === id);

      if (section && editor) {
        // Find the section and get its index
        const lines = editor.getLines();

        for (let i = 0; i < lines.length; i++) {
          const lineText = lines[i].domNode?.innerText.trim();

          if (lineText === section.title) {
            const index = editor.getIndex(lines[i]);

            // Use the same multi-tier centering approach as avatar clicks
            console.log(
              `🚀 [SECTION] Starting scroll to section "${section.title}" at index ${index}`,
            );

            // First try the native method (most reliable for centering)
            const nativeSuccess = scrollToCursorAndCenterNative(
              editor,
              index,
              0,
            );
            if (nativeSuccess) {
              console.log(
                `✅ [SECTION] Successfully centered section "${section.title}" with native method`,
              );
            } else {
              console.log(
                `⚠️ [SECTION] Native centering failed for "${section.title}", trying top-of-page method`,
              );
              // Try specialized top-of-page method
              const topSuccess = scrollToCursorAndCenterTop(editor, index, 0);
              if (topSuccess) {
                console.log(
                  `✅ [SECTION] Successfully centered section "${section.title}" with top-of-page method`,
                );
              } else {
                console.log(
                  `⚠️ [SECTION] Top-of-page centering failed for "${section.title}", trying robust method`,
                );
                // Fallback to robust async method
                scrollToCursorAndCenterRobust(editor, index, 0)
                  .then((success) => {
                    if (success) {
                      console.log(
                        `✅ [SECTION] Successfully centered section "${section.title}" with robust method`,
                      );
                    } else {
                      console.log(
                        `❌ [SECTION] All centering methods failed for "${section.title}"`,
                      );
                    }
                  })
                  .catch((error) => {
                    console.error(
                      `❌ [SECTION] Error in robust centering for "${section.title}":`,
                      error,
                    );
                  });
              }
            }
            setActiveSection(section.id);

            // Add a brief highlight effect to make the centering more obvious
            const lineElement = lines[i].domNode;
            if (lineElement) {
              lineElement.style.backgroundColor = "#fff3cd";
              lineElement.style.transition = "background-color 0.3s ease";

              // Remove the highlight after 2 seconds
              setTimeout(() => {
                lineElement.style.backgroundColor = "";
              }, 2000);
            }

            break;
          }
        }
        break;
      }
    }
  };

  /** Scroll to a user's cursor position */
  const scrollToUserCursor = (user) => {
    // Check if component is still mounted
    if (!isMountedRef.current || isUnmounting) {
      console.log(
        "❌ [MAIN] Component unmounting, cannot scroll to user cursor",
      );
      return;
    }

    const startTime = Date.now();
    console.group(
      `🎯 [MAIN] Avatar clicked for user at ${new Date().toISOString()}`,
    );
    console.log("User data:", user, "isStale:", user.isStale);
    console.groupEnd();

    const editor = editorRef.current?.getEditor(0);
    if (!editor) {
      console.log("❌ [MAIN] Editor not available");
      return;
    }

    // Check if user is stale - if so, use last known cursor position
    if (user.isStale) {
      console.log(
        `⏳ [MAIN] User ${user.name} is stale, checking for last known cursor position...`,
      );
      const lastKnownCursor = globalPresenceManager.getLastKnownCursor(
        user.user_id,
      );

      if (lastKnownCursor && lastKnownCursor.index !== undefined) {
        console.log(
          `📍 [MAIN] Found last known cursor for stale user ${user.name}:`,
          lastKnownCursor,
        );

        // Scroll to the last known cursor position
        try {
          editor.setSelection(
            lastKnownCursor.index,
            lastKnownCursor.length || 0,
          );
          scrollToCursorAndCenterNative(
            editor,
            lastKnownCursor.index,
            lastKnownCursor.length || 0,
          );
          console.log(
            `✅ [MAIN] Scrolled to last known position for stale user ${user.name}`,
          );
        } catch (error) {
          console.error(
            `❌ [MAIN] Error scrolling to last known cursor for stale user:`,
            error,
          );
        }
        return;
      } else {
        console.log(
          `❌ [MAIN] No last known cursor for stale user ${user.name}`,
        );
        return;
      }
    }

    // Use provider from collaboration hook
    const provider = collaborationProvider;

    if (!provider || !provider.awareness) {
      console.log("❌ [MAIN] Provider or awareness not available");
      return;
    }

    // Double-check provider is still valid before accessing
    try {
      if (!provider.awareness) {
        console.log("❌ [MAIN] Provider awareness no longer available");
        return;
      }
    } catch (error) {
      console.log("❌ [MAIN] Error checking provider awareness:", error);
      return;
    }

    console.log(
      `🔍 [MAIN] Editor and provider available, starting cursor search...`,
    );

    // Get all awareness states
    let awareness;
    let states;
    try {
      awareness = provider.awareness;
      if (!awareness) {
        console.log("❌ [MAIN] Awareness not available");
        return;
      }
      states = awareness.getStates();
    } catch (error) {
      console.log("❌ [MAIN] Error accessing awareness:", error);
      return;
    }

    console.group(`🔍 [MAIN] All awareness states (${states.size} total)`);
    console.log("States:", Array.from(states.entries()));
    console.groupEnd();
    console.log(`🔍 [MAIN] Looking for user ID: ${user.user_id}`);

    // Find the user's cursor position
    let userFound = false;
    for (const [clientId, state] of states) {
      console.log(`🔍 [MAIN] Checking state for clientId: ${clientId}`, {
        hasUser: !!state.user,
        userId: state.user?.id,
        hasCursor: !!state.cursor,
        cursorType: state.cursor ? typeof state.cursor : "none",
      });

      if (state.user && state.user.id === user.user_id) {
        userFound = true;
        console.log(
          `✅ [MAIN] Found matching user state for ${user.name}:`,
          JSON.stringify(state, null, 2),
        );
        const cursor = state.cursor;
        if (cursor) {
          console.log(
            `🎯 [MAIN] Scrolling to ${user.name}'s cursor at position:`,
            JSON.stringify(cursor, null, 2),
          );

          // Handle different cursor position formats
          let cursorIndex, cursorLength;

          console.log(`🔧 [MAIN] Processing cursor format for ${user.name}:`, {
            hasAnchor: cursor.anchor !== undefined,
            hasHead: cursor.head !== undefined,
            hasIndex: cursor.index !== undefined,
            anchorType: typeof cursor.anchor,
            headType: typeof cursor.head,
            indexType: typeof cursor.index,
          });

          if (cursor.anchor !== undefined && cursor.head !== undefined) {
            console.log(
              `🔧 [MAIN] Using Yjs anchor/head format for ${user.name}`,
            );
            // Yjs format with anchor and head as objects containing items
            let anchorValue, headValue;

            // Yjs items have client and clock, not index
            // We need to convert Yjs position to text position
            if (cursor.anchor.item && cursor.anchor.item.client !== undefined) {
              console.log(
                `🔧 [MAIN] Converting Yjs anchor position for ${user.name}:`,
                cursor.anchor,
              );
              // This is a Yjs position - we need to convert it to text position
              try {
                // Use the correct Yjs API to convert relative position to absolute position
                const absolutePos =
                  Y.createAbsolutePositionFromRelativePosition(
                    cursor.anchor,
                    provider.doc,
                  );
                if (
                  absolutePos &&
                  absolutePos.type &&
                  absolutePos.index !== undefined
                ) {
                  anchorValue = absolutePos.index;
                  console.log(
                    `📍 [MAIN] Converted Yjs anchor position for ${user.name}: ${cursor.anchor.item.client}:${cursor.anchor.item.clock} -> ${anchorValue}`,
                  );
                } else {
                  console.log(
                    `❌ [MAIN] Could not convert Yjs anchor position for ${user.name} - invalid absolute position:`,
                    JSON.stringify(absolutePos, null, 2),
                  );
                  anchorValue = undefined;
                }
              } catch (error) {
                console.log(
                  `❌ [MAIN] Error converting Yjs anchor position for ${user.name}:`,
                  error,
                );
                anchorValue = undefined;
              }
            } else if (cursor.anchor.index !== undefined) {
              anchorValue = cursor.anchor.index;
              console.log(
                `📍 [MAIN] Using direct anchor index for ${user.name}: ${anchorValue}`,
              );
            } else if (typeof cursor.anchor === "number") {
              anchorValue = cursor.anchor;
              console.log(
                `📍 [MAIN] Using numeric anchor for ${user.name}: ${anchorValue}`,
              );
            }

            if (cursor.head.item && cursor.head.item.client !== undefined) {
              // This is a Yjs position - we need to convert it to text position
              try {
                // Use the correct Yjs API to convert relative position to absolute position
                const absolutePos =
                  Y.createAbsolutePositionFromRelativePosition(
                    cursor.head,
                    provider.doc,
                  );
                if (
                  absolutePos &&
                  absolutePos.type &&
                  absolutePos.index !== undefined
                ) {
                  headValue = absolutePos.index;
                  console.log(
                    `📍 Converted Yjs head position: ${cursor.head.item.client}:${cursor.head.item.clock} -> ${headValue}`,
                  );
                } else {
                  console.log(
                    "❌ Could not convert Yjs head position - invalid absolute position:",
                    JSON.stringify(absolutePos, null, 2),
                  );
                  headValue = undefined;
                }
              } catch (error) {
                console.log("❌ Error converting Yjs head position:", error);
                headValue = undefined;
              }
            } else if (cursor.head.index !== undefined) {
              headValue = cursor.head.index;
            } else if (typeof cursor.head === "number") {
              headValue = cursor.head;
            }

            if (anchorValue !== undefined && headValue !== undefined) {
              cursorIndex = anchorValue;
              cursorLength = headValue - anchorValue;
              console.log(
                `📍 [MAIN] Using Yjs anchor/head format for ${user.name}: anchor=${anchorValue}, head=${headValue}, index=${cursorIndex}, length=${cursorLength}`,
              );
            } else {
              console.log(
                `❌ [MAIN] Could not extract position from Yjs cursor for ${user.name}:`,
                JSON.stringify(
                  { anchor: cursor.anchor, head: cursor.head },
                  null,
                  2,
                ),
              );
              return;
            }
          } else if (cursor.index !== undefined) {
            // Old format with index and length
            cursorIndex = cursor.index;
            cursorLength = cursor.length || 0;
            console.log(
              `📍 [MAIN] Using index/length format for ${user.name}: index=${cursorIndex}, length=${cursorLength}`,
            );
          } else {
            console.log(
              `❌ [MAIN] Unknown cursor format for ${user.name}:`,
              JSON.stringify(cursor, null, 2),
            );
            return;
          }

          console.log(
            `[LOG-10] 🚀 [MAIN] Starting scroll to cursor for ${user.name} at index ${cursorIndex}, length ${cursorLength}`,
          );

          // Check if cursor is within the top 16 lines - if so, just scroll to top
          try {
            const lines = editor.getLines();
            let cursorLineIndex = -1;

            // Find which line the cursor is on
            for (let i = 0; i < lines.length; i++) {
              const line = lines[i];
              if (!line) continue;

              const lineIndex = editor.getIndex(line);
              const lineLength = line.length();

              // Check if cursor is within this line (inclusive of line start, exclusive of next line start)
              if (
                cursorIndex >= lineIndex &&
                cursorIndex < lineIndex + lineLength
              ) {
                cursorLineIndex = i;
                break;
              }
            }

            // If cursor is within the top 16 lines, scroll to top instead
            if (cursorLineIndex >= 0 && cursorLineIndex < 16) {
              console.log(
                `📍 [MAIN] Cursor is on line ${cursorLineIndex + 1} (within top 16 lines), scrolling to top instead`,
              );

              // Set the selection first to ensure cursor is visible
              editor.setSelection(cursorIndex, cursorLength);

              // Force scroll to absolute top using multiple methods
              const scrollToAbsoluteTop = () => {
                const container = editor.container;
                const editorElement = document.querySelector(".ql-editor");
                const containerElement =
                  document.querySelector(".ql-container");

                // Find all possible scrollable elements in the editor hierarchy
                const scrollableElements = [];

                // Add Quill-specific elements
                if (container) scrollableElements.push(container);
                if (editorElement) scrollableElements.push(editorElement);
                if (containerElement) scrollableElements.push(containerElement);

                // Walk up the DOM tree to find all scrollable parents
                let currentElement = container;
                while (currentElement && currentElement.parentElement) {
                  currentElement = currentElement.parentElement;
                  // Check if element is scrollable (has overflow and scrollHeight > clientHeight)
                  const style = window.getComputedStyle(currentElement);
                  const isScrollable =
                    (style.overflow === "auto" ||
                      style.overflow === "scroll" ||
                      style.overflowY === "auto" ||
                      style.overflowY === "scroll") &&
                    currentElement.scrollHeight > currentElement.clientHeight;
                  if (isScrollable || currentElement.scrollTop > 0) {
                    scrollableElements.push(currentElement);
                  }
                }

                // Also check the main content area wrapper
                const mainContentArea = document.querySelector(
                  ".document-sidebar.main-content-area",
                );
                if (mainContentArea) {
                  scrollableElements.push(mainContentArea);
                }

                // Remove duplicates
                const uniqueElements = [...new Set(scrollableElements)];

                console.log(
                  `🔍 [MAIN] Found ${uniqueElements.length} scrollable elements to reset`,
                );

                // Method 1: Immediate scrollTop = 0 (no smooth) to ensure absolute top
                uniqueElements.forEach((element, index) => {
                  if (element) {
                    element.scrollTop = 0;
                    console.log(
                      `✅ [MAIN] Set element ${index} (${element.className || element.tagName}).scrollTop = 0`,
                    );
                  }
                });

                // Method 2: Try window scroll if needed
                window.scrollTo({
                  top: 0,
                  behavior: "instant",
                });

                // Method 3: Verify and force again after a brief moment
                setTimeout(() => {
                  let anyNotAtTop = false;
                  uniqueElements.forEach((element, index) => {
                    if (element && element.scrollTop > 0) {
                      console.log(
                        `⚠️ [MAIN] Element ${index} still not at top (scrollTop: ${element.scrollTop}), forcing again`,
                      );
                      element.scrollTop = 0;
                      anyNotAtTop = true;
                    }
                  });

                  // Final verification - log all scroll positions
                  const scrollPositions = uniqueElements.map((el, idx) => ({
                    index: idx,
                    className: el.className || el.tagName,
                    scrollTop: el?.scrollTop || 0,
                  }));
                  console.log(
                    `📍 [MAIN] Final scroll positions:`,
                    scrollPositions,
                  );

                  // If any element is still not at top, force one more time
                  if (anyNotAtTop) {
                    setTimeout(() => {
                      uniqueElements.forEach((element) => {
                        if (element) element.scrollTop = 0;
                      });
                    }, 50);
                  }
                }, 100);
              };

              // Execute immediately and also after a small delay
              scrollToAbsoluteTop();
              setTimeout(scrollToAbsoluteTop, 50);
              setTimeout(scrollToAbsoluteTop, 150); // Extra attempt after longer delay

              // Highlight the user temporarily
              setHighlightedUser(user);

              // Force show the name tag with comprehensive hover effect
              const forceShowNameTag = () => {
                const cursorElements = document.querySelectorAll(".ql-cursor");
                console.log(
                  `🔍 [TOP-16] Found ${cursorElements.length} cursor elements in DOM`,
                );

                cursorElements.forEach((cursorEl, index) => {
                  const cursorName = cursorEl.querySelector(".ql-cursor-name");
                  const cursorFlag = cursorEl.querySelector(".ql-cursor-flag");

                  if (cursorName && cursorName.textContent === user.name) {
                    // Apply user color to cursor tag background
                    if (user.color) {
                      cursorName.style.backgroundColor = user.color;
                      cursorName.style.background = user.color;
                      console.log(
                        `🎨 [TOP-16] Applied user color ${user.color} to cursor tag for ${user.name}`,
                      );

                      // Make the cursor border match the tag color
                      cursorEl.style.setProperty(
                        "border-left-color",
                        user.color,
                        "important",
                      );

                      // Also target any cursor caret elements within this cursor
                      const cursorCarets =
                        cursorEl.querySelectorAll(".ql-cursor-caret");
                      cursorCarets.forEach((caret) => {
                        caret.style.setProperty(
                          "border-left-color",
                          user.color,
                          "important",
                        );
                      });

                      console.log(
                        `🎯 [TOP-16] Applied tag color ${user.color} to cursor border for ${user.name}`,
                      );
                    }

                    // Force the flag to be visible
                    if (cursorFlag) {
                      cursorFlag.style.display = "block";
                      cursorFlag.style.opacity = "1";
                      cursorFlag.style.visibility = "visible";
                      cursorFlag.style.transition = "none"; // Disable transitions temporarily
                      console.log(
                        `✅ [TOP-16] Forced flag visibility for ${user.name}`,
                      );
                    }

                    // Force the name to be visible
                    if (cursorName) {
                      cursorName.style.display = "block";
                      cursorName.style.opacity = "1";
                      cursorName.style.visibility = "visible";
                      console.log(
                        `✅ [TOP-16] Forced name visibility for ${user.name}`,
                      );
                    }

                    // Add our tracking class
                    cursorEl.classList.add("avatar-highlighted");
                    console.log(
                      `✅ [TOP-16] Added avatar-highlighted class to cursor for ${user.name}`,
                    );

                    // Also try triggering various events that might work
                    const events = [
                      "mouseenter",
                      "mouseover",
                      "focus",
                      "click",
                    ];
                    events.forEach((eventType) => {
                      const event = new MouseEvent(eventType, {
                        bubbles: true,
                        cancelable: true,
                        view: window,
                      });
                      cursorEl.dispatchEvent(event);
                    });
                    console.log(
                      `✅ [TOP-16] Triggered multiple events for ${user.name}'s cursor`,
                    );
                  }
                });
              };

              // Apply immediately after a short delay to ensure cursor is rendered
              setTimeout(forceShowNameTag, 200);

              // Keep forcing the name tag every 100ms for 3 seconds
              const intervalId = setInterval(forceShowNameTag, 100);

              // Clear interval after 3 seconds
              setTimeout(() => {
                clearInterval(intervalId);
                console.log("🛑 [TOP-16] Stopped triggering hover effect");
              }, 3000);

              // Clean up after 3 seconds
              setTimeout(() => {
                setHighlightedUser(null);

                // Clean up highlighting after timeout
                const cursorElements = document.querySelectorAll(".ql-cursor");
                cursorElements.forEach((cursorEl) => {
                  // Only clean up if this cursor has the avatar-highlighted class
                  if (cursorEl.classList.contains("avatar-highlighted")) {
                    cursorEl.classList.remove("avatar-highlighted");

                    // Restore original styles
                    const cursorFlag =
                      cursorEl.querySelector(".ql-cursor-flag");
                    const cursorName =
                      cursorEl.querySelector(".ql-cursor-name");

                    if (cursorFlag) {
                      cursorFlag.style.display = "";
                      cursorFlag.style.opacity = "";
                      cursorFlag.style.visibility = "";
                      cursorFlag.style.transition = "";
                      console.log("🔄 [TOP-16] Restored flag styles");
                    }

                    if (cursorName) {
                      cursorName.style.display = "";
                      cursorName.style.opacity = "";
                      cursorName.style.visibility = "";
                      console.log("🔄 [TOP-16] Restored name styles");
                    }

                    // Trigger mouseleave to hide the name tag
                    const mouseLeaveEvent = new MouseEvent("mouseleave", {
                      bubbles: true,
                      cancelable: true,
                      view: window,
                    });
                    cursorEl.dispatchEvent(mouseLeaveEvent);
                  }
                });
                console.log(
                  "🧹 [TOP-16] Cleaned up avatar highlighting after timeout",
                );
              }, 3000);

              return; // Exit early, don't proceed with normal scrolling
            } else if (cursorLineIndex >= 0) {
              console.log(
                `📍 [MAIN] Cursor is on line ${cursorLineIndex + 1} (beyond top 16 lines), proceeding with normal scroll`,
              );
            } else {
              console.log(
                `📍 [MAIN] Could not determine cursor line, proceeding with normal scroll`,
              );
            }
          } catch (error) {
            console.warn(
              `⚠️ [MAIN] Error checking cursor line position, proceeding with normal scroll:`,
              error,
            );
          }

          // Scroll to the cursor position using multi-tier approach
          // First try the native method (most reliable for centering)
          console.log(`[LOG-11] [MAIN] Attempting native scroll method`);
          const nativeSuccess = scrollToCursorAndCenterNative(
            editor,
            cursorIndex,
            cursorLength,
          );
          if (nativeSuccess) {
            const totalTime = Date.now() - startTime;
            console.log(
              `[LOG-12] ✅ [MAIN] Successfully centered cursor position for ${user.name} with native method (${totalTime}ms)`,
            );

            // Add manual scroll adjustment: make small adjustment to push cursor lower if needed
            setTimeout(() => {
              try {
                const container = editor.container;
                if (!container) {
                  console.log(
                    `[LOG-12A] ⚠️ [MAIN] No container found for scroll adjustment`,
                  );
                  return;
                }

                // Find the cursor element in the DOM
                const cursorElements = document.querySelectorAll(".ql-cursor");
                let targetCursor = null;

                // Find the cursor for this specific user
                cursorElements.forEach((cursorEl) => {
                  const nameElement = cursorEl.querySelector(".ql-cursor-name");
                  if (nameElement && nameElement.textContent === user.name) {
                    targetCursor = cursorEl;
                  }
                });

                if (!targetCursor) {
                  console.log(
                    `[LOG-12B] ⚠️ [MAIN] Could not find cursor element for ${user.name} in DOM`,
                  );
                  return;
                }

                // Get cursor position relative to container viewport
                const containerRect = container.getBoundingClientRect();
                const cursorRect = targetCursor.getBoundingClientRect();

                // Calculate cursor position relative to container
                const cursorTopRelativeToContainer =
                  cursorRect.top - containerRect.top + container.scrollTop;
                const viewportHeight = containerRect.height;
                const currentScrollTop = container.scrollTop;

                // Check if cursor is visible in viewport
                const cursorTopInViewport = cursorRect.top - containerRect.top;
                const cursorBottomInViewport =
                  cursorRect.bottom - containerRect.top;
                const isCursorVisible =
                  cursorTopInViewport >= 0 &&
                  cursorBottomInViewport <= viewportHeight;

                // Calculate desired position: cursor should be at 75% from top of viewport (lower but not at bottom)
                const targetViewportPosition = viewportHeight * 0.75;
                const cursorCenter =
                  cursorTopRelativeToContainer + cursorRect.height / 2;
                const idealScrollTop = cursorCenter - targetViewportPosition;
                const maxScrollTop = container.scrollHeight - viewportHeight;
                const targetScrollTop = Math.max(
                  0,
                  Math.min(idealScrollTop, maxScrollTop),
                );

                console.log(`[LOG-12C] 📊 [MAIN] Cursor position analysis:`, {
                  cursorTopInViewport,
                  cursorBottomInViewport,
                  isCursorVisible,
                  currentScrollTop,
                  targetScrollTop,
                  scrollDistance: targetScrollTop - currentScrollTop,
                  viewportHeight,
                  maxScrollTop,
                });

                // Only scroll if:
                // 1. Cursor is not visible, OR
                // 2. Cursor is visible but we need to scroll down to position it lower (targetScrollTop > currentScrollTop)
                // Don't scroll if cursor is below viewport and we'd need to scroll up
                const shouldScroll =
                  !isCursorVisible ||
                  (isCursorVisible &&
                    targetScrollTop > currentScrollTop &&
                    targetScrollTop - currentScrollTop > 10);

                if (shouldScroll && targetScrollTop !== currentScrollTop) {
                  container.scrollTo({
                    top: targetScrollTop,
                    behavior: "smooth",
                  });
                  console.log(
                    `[LOG-12D] 🔄 [MAIN] Scrolled from ${currentScrollTop} to ${targetScrollTop} (${targetScrollTop - currentScrollTop}px)`,
                  );
                } else {
                  console.log(
                    `[LOG-12E] ⚠️ [MAIN] Skipping scroll adjustment:`,
                    {
                      shouldScroll,
                      isCursorVisible,
                      targetScrollTop,
                      currentScrollTop,
                      scrollDistance: targetScrollTop - currentScrollTop,
                    },
                  );
                }
              } catch (error) {
                console.log(
                  `[LOG-12F] ❌ [MAIN] Error in manual scroll adjustment:`,
                  error,
                );
              }
            }, 300); // Wait 300ms for initial scroll to complete
          } else {
            console.log(
              `[LOG-13] ⚠️ [MAIN] Native centering failed for ${user.name}, trying top-of-page method`,
            );
            // Try specialized top-of-page method
            console.log(`[LOG-14] [MAIN] Attempting top-of-page scroll method`);
            const topSuccess = scrollToCursorAndCenterTop(
              editor,
              cursorIndex,
              cursorLength,
            );
            if (topSuccess) {
              const totalTime = Date.now() - startTime;
              console.log(
                `[LOG-15] ✅ [MAIN] Successfully centered cursor position for ${user.name} with top-of-page method (${totalTime}ms)`,
              );
            } else {
              console.log(
                `[LOG-16] ⚠️ [MAIN] Top-of-page centering failed for ${user.name}, trying robust method`,
              );
              // Fallback to robust async method
              console.log(`[LOG-17] [MAIN] Attempting robust scroll method`);
              scrollToCursorAndCenterRobust(editor, cursorIndex, cursorLength)
                .then((success) => {
                  const totalTime = Date.now() - startTime;
                  if (success) {
                    console.log(
                      `[LOG-18] ✅ [MAIN] Successfully centered cursor position for ${user.name} with robust method (${totalTime}ms)`,
                    );
                  } else {
                    console.log(
                      `[LOG-19] ⚠️ [MAIN] Robust centering failed for ${user.name}, trying simple synchronous method`,
                    );
                    // Fallback to simple synchronous method
                    console.log(
                      `[LOG-20] [MAIN] Attempting simple scroll method`,
                    );
                    const simpleSuccess = scrollToCursorAndCenterSimple(
                      editor,
                      cursorIndex,
                      cursorLength,
                    );
                    if (simpleSuccess) {
                      console.log(
                        `[LOG-21] ✅ [MAIN] Successfully centered cursor position for ${user.name} with simple method (${totalTime}ms)`,
                      );
                    } else {
                      console.log(
                        `[LOG-22] ⚠️ [MAIN] All centering methods failed for ${user.name}, but cursor position was set (${totalTime}ms)`,
                      );
                    }
                  }
                })
                .catch((error) => {
                  const totalTime = Date.now() - startTime;
                  console.log(
                    `[LOG-23] ❌ [MAIN] Error in robust cursor centering for ${user.name}, trying simple method:`,
                    error,
                  );
                  // Fallback to simple synchronous method
                  console.log(
                    `[LOG-24] [MAIN] Attempting simple scroll method (error fallback)`,
                  );
                  const simpleSuccess = scrollToCursorAndCenterSimple(
                    editor,
                    cursorIndex,
                    cursorLength,
                  );
                  if (simpleSuccess) {
                    console.log(
                      `[LOG-25] ✅ [MAIN] Successfully centered cursor position for ${user.name} with simple fallback method (${totalTime}ms)`,
                    );
                  } else {
                    console.log(
                      `[LOG-26] ⚠️ [MAIN] All centering methods failed for ${user.name}, but cursor position was set (${totalTime}ms)`,
                    );
                  }
                });
            }
          }

          // Highlight the user temporarily
          setHighlightedUser(user);

          // Force show the name tag by directly manipulating the DOM
          const forceShowNameTag = () => {
            const cursorElements = document.querySelectorAll(".ql-cursor");
            console.log(
              `🔍 Found ${cursorElements.length} cursor elements in DOM`,
            );

            cursorElements.forEach((cursorEl, index) => {
              const cursorName = cursorEl.querySelector(".ql-cursor-name");
              const cursorFlag = cursorEl.querySelector(".ql-cursor-flag");

              console.log(`🔍 Cursor ${index}:`, {
                element: cursorEl,
                nameElement: cursorName,
                flagElement: cursorFlag,
                nameText: cursorName
                  ? cursorName.textContent
                  : "No name element",
                currentClasses: cursorEl.className,
                lookingFor: user.name,
                flagStyles: cursorFlag ? cursorFlag.style.cssText : "No flag",
                nameStyles: cursorName
                  ? cursorName.style.cssText
                  : "No name styles",
              });

              if (cursorName && cursorName.textContent === user.name) {
                // Log the cursor tag color
                const tagColor =
                  cursorName.style.backgroundColor ||
                  cursorName.style.background ||
                  getComputedStyle(cursorName).backgroundColor ||
                  getComputedStyle(cursorName).background;
                console.log(`🏷️ Cursor tag color for ${user.name}:`, tagColor);

                // Log the cursor border color
                const cursorColor =
                  cursorEl.style.borderLeftColor ||
                  getComputedStyle(cursorEl).borderLeftColor;
                console.log(
                  `🎯 Cursor border color for ${user.name}:`,
                  cursorColor,
                );

                // Apply user color to cursor tag background
                if (user.color) {
                  cursorName.style.backgroundColor = user.color;
                  cursorName.style.background = user.color;
                  console.log(
                    `🎨 Applied user color ${user.color} to cursor tag for ${user.name}`,
                  );

                  // Make the cursor border match the tag color
                  cursorEl.style.setProperty(
                    "border-left-color",
                    user.color,
                    "important",
                  );

                  // Also target any cursor caret elements within this cursor
                  const cursorCarets =
                    cursorEl.querySelectorAll(".ql-cursor-caret");
                  cursorCarets.forEach((caret) => {
                    caret.style.setProperty(
                      "border-left-color",
                      user.color,
                      "important",
                    );
                  });

                  console.log(
                    `🎯 Applied tag color ${user.color} to cursor border for ${user.name}`,
                  );
                }

                // Force the flag to be visible
                if (cursorFlag) {
                  cursorFlag.style.display = "block";
                  cursorFlag.style.opacity = "1";
                  cursorFlag.style.visibility = "visible";
                  cursorFlag.style.transition = "none"; // Disable transitions temporarily
                  console.log(`✅ Forced flag visibility for ${user.name}`);
                }

                // Force the name to be visible
                if (cursorName) {
                  cursorName.style.display = "block";
                  cursorName.style.opacity = "1";
                  cursorName.style.visibility = "visible";
                  console.log(`✅ Forced name visibility for ${user.name}`);
                }

                // Add our tracking class
                cursorEl.classList.add("avatar-highlighted");
                console.log(
                  `✅ Added avatar-highlighted class to cursor for ${user.name}`,
                );

                // Also try triggering various events that might work
                const events = ["mouseenter", "mouseover", "focus", "click"];
                events.forEach((eventType) => {
                  const event = new MouseEvent(eventType, {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                  });
                  cursorEl.dispatchEvent(event);
                });
                console.log(
                  `✅ Triggered multiple events for ${user.name}'s cursor`,
                );
              }
            });
          };

          // Apply immediately
          setTimeout(forceShowNameTag, 200);

          // Keep forcing the name tag every 100ms for 3 seconds
          const intervalId = setInterval(forceShowNameTag, 100);

          // Clear interval after 3 seconds
          setTimeout(() => {
            clearInterval(intervalId);
            console.log("🛑 Stopped triggering hover effect");
          }, 3000);

          setTimeout(() => {
            setHighlightedUser(null);

            // Clean up highlighting after timeout
            const cursorElements = document.querySelectorAll(".ql-cursor");
            cursorElements.forEach((cursorEl) => {
              // Only clean up if this cursor has the avatar-highlighted class
              if (cursorEl.classList.contains("avatar-highlighted")) {
                cursorEl.classList.remove("avatar-highlighted");

                // Restore original styles
                const cursorFlag = cursorEl.querySelector(".ql-cursor-flag");
                const cursorName = cursorEl.querySelector(".ql-cursor-name");

                if (cursorFlag) {
                  cursorFlag.style.display = "";
                  cursorFlag.style.opacity = "";
                  cursorFlag.style.visibility = "";
                  cursorFlag.style.transition = "";
                  console.log("🔄 Restored flag styles");
                }

                if (cursorName) {
                  cursorName.style.display = "";
                  cursorName.style.opacity = "";
                  cursorName.style.visibility = "";
                  console.log("🔄 Restored name styles");
                }

                // Trigger mouseleave to hide the name tag
                const mouseLeaveEvent = new MouseEvent("mouseleave", {
                  bubbles: true,
                  cancelable: true,
                  view: window,
                });
                cursorEl.dispatchEvent(mouseLeaveEvent);
              }
            });
            console.log("🧹 Cleaned up avatar highlighting after timeout");
          }, 3000);

          return;
        } else {
          console.log(
            `❌ [MAIN] No cursor position found for user: ${user.name}`,
          );
          // Show alert that user hasn't clicked on the document yet
          toast({
            title: "User Not Active",
            description: `${user.name} hasn't clicked on the document yet. They need to place their cursor in the document first.`,
            status: "info",
            duration: 3000,
            isClosable: true,
          });
        }
      }
    }

    if (!userFound) {
      const totalTime = Date.now() - startTime;
      console.log(
        `❌ [MAIN] User ${user.name} not found in awareness states (${totalTime}ms)`,
      );
      // Show alert that user is not connected
      toast({
        title: "User Not Connected",
        description: `${user.name} is not currently connected to this document.`,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    } else {
      const totalTime = Date.now() - startTime;
      console.log(
        `❌ [MAIN] No cursor position found for user: ${user.name} (${totalTime}ms)`,
      );
    }
  };

  let saveTimeout; // Debounce save operations

  // scanForSections is now provided by useDocumentOperations hook
  const scanForSections = scanForSectionsFromHook;

  // getCurrentUser is now provided by useDocumentOperations hook
  // const getCurrentUser = getCurrentUserFromHook; // Already aliased above

  /** Handle text changes in the editor */
  const handleTextChange = async (delta, oldDelta, source, pageIndex) => {
    if (source !== "user" || isUnmounting) return; // Don't process if unmounting

    if (
      isInGroup(userRole, 'tenantSchema') &&
      !tenantShareAllowsDocumentEdit(tenantRowAccess)
    ) {
      return;
    }

    // Add null check for editorRef.current
    if (!editorRef.current) {
      console.warn("EditorRef is null, skipping handleTextChange");
      return;
    }

    // Check if getEditor method exists
    if (!editorRef.current.getEditor) {
      console.warn(
        "EditorRef.getEditor is not available, skipping handleTextChange",
      );
      return;
    }

    // Get editor and check if it's null
    const editor = editorRef.current.getEditor(pageIndex);
    if (!editor) {
      console.warn(
        `Editor for page ${pageIndex} is null, skipping handleTextChange`,
      );
      return;
    }

    // Check if getEditorCount exists before using it
    if (!editorRef.current.getEditorCount) {
      console.warn(
        "EditorRef.getEditorCount is not available, skipping handleTextChange",
      );
      return;
    }

    try {
      const currentUser = await getCurrentUser();
      const newSections = [];

      // Safely iterate through all pages
      const editorCount = editorRef.current.getEditorCount();
      for (let i = 0; i < editorCount; i++) {
        const pageEditor = editorRef.current.getEditor(i);
        if (!pageEditor) {
          console.warn(`Editor for page ${i} is null, skipping`);
          continue;
        }

        try {
          const lines = pageEditor.getLines();
          if (!lines) continue;

          lines.forEach((line) => {
            if (!line?.domNode) return;

            const text = line.domNode.innerText?.trim();
            if (!text) return;

            try {
              const lineIndex = pageEditor.getIndex(line);
              const format = pageEditor.getFormat(lineIndex);

              if (format?.header) {
                // Always add all sections, regardless of duplicates
                const sectionId = `section-${newSections.length}`;
                newSections.push({
                  id: sectionId,
                  title: text,
                  level: format.header,
                  isEditing: false,
                  createdAt: new Date().toISOString(),
                  lastupdated: new Date().toISOString(),
                  createdBy: currentUser.name,
                  lastUpdatedBy: currentUser.name,
                });
                setActiveSection(sectionId);
              }
            } catch (error) {
              console.warn("Error processing line format:", error);
            }
          });
        } catch (error) {
          console.warn(`Error processing page ${i}:`, error);
        }
      }

      const updatedSections = sections
        .filter((section) =>
          newSections.some((newSection) => newSection.title === section.title),
        )
        .map((section) => ({
          ...section,
          lastupdated: new Date().toISOString(),
          lastUpdatedBy: currentUser.name,
        }));

      // Safely get selection position
      let position = 0;
      try {
        const selection = editor.getSelection();
        position = selection?.index || 0;
      } catch (error) {
        console.warn("Error getting editor selection:", error);
      }

      newSections.forEach((section) => {
        // Add null check for editorRef.current before calling getBoundsForSection
        if (editorRef.current && editorRef.current.getBoundsForSection) {
          try {
            const [startIndex, endIndex] =
              editorRef.current.getBoundsForSection(0, section.title);
            if (position > startIndex && position <= endIndex) {
              section.isEditing = true;
              setActiveSection(section.id);
            } else {
              section.isEditing = false;
            }
          } catch (error) {
            console.warn("Error getting bounds for section:", error);
          }
        }
      });

      setSections([
        ...updatedSections,
        ...newSections.filter(
          (newSection) =>
            !updatedSections.some(
              (section) => section.title === newSection.title,
            ),
        ),
      ]);

      if (saveTimeout) clearTimeout(saveTimeout);

      saveTimeout = setTimeout(() => {
        if (!isUnmounting && editorRef.current) {
          handleSaveDoc();
          updateLastUpdated(tableName, name);
        }
      }, 2000);
    } catch (error) {
      console.error("Error in handleTextChange:", error);
      // Don't throw, just log the error to prevent breaking the app
    }
  };

  // updateGlobalPresenceFromAwareness is now defined above before the hooks

  // handleSaveDoc is now provided by useDocumentOperations hook
  // Wrap handleSaveDoc to add activity tracking
  const handleSaveDoc = useCallback(
    async (...args) => {
      if (safeShareMode) {
        console.log("Safe-Share active: saving disabled.");
        return null;
      }
      if (
        isInGroup(userRole, 'tenantSchema') &&
        !tenantShareAllowsDocumentEdit(tenantRowAccess)
      ) {
        toast({
          title: "Read-only",
          description:
            "You have viewer access to this row and cannot edit the document.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return null;
      }
      const result = await handleSaveDocFromHook(...args);
      const docId = validId || uuid || id || "unknown";
      trackUserEvent('document_edit', {
        metadata: { document_id: docId },
      });
      return result;
    },
    [
      handleSaveDocFromHook,
      extractedName,
      patientName,
      name,
      validId,
      uuid,
      id,
      passedFilePath,
      userRole,
      tenantRowAccess,
      toast,
      safeShareMode,
    ],
  );

  const toggleSafeShareMode = async () => {
    if (!editorRef.current) return;
    const editor = editorRef.current.getEditor(0);
    if (!editor) return;

    if (safeShareMode) {
      // Toggling OFF: Restore original content
      if (originalContents) {
        editor.setContents(originalContents);
      }
      setOriginalContents(null);
      setSafeShareMode(false);
      
      toast({
        title: "Safe-Share Disabled",
        description: "Original document content restored.",
        status: "info",
        duration: 2000,
        isClosable: true,
      });
    } else {
      // Toggling ON:
      // Save changes first if any
      if (typeof handleSaveDoc === "function") {
        await handleSaveDoc();
      }

      setIsDeidentifying(true);
      
      // Set safeShareMode to true first to disconnect Yjs collaboration
      setSafeShareMode(true);

      // Defer the rest to allow React to re-render and clean up the WebSocket connection
      setTimeout(async () => {
        try {
          const currentContents = editor.getContents();
          const plainText = editor.getText();

          // Call the Django de-identification service
          const backendUrl = getBackendURL();
          const response = await fetch(`${backendUrl}/api/deidentify/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              text: plainText,
              method: safeShareMethod,
            }),
          });

          if (!response.ok) {
            throw new Error(`Failed to de-identify text: ${response.statusText}`);
          }

          const data = await response.json();
          const deidentifiedText = data.deidentified_text;

          // Store original contents
          setOriginalContents(currentContents);
          
          // Update editor text
          editor.setText(deidentifiedText);

          toast({
            title: "Safe-Share Enabled",
            description: `Document de-identified using '${safeShareMethod}' method. Auto-save disabled.`,
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        } catch (error) {
          console.error("Error enabling Safe-Share mode:", error);
          // Revert safeShareMode to false on failure
          setSafeShareMode(false);
          toast({
            title: "Safe-Share Error",
            description: `Failed to de-identify document: ${error.message}`,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        } finally {
          setIsDeidentifying(false);
        }
      }, 300);
    }
  };

  const getTemplateTypeForCurrentDocument = useCallback(() => {
    let templateType = correctedType;
    if (templateType === "FFP") templateType = "NP";
    else if (templateType === "FFS") templateType = "NS";
    else if (templateType === "SOC") templateType = "SO";
    else if (templateType === "STU") templateType = "ST";

    if (documentTemplates[templateType]) return templateType;
    return roleDefaultTemplateType || templateType;
  }, [correctedType, roleDefaultTemplateType]);

  const applyTemplateToEditor = useCallback(
    async ({ saveAfterApply = true } = {}) => {
      const editor = editorRef.current?.getEditor(0);
      if (!editor) {
        throw new Error("Editor not available for template application");
      }

      const patientType = getTemplateTypeForCurrentDocument();
      const template = documentTemplates[patientType];

      if (!template) {
        editor.setContents({ ops: [{ insert: "\n" }] }, "api");
        setLoadedTemplateType("Blank");
        setDocumentLoaded(true);
        setIsLoadingDocument(false);
        quillRefs.current.forEach((quill) => {
          if (quill) quill.enable(true);
        });
        setContentLoadingComplete(true);
        throw new Error(`No template available for type: ${patientType}`);
      }

      let calculatedAge = "___";
      try {
        if (patientData?.dob) {
          calculatedAge = calculateAge(patientData.dob, effectiveDate)?.toString() || "___";
        } else {
          const supabase = await getSupabase();
          const { data: patientRecord } = await supabase
            .from(tableName)
            .select("dob")
            .eq(prompts[0][0], extractedName)
            .order("created_at", { ascending: false })
            .limit(1)
            .single();

          if (patientRecord?.dob) {
            calculatedAge = calculateAge(patientRecord.dob, effectiveDate)?.toString() || "___";
          }
        }
      } catch (error) {
        console.warn("Error calculating age for template:", error);
      }

      const sourceDelta = template.delta || template.content || { ops: [] };
      const deltaContent = JSON.parse(JSON.stringify(sourceDelta));

      deltaContent.ops?.forEach((op) => {
        if (op.insert && typeof op.insert === "string") {
          const patientTime =
            patientScheduledTime ||
            navbarTime ||
            new Date().toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            });
          const ehrIdForHeading = id || "";
          op.insert = op.insert
            .replace(/\[NAME\]/g, extractedName || "")
            .replace(/\[ID\]/g, ehrIdForHeading)
            .replace(/\[Patient Name\]/g, extractedName || "")
            .replace(/\[MRN\]/g, ehrIdForHeading)
            .replace(/\[time\]/g, patientTime)
            .replace(/\[age\]/g, calculatedAge || "___")
            .replace(/\[date\]/g, effectiveDate)
            .replace(
              /\[MM\/DD\/YYYY\]/g,
              (() => {
                const [year, month, day] = effectiveDate.split("-");
                return `${String(parseInt(month)).padStart(2, "0")}/${String(parseInt(day)).padStart(2, "0")}/${year}`;
              })(),
            );
        }
      });

      editor.setContents(deltaContent, "api");
      setLoadedTemplateType(patientType);
      setDocumentLoaded(true);
      setIsLoadingDocument(false);
      quillRefs.current.forEach((quill) => {
        if (quill) quill.enable(true);
      });
      setContentLoadingComplete(true);

      setTimeout(() => {
        scanForSections();
        if (saveAfterApply) {
          handleSaveDoc();
        }
      }, 500);

      return patientType;
    },
    [
      editorRef,
      getTemplateTypeForCurrentDocument,
      patientData,
      effectiveDate,
      extractedName,
      patientScheduledTime,
      navbarTime,
      id,
      quillRefs,
      scanForSections,
      handleSaveDoc,
    ],
  );

  const handleDeleteDocumentAndLoadTemplate = useCallback(async () => {
    if (tenantDocReadOnly) return;
    if (!filePath) {
      toast({
        title: "No document path",
        description: "Cannot delete/reload because this document has no storage path yet.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const confirmed = window.confirm(
      "Delete the saved document and reload from the default template? This replaces the current document content.",
    );
    if (!confirmed) return;

    setIsLoadingDocument(true);
    try {
      const supabase = await getSupabase();
      await supabase.storage.from(bucketName_files).remove([filePath]);
      await supabase.from("documents").delete().eq("path", filePath);

      const patientType = await applyTemplateToEditor({ saveAfterApply: true });
      toast({
        title: "Template loaded",
        description: `Deleted saved document and loaded ${patientType} template.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting document and loading template:", error);
      setIsLoadingDocument(false);
      toast({
        title: "Could not reload template",
        description: error?.message || "An error occurred.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [
    tenantDocReadOnly,
    filePath,
    toast,
    applyTemplateToEditor,
  ]);

  // handleRemoveDuplicates is now provided by useDocumentOperations hook
  const handleRemoveDuplicates = handleRemoveDuplicatesFromHook;

  // Helper function to convert Quill delta to plain text
  const convertDeltaToPlainText = (delta) => {
    if (!delta || !delta.ops) return "";
    return delta.ops
      .map((op) => {
        if (typeof op.insert === "string") {
          return op.insert;
        }
        return "";
      })
      .join("")
      .replace(/\n/g, " ")
      .trim();
  };

  // Canonical plain text for doc-as-template: MUST be editor.getText() so indices match
  // deleteText/insertText. Joining delta.ops can diverge (embeds, blots) and breaks offsets.
  const getDocumentContentWithNewlines = () => {
    const editor = editorRef.current?.getEditor(0);
    if (!editor) return "";
    const fromGetText = editor.getText().replace(/\r\n/g, "\n");
    try {
      const delta = editor.getContents();
      const fromOps = (delta?.ops || [])
        .map((op) => (typeof op.insert === "string" ? op.insert : ""))
        .join("")
        .replace(/\r\n/g, "\n");
      if (fromGetText !== fromOps) {
        console.warn(
          "📝 [DocAsTemplate] getText() !== delta op join — diff/apply use getText() only",
          { getTextLen: fromGetText.length, opsJoinLen: fromOps.length },
        );
      }
    } catch (e) {
      console.warn("📝 [DocAsTemplate] delta compare failed:", e);
    }
    return fromGetText;
  };

  /** Map an index in CRLF-normalized text to Quill raw getText() index */
  const canonToRawIndex = (rawText, canonIndex) => {
    if (!rawText.includes("\r")) return Math.min(canonIndex, rawText.length);
    let c = 0;
    let r = 0;
    while (r < rawText.length && c < canonIndex) {
      if (rawText[r] === "\r" && rawText[r + 1] === "\n") {
        r += 2;
        c += 1;
      } else {
        r += 1;
        c += 1;
      }
    }
    return r;
  };

  // Log editor formatting status (for de-formatting diagnostics)
  const logEditorFormatStatus = (label) => {
    const editor = editorRef.current?.getEditor(0);
    if (!editor) {
      console.log(`📝 [DocAsTemplate] ${label}: no editor`);
      return;
    }
    const delta = editor.getContents();
    const ops = delta.ops || [];
    let headerOps = 0;
    let colorOps = 0;
    let boldOps = 0;
    let underlineOps = 0;
    let linkOps = 0;
    for (const op of ops) {
      const a = op.attributes;
      if (!a) continue;
      if (a.header) headerOps += 1;
      if (a.color) colorOps += 1;
      if (a.bold) boldOps += 1;
      if (a.underline) underlineOps += 1;
      if (a.link) linkOps += 1;
    }
    const textLen = editor.getText()?.length ?? 0;
    console.log(
      `📝 [DocAsTemplate] ${label}: ops=${ops.length} textLen=${textLen} header=${headerOps} color=${colorOps} bold=${boldOps} underline=${underlineOps} link=${linkOps}`,
    );
  };

  /**
   * Find needle in haystack for surgical replace. Compares in a \\n-normalized space
   * but returns raw indices/length into haystack (Quill getText()) so deleteText matches.
   */
  const findDocTemplateChunk = (haystack, needle) => {
    if (needle == null || needle === "") return { idx: -1, length: 0 };
    let idx = haystack.indexOf(needle);
    if (idx !== -1) return { idx, length: needle.length };
    const hNorm = haystack.replace(/\r\n/g, "\n");
    const nNorm = needle.replace(/\r\n/g, "\n");
    const hi = hNorm.indexOf(nNorm);
    if (hi === -1) return { idx: -1, length: 0 };
    if (hNorm === haystack) return { idx: hi, length: nNorm.length };
    const mapNormToRaw = (normStart, normEnd) => {
      let i = 0;
      let j = 0;
      while (j < normStart && i < haystack.length) {
        if (haystack[i] === "\r" && haystack[i + 1] === "\n") {
          i += 2;
          j += 1;
        } else {
          i += 1;
          j += 1;
        }
      }
      const start = i;
      while (j < normEnd && i < haystack.length) {
        if (haystack[i] === "\r" && haystack[i + 1] === "\n") {
          i += 2;
          j += 1;
        } else {
          i += 1;
          j += 1;
        }
      }
      return { idx: start, length: i - start };
    };
    return mapNormToRaw(hi, hi + nNorm.length);
  };

  // Function to get current document content as plain text
  const getDocumentContent = () => {
    const editor = editorRef.current?.getEditor(0);
    if (!editor) return "";
    const delta = editor.getContents();
    return convertDeltaToPlainText(delta);
  };

  // Handle Process form button click
  const handleGenerateFromForms = async (qualifyingFormLinks = null) => {
    console.log(
      "🔍 EditableDocumentPage: handleGenerateFromForms called",
      qualifyingFormLinks
        ? `with ${qualifyingFormLinks.length} qualifying forms`
        : "without qualifying forms filter",
    );

    if (!patientData && !userInfo) {
      toast({
        title: "Error",
        description: "Patient data not available",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const patient = patientData || userInfo;

    try {
      const patientId = actualPatientId || patient?.id || uuid || id;
      let formLinks;
      if (qualifyingFormLinks && qualifyingFormLinks.length > 0) {
        formLinks = qualifyingFormLinks;
      } else {
        formLinks = await extractAllSubmissionLinks(patientId);
      }

      if (!formLinks || formLinks.length === 0) {
        toast({
          title: "No Forms Found",
          description: "No form submissions found for this patient",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Use correctedType if available to override patient type
      const patientForProcessing = correctedType
        ? { ...patient, type: correctedType }
        : patient;

      const processingData =
        await prepareFormProcessingData(patientForProcessing);
      setCurrentPatientType(processingData.patientType);
      setPromptEditorInitialData(processingData);
      setIsPromptEditorOpen(true);
    } catch (error) {
      console.error(
        "❌ EditableDocumentPage: Error preparing form prompt:",
        error,
      );
      toast({
        title: "Error",
        description: `Failed to prepare form prompt: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Open FormsModal - data fetching is handled internally by the component
  const handleViewForms = () => {
    const patientId = actualPatientId || validId || id || uuid;
    console.log("🔍 Opening forms modal for patient:", patientId);
    setIsFormsModalOpen(true);
  };

  // Helper function to construct date-specific reports path
  const getDateSpecificReportsPath = (reportsPath, dateStr) => {
    if (!reportsPath || !dateStr) return reportsPath;

    // Convert dateStr from YYYY-MM-DD to MM-DD-YY format for path
    const [year, month, day] = dateStr.split("-");
    const dateForPath = `${month}-${day}-${year.slice(2)}`; // MM-DD-YY

    // Extract base path (everything up to the date folder) and replace date
    const pathParts = reportsPath.split("/");

    // Check if path has a date-like folder (MM-DD-YY format)
    if (pathParts.length >= 1) {
      const lastPart = pathParts[pathParts.length - 1];
      // Check if last part looks like a date (MM-DD-YY)
      if (/^\d{2}-\d{2}-\d{2}$/.test(lastPart)) {
        // Replace the date part with current document's date
        pathParts[pathParts.length - 1] = dateForPath;
        return pathParts.join("/");
      } else if (pathParts.length >= 2) {
        // Check second to last part
        const secondLastPart = pathParts[pathParts.length - 2];
        if (/^\d{2}-\d{2}-\d{2}$/.test(secondLastPart)) {
          pathParts[pathParts.length - 2] = dateForPath;
          return pathParts.join("/");
        } else {
          // No date found in path, append the date folder
          return `${reportsPath}/${dateForPath}`;
        }
      }
    }

    return reportsPath;
  };

  // Helper function to check if files exist in a folder (simplified version of handleViewReports)
  const checkFilesExist = async (folderPath, patientId = null) => {
    if (!folderPath) return false;

    const supabase = await getSupabase();
    const effectivePatientId =
      patientId || actualPatientId || validId || id || uuid;
    const BUCKET_NAME_LOCAL =
      bucketName_files &&
      bucketName_files !== "undefined" &&
      bucketName_files !== undefined
        ? bucketName_files
        : "files";

    try {
      // Parse the folder path to extract patient name
      const pathParts = folderPath.split("/");
      let basePath = "";
      let patientFolder = "";
      let dateFolder = "";

      if (pathParts.length >= 3) {
        basePath = pathParts.slice(0, -2).join("/"); // "New/Reports"
        patientFolder = pathParts[pathParts.length - 2];
        dateFolder = pathParts[pathParts.length - 1];
      }

      // Extract patient name (everything before the first underscore)
      const patientNameMatch = patientFolder
        ? patientFolder.match(/^(.+?)_/)
        : null;
      const patientName = patientNameMatch
        ? patientNameMatch[1]
        : patientFolder;

      const fileMap = new Map();

      // 1. Check the original folder path first
      const { data: dataOriginal, error: errorOriginal } =
        await supabase.storage.from(BUCKET_NAME_LOCAL).list(folderPath);

      if (!errorOriginal && dataOriginal) {
        const filesOnly = dataOriginal.filter((item) =>
          item.name.includes("."),
        );
        filesOnly.forEach((file) => {
          if (!fileMap.has(file.name)) {
            fileMap.set(file.name, file);
          }
        });
        if (fileMap.size > 0) return true; // Found files, return early
      }

      // 2. If no files found, check matching folders in base path
      if (basePath && patientName) {
        const { data: allFolders, error: listError } = await supabase.storage
          .from(BUCKET_NAME_LOCAL)
          .list(basePath);

        if (!listError && allFolders) {
          const patientNameLower = patientName.toLowerCase().trim();
          const normalizedPatientName = patientNameLower
            .replace(/\s+/g, " ")
            .replace(/\s*,\s*/g, ", ");

          const matchingFolders = allFolders.filter((folder) => {
            if (folder.name.includes(".")) return false;
            const folderNameLower = folder.name.toLowerCase();
            const folderBaseName = folderNameLower.includes("_")
              ? folderNameLower.split("_")[0].trim()
              : "";
            const normalizedFolderBaseName = folderBaseName
              .replace(/\s+/g, " ")
              .replace(/\s*,\s*/g, ", ");
            return (
              normalizedFolderBaseName === normalizedPatientName ||
              folderNameLower.startsWith(`${normalizedPatientName}_`) ||
              folderNameLower.startsWith(`${normalizedPatientName} `)
            );
          });

          // Check each matching folder for files in the current date folder
          for (const folder of matchingFolders) {
            const patientFolderPath = `${basePath}/${folder.name}`;
            const { data: dateFolders, error: dateFoldersError } =
              await supabase.storage
                .from(BUCKET_NAME_LOCAL)
                .list(patientFolderPath);

            if (!dateFoldersError && dateFolders) {
              const currentDateFolders = dateFolders.filter((item) => {
                if (item.name.includes(".")) return false;
                return item.name === dateFolder;
              });

              for (const dateFolderItem of currentDateFolders) {
                const fullDateFolderPath = `${patientFolderPath}/${dateFolderItem.name}`;
                const { data: dateFolderFiles, error: dateFolderError } =
                  await supabase.storage
                    .from(BUCKET_NAME_LOCAL)
                    .list(fullDateFolderPath);

                if (!dateFolderError && dateFolderFiles) {
                  const filesOnly = dateFolderFiles.filter((f) =>
                    f.name.includes("."),
                  );
                  if (filesOnly.length > 0) return true; // Found files
                }
              }
            }
          }
        }
      }

      return fileMap.size > 0;
    } catch (error) {
      console.error("❌ Error checking files existence:", error);
      return false;
    }
  };

  // Handle View Reports (same as PatientPage - full implementation)
  const handleViewReports = async (folderPath, patientId = null) => {
    const supabase = await getSupabase();
    const effectivePatientId =
      patientId || actualPatientId || validId || id || uuid;
    const BUCKET_NAME =
      bucketName_files &&
      bucketName_files !== "undefined" &&
      bucketName_files !== undefined
        ? bucketName_files
        : "files";

    console.log("🔍 handleViewReports - BUCKET_NAME:", BUCKET_NAME);
    console.log("🔍 handleViewReports - folderPath:", folderPath);
    console.log("🔍 handleViewReports - patientId:", effectivePatientId);

    setCurrentFolderPath(folderPath);
    if (!folderPath) {
      setCurrentFolderFiles([]);
      setModalIsOpen(true);
      return;
    }

    try {
      // Parse the folder path to extract patient name
      const pathParts = folderPath.split("/");
      let basePath = "";
      let patientFolder = "";
      let dateFolder = "";

      if (pathParts.length >= 3) {
        basePath = pathParts.slice(0, -2).join("/"); // "New/Reports"
        patientFolder = pathParts[pathParts.length - 2];
        dateFolder = pathParts[pathParts.length - 1];
      }

      // Extract patient name (everything before the first underscore)
      const patientNameMatch = patientFolder
        ? patientFolder.match(/^(.+?)_/)
        : null;
      const patientName = patientNameMatch
        ? patientNameMatch[1]
        : patientFolder;

      console.log(
        `🔍 Extracted patient name: "${patientName}" from folder: "${patientFolder}"`,
      );
      console.log(`🔍 Base path: "${basePath}", Date folder: "${dateFolder}"`);

      const fileMap = new Map(); // Use Map to deduplicate by name, storing file with folder path

      // 1. Fetch from the original folder path first
      console.log(
        `🔍 Step 1: Attempting to list files from original path: ${folderPath}`,
      );
      const { data: dataOriginal, error: errorOriginal } =
        await supabase.storage.from(BUCKET_NAME).list(folderPath);

      if (!errorOriginal && dataOriginal) {
        const filesOnly = dataOriginal.filter((item) =>
          item.name.includes("."),
        );
        filesOnly.forEach((file) => {
          if (!fileMap.has(file.name)) {
            fileMap.set(file.name, { ...file, actualFolderPath: folderPath });
          }
        });
        console.log(
          `✅ Fetched ${filesOnly.length} files from original folder: ${folderPath}`,
        );
      } else if (errorOriginal) {
        console.warn(
          `⚠️ Error fetching from original folder: ${errorOriginal.message}`,
        );
      }

      // 1b. Also try to directly access the email-based folder path if it exists
      if (effectivePatientId && patientName && dateFolder) {
        try {
          const { data: patientData, error: patientError } = await supabase
            .from(tableName)
            .select("email")
            .eq("id", effectivePatientId)
            .single();

          if (!patientError && patientData && patientData.email) {
            const patientEmail = patientData.email;
            console.log(`🔍 Step 1b: Found patient email: ${patientEmail}`);

            const emailFolderVariations = [
              `${patientName}_ ${patientEmail}`,
              `${patientName}_${patientEmail}`,
              `${patientName} ${patientEmail}`,
            ];

            for (const emailFolderName of emailFolderVariations) {
              const emailFolderPath = `${basePath}/${emailFolderName}/${dateFolder}`;
              console.log(
                `🔍 Step 1b: Trying email folder path: ${emailFolderPath}`,
              );

              const { data: emailFolderData, error: emailFolderError } =
                await supabase.storage.from(BUCKET_NAME).list(emailFolderPath);

              if (!emailFolderError && emailFolderData) {
                const emailFilesOnly = emailFolderData.filter((item) =>
                  item.name.includes("."),
                );
                if (emailFilesOnly.length > 0) {
                  emailFilesOnly.forEach((file) => {
                    fileMap.set(file.name, {
                      ...file,
                      actualFolderPath: emailFolderPath,
                    });
                  });
                  console.log(
                    `✅ Fetched ${emailFilesOnly.length} files from email folder: ${emailFolderPath}`,
                  );
                  break;
                }
              }
            }
          }
        } catch (error) {
          console.warn(`⚠️ Error fetching patient email:`, error);
        }
      }

      // 2. List all folders in basePath and find all folders starting with patient name
      if (basePath && patientName) {
        console.log(`🔍 Step 2: Listing all folders in base path: ${basePath}`);
        const { data: allFolders, error: listError } = await supabase.storage
          .from(BUCKET_NAME)
          .list(basePath);

        if (!listError && allFolders) {
          const patientNameLower = patientName.toLowerCase().trim();
          const normalizedPatientName = patientNameLower
            .replace(/\s+/g, " ")
            .replace(/\s*,\s*/g, ", ");

          const matchingFolders = allFolders.filter((folder) => {
            if (folder.name.includes(".")) return false;
            const folderNameLower = folder.name.toLowerCase();
            const exactMatch =
              folderNameLower.startsWith(`${normalizedPatientName}_`) ||
              folderNameLower.startsWith(`${normalizedPatientName} `);
            return exactMatch;
          });

          // For each matching folder, check date subfolders
          for (const folder of matchingFolders) {
            const patientFolderPath = `${basePath}/${folder.name}`;
            const { data: dateFolders, error: dateFoldersError } =
              await supabase.storage.from(BUCKET_NAME).list(patientFolderPath);

            if (!dateFoldersError && dateFolders) {
              const currentDateFolders = dateFolders.filter((item) => {
                if (item.name.includes(".")) return false;
                return item.name === dateFolder;
              });

              for (const dateFolderItem of currentDateFolders) {
                const fullDateFolderPath = `${patientFolderPath}/${dateFolderItem.name}`;
                const { data: dateFolderFiles, error: dateFolderError } =
                  await supabase.storage
                    .from(BUCKET_NAME)
                    .list(fullDateFolderPath);

                if (!dateFolderError && dateFolderFiles) {
                  const filesOnly = dateFolderFiles.filter((f) =>
                    f.name.includes("."),
                  );
                  filesOnly.forEach((file) => {
                    fileMap.set(file.name, {
                      ...file,
                      actualFolderPath: fullDateFolderPath,
                    });
                  });
                }
              }
            }
          }
        }
      }

      // Convert Map to Array and sort by name
      const combinedFiles = Array.from(fileMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      console.log(`✅ Total unique files found: ${combinedFiles.length}`);

      setCurrentFolderFiles(combinedFiles);
      setModalIsOpen(true);
    } catch (error) {
      console.error("Error fetching files from Supabase:", error);
      setCurrentFolderFiles([]);
      setModalIsOpen(true);
    }
  };

  // Handle View Folder Files (same as PatientPage)
  const handleViewFolderFiles = async (folderPath) => {
    const supabase = await getSupabase();
    const BUCKET_NAME =
      bucketName_files &&
      bucketName_files !== "undefined" &&
      bucketName_files !== undefined
        ? bucketName_files
        : "files";
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .list(folderPath);

    if (error) {
      console.error("Error fetching files from Supabase:", error);
    } else {
      setCurrentFolderFiles(data);
      setModalIsOpen(true);
    }
  };

  // Handle Go Up One Level (same as PatientPage)
  const handleGoUpOneLevel = async () => {
    const parentFolderPath = currentFolderPath
      .split("/")
      .slice(0, -1)
      .join("/");

    setPreviousFolderPath(currentFolderPath);
    setCurrentFolderPath(parentFolderPath);
    const supabase = await getSupabase();
    const BUCKET_NAME =
      bucketName_files &&
      bucketName_files !== "undefined" &&
      bucketName_files !== undefined
        ? bucketName_files
        : "files";
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .list(parentFolderPath);

    if (error) {
      console.error("Error fetching folders from Supabase:", error);
    } else {
      setCurrentFolderFiles(data);
    }
  };

  // Handle Go Down One Level (same as PatientPage)
  const handleGoDownOneLevel = async () => {
    handleViewFolderFiles(previousFolderPath);
  };

  // Callback from ProcessFormPromptEditor when user clicks Run
  const handleFormProcessingRun = async ({ fullPrompt, useStoredFullText }) => {
    console.log(
      "🔍 EditableDocumentPage: Running Process form with prompt, length:",
      fullPrompt.length,
    );

    const documentContent = getDocumentContent();

    const initialMessages = [
      {
        id: Date.now().toString(),
        type: "user",
        content: fullPrompt,
        timestamp: new Date(),
      },
    ];

    if (onOpenAIAssistant) {
      onOpenAIAssistant(
        validId || id || uuid,
        patientData || userInfo ? [patientData || userInfo] : null,
        documentContent || "",
        initialMessages,
        ["forms"],
        null,
        null,
        useStoredFullText,
      );
    } else {
      toast({
        title: "Error",
        description: "AI Assistant is not available",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Convert markdown text to Quill delta (handles **bold** inline)
  const markdownToQuillDelta = (text) => {
    const ops = [];
    const lines = text.split("\n");
    const boldRegex = /\*\*([^*]+)\*\*/g;

    for (const line of lines) {
      let lastIndex = 0;
      let match;
      boldRegex.lastIndex = 0;

      while ((match = boldRegex.exec(line)) !== null) {
        if (match.index > lastIndex) {
          ops.push({ insert: line.slice(lastIndex, match.index) });
        }
        ops.push({ insert: match[1], attributes: { bold: true } });
        lastIndex = boldRegex.lastIndex;
      }
      if (lastIndex < line.length) {
        ops.push({ insert: line.slice(lastIndex) });
      }
      ops.push({ insert: "\n" });
    }

    return { ops };
  };

  // Handle "Use Doc as Template" — AI edits the doc in-place using form data
  const handleDocAsTemplateRun = async ({
    editablePrompt,
    useStoredFullText,
    forceProcessForms = false,
    patientRow = null,
  }) => {
    const editor = editorRef.current?.getEditor(0);
    // patientRow is passed directly from PatientPage and has the correct submissions_array.
    // Fall back to patientData (EDP's own fetch) then userInfo.
    const patientForRun = patientRow || patientData || userInfo;
    const patientId = actualPatientId || uuid || id;

    // 0. If forceProcessForms, re-extract form text for submissions missing full_text
    if (forceProcessForms && patientForRun) {
      try {
        const supabase = await getSupabase();
        const submissions_array = patientForRun?.submissions_array || [];
        const backendUrl = getBackendURL();
        const formsToProcess = [];

        for (const submission of submissions_array) {
          let form_id = null;
          let submission_id = null;
          if (typeof submission === "object" && submission !== null) {
            form_id = submission.form_id;
            submission_id = submission.submission_id;
            if (!form_id || !submission_id) {
              const url = submission.url || submission;
              if (typeof url === "string") {
                const match = url.match(/\/([^/]+)\/submission\/([^/]+)/);
                if (match) {
                  form_id = match[1];
                  submission_id = match[2];
                }
              }
            }
          } else if (typeof submission === "string") {
            const match = submission.match(/\/([^/]+)\/submission\/([^/]+)/);
            if (match) {
              form_id = match[1];
              submission_id = match[2];
            }
          }
          if (form_id && submission_id) {
            const { data: fd, error: fe } = await supabase
              .from("StoringFormSubmission")
              .select("full_text")
              .eq("form_id", form_id)
              .eq("submission_id", submission_id)
              .maybeSingle();
            if (!fe && fd && !fd.full_text)
              formsToProcess.push({ form_id, submission_id });
          }
        }

        if (formsToProcess.length > 0) {
          toast({
            title: "Extracting Form Text",
            description: `Processing ${formsToProcess.length} form(s)...`,
            status: "info",
            duration: 3000,
            isClosable: true,
          });
          await Promise.all(
            formsToProcess.map(({ form_id, submission_id }) =>
              fetch(`${backendUrl}/api/ai-assistant/process-form-submission/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ form_id, submission_id }),
              }),
            ),
          );
        }
      } catch (e) {
        console.warn("Could not re-extract form text:", e);
      }
    }

    // 1. Ensure versioning exists (race: auto doc-as-template effect can run before VS init effect)
    const ensureVersioningForSnapshot = async () => {
      if (versioningInstanceRef.current) {
        const vs = versioningInstanceRef.current;
        await vs.setCurrentUser();
        return vs;
      }
      if (!filePath || !documentLoaded) {
        console.warn("📝 [DocAsTemplate] Cannot init versioning helper:", {
          hasFilePath: !!filePath,
          documentLoaded,
        });
        return null;
      }
      console.info(
        "📝 [DocAsTemplate] Step 1a: Eager-init versioning system for manual snapshots",
      );
      const vs = createVersioningSystem(filePath, editorRef);
      versioningInstanceRef.current = vs;
      setVersioningSystem(vs);
      setTimeout(async () => {
        try {
          vs.setupEventListeners();
          await vs.setCurrentUser();
        } catch (e) {
          console.warn(
            "📝 [DocAsTemplate] Versioning setup deferred step failed:",
            e,
          );
        }
      }, 1000);
      await vs.setCurrentUser();
      return vs;
    };

    await ensureVersioningForSnapshot();

    // 2. Lock the document locally and broadcast to collaborators via awareness.
    // Do NOT call editor.enable(false) — Quill's disable re-renders the editor and
    // strips visual formatting (bold headers, etc.).  The isDocProcessingLocked overlay
    // handles click-blocking via pointerEvents instead.
    setIsDocProcessingLocked(true);
    logEditorFormatStatus("AFTER setIsDocProcessingLocked(true)");
    if (collaborationProvider?.awareness) {
      collaborationProvider.awareness.setLocalStateField(
        "ai_doc_processing_locked",
        true,
      );
    }
    logEditorFormatStatus("AFTER awareness.setLocalStateField");

    // 3. Capture original text and open the diff viewer in loading state.
    const currentDocText = getDocumentContentWithNewlines();
    docTemplateCanonSnapshotRef.current = currentDocText;
    console.info(
      "📝 [DocAsTemplate] Step 3: Captured canonical doc text for diff",
      {
        length: currentDocText.length,
        firstLinePreview: currentDocText.split("\n")[0]?.slice(0, 80),
      },
    );
    setDiffOriginalText(currentDocText);
    setDiffNewText("");
    setIsGeneratingDiff(true);
    setIsDiffViewerOpen(true);
    logEditorFormatStatus("AFTER setIsDiffViewerOpen(true)");

    // 4. Build the prompt — minimal targeted insertions only, rest copied verbatim.
    // editablePrompt is written for "generate from template" mode (bold headers, A&P, etc.)
    // and must NOT be included here — it causes Claude to reformat every line, making the
    // entire document appear changed in the diff viewer.
    const docAsTemplatePrompt =
      `You have access to the patient's form submissions via the "forms" source.\n\n` +
      `TASK: Insert form data into the existing document at appropriate blank or placeholder locations.\n\n` +
      `STRICT RULES — follow every one exactly:\n` +
      `1. Copy every line of the document EXACTLY as-is — same words, same punctuation, same spacing, same markdown.\n` +
      `2. The ONLY lines you may change are those that are blank, contain "N/A", contain placeholder text like "[...]" or "___", or are clearly empty fields waiting for a value.\n` +
      `3. Do NOT rephrase, reorder, or rewrite any line that already has content.\n` +
      `4. Do NOT add bold, italics, or any other markdown that is not already present on that line.\n` +
      `5. Do NOT add new sections, headers, or an assessment/plan unless the document already has a placeholder for one.\n` +
      `6. Match form answers to the correct field in the document based on field name and context.\n` +
      `7. Preserve the exact line structure — every newline in the original must appear in the same position in your output. Do NOT merge lines. Do NOT move a line's content onto the previous or next line.\n` +
      `8. Never remove or relocate section labels such as "HPI:" — keep them on their own line as in the original unless that line is entirely replaced.\n` +
      `9. Do NOT put narrative text on the same line as the document title/header line (the first line with patient name/time).\n` +
      `10. After the last line of the document, you MAY append new lines with an "Additional Information:" section ONLY for form data that had no matching field in the body. Put each bullet on its own line; do not glue this block to the previous paragraph.\n` +
      `11. Do NOT repeat in "Additional Information" any sentence, bullet, or fact that already appears elsewhere in the document (including text you inserted into blanks or placeholders). That section must contain only leftover form facts with no home in the body.\n` +
      `12. Return ONLY the complete document text with your targeted insertions — no commentary, no code blocks.`;

    try {
      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userJwtToken = session?.access_token || null;
      const userId = session?.user?.id || null;
      const username =
        session?.user?.user_metadata?.username || session?.user?.email || "";

      const response = await fetch(
        `${getBackendURL()}/api/ai-assistant/query/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(userJwtToken ? { Authorization: `Bearer ${userJwtToken}` } : {}),
          },
          body: JSON.stringify({
            query: docAsTemplatePrompt,
            patientId,
            patientData: patientForRun ? [patientForRun] : null,
            documentContent: currentDocText,
            sessionId: `session_doctemplate_${Date.now()}`,
            userId,
            username,
            selectedSources: ["forms"],
            schema: supabaseSchema,
            userJwtToken,
            userRole,
            useExtractedData: true,
          }),
        },
      );

      if (!response.ok) throw new Error(`API error ${response.status}`);

      const respData = await response.json();
      const answerRaw = respData.answer || "";
      const answerNorm = answerRaw.replace(/\r\n/g, "\n");
      if (answerRaw !== answerNorm) {
        console.log(
          "📝 [DocAsTemplate] Normalized CRLF in AI answer for diff consistency",
        );
      }
      setDiffNewText(answerNorm);
    } catch (error) {
      console.error("❌ Doc-as-template AI error:", error);
      toast({
        title: "Error generating changes",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      releaseDocProcessingLock();
      setIsDiffViewerOpen(false);
      stripDocAsTemplateNavigationState();
    } finally {
      setIsGeneratingDiff(false);
    }
  };

  // Release the collaborative awareness lock
  const releaseDocProcessingLock = () => {
    setIsDocProcessingLocked(false);
    docTemplateCanonSnapshotRef.current = null;
    // No editor.enable() calls — we never disabled it; the CSS overlay was the lock.
    if (collaborationProvider?.awareness) {
      collaborationProvider.awareness.setLocalStateField(
        "ai_doc_processing_locked",
        false,
      );
    }
  };

  // Apply accepted diff changes to the document and unlock
  const handleApplyDiffChanges = async (acceptedText, acceptedChanges) => {
    logEditorFormatStatus("BEFORE applyDiff");
    console.log(
      `📝 [DocAsTemplate] applyDiff: acceptedChanges=${acceptedChanges?.length || 0}`,
    );
    const editor = editorRef.current?.getEditor(0);

    if (editor) {
      if (acceptedChanges && acceptedChanges.length > 0) {
        const fullRaw = editor.getText();
        const fullCanon = fullRaw.replace(/\r\n/g, "\n");
        const snap = docTemplateCanonSnapshotRef.current;
        if (snap != null && snap !== fullCanon) {
          console.warn(
            "📝 [DocAsTemplate] Document text changed since diff was built — offsets may be wrong",
            { snapLen: snap.length, currentLen: fullCanon.length },
          );
        }
        console.info("📝 [DocAsTemplate] Step apply: snapshot vs editor", {
          snapshotLen: snap?.length,
          editorCanonLen: fullCanon.length,
        });

        const acceptedFiltered = acceptedChanges.filter((c) => {
          if (c.origStart !== undefined && c.origEnd !== undefined) {
            if (c.origEnd < c.origStart) {
              console.warn(
                "📝 [DocAsTemplate] Invalid chunk origEnd < origStart — skipped",
                c,
              );
              return false;
            }
            return true;
          }
          if (!c.oldText || c.oldText.length === 0) {
            console.warn(
              "📝 [DocAsTemplate] Skipping insert-only hunk (no offsets and no anchor text):",
              (c.newText || "").slice(0, 120),
            );
            return false;
          }
          return true;
        });

        const positioned = acceptedFiltered
          .map((c, chunkIndex) => {
            if (c.origStart !== undefined && c.origEnd !== undefined) {
              const slice = fullCanon.slice(c.origStart, c.origEnd);
              if (c.oldText && slice !== c.oldText) {
                console.warn(
                  `📝 [DocAsTemplate] Chunk ${chunkIndex + 1}: canonical[origStart:origEnd] !== oldText (still applying at recorded offsets)`,
                  {
                    slicePreview: slice.slice(0, 80),
                    oldPreview: c.oldText.slice(0, 80),
                  },
                );
              }
              const rawStart = canonToRawIndex(fullRaw, c.origStart);
              const rawEnd = canonToRawIndex(fullRaw, c.origEnd);
              const rawLen = rawEnd - rawStart;
              return {
                rawStart,
                rawLen,
                newText: c.newText,
                origStart: c.origStart,
                origEnd: c.origEnd,
                chunkType: c.type,
              };
            }
            const { idx, length } = findDocTemplateChunk(fullRaw, c.oldText);
            if (idx === -1) {
              console.warn(
                "📝 [DocAsTemplate] Legacy indexOf could not locate oldText — skipped:",
                (c.oldText || "").slice(0, 120),
              );
              return null;
            }
            return {
              rawStart: idx,
              rawLen: length,
              newText: c.newText,
              origStart: null,
              origEnd: null,
              chunkType: "legacy",
            };
          })
          .filter(Boolean)
          .sort(
            (a, b) => (b.origStart ?? b.rawStart) - (a.origStart ?? a.rawStart),
          );

        if (acceptedChanges.length > 0 && positioned.length === 0) {
          console.error(
            "📝 [DocAsTemplate] No chunks applied — check diff vs editor text. fullCanonLen=",
            fullCanon.length,
          );
        }

        let chunkStep = 0;
        for (const p of positioned) {
          chunkStep += 1;
          const idx = p.rawStart;
          const length = p.rawLen;
          const newText = p.newText ?? "";
          console.info(
            `📝 [DocAsTemplate] Step apply ${chunkStep}/${positioned.length}`,
            {
              chunkType: p.chunkType,
              origStart: p.origStart,
              origEnd: p.origEnd,
              rawStart: idx,
              rawLen: length,
              newTextLen: newText.length,
            },
          );

          const inlineFmtRaw = editor.getFormat(idx, 1);
          const inlineFmt = Object.fromEntries(
            Object.entries(inlineFmtRaw).filter(
              ([, v]) => v != null && v !== false,
            ),
          );

          const newlineFormats = {};
          const rangeText = fullRaw.slice(idx, idx + length);
          let nlOffset = rangeText.indexOf("\n");
          while (nlOffset !== -1) {
            newlineFormats[nlOffset] = editor.getFormat(idx + nlOffset, 1);
            nlOffset = rangeText.indexOf("\n", nlOffset + 1);
          }

          editor.deleteText(idx, length, "api");
          if (newText) {
            if (Object.keys(inlineFmt).length > 0) {
              editor.insertText(idx, newText, inlineFmt, "api");
            } else {
              editor.insertText(idx, newText, "api");
            }
            Object.entries(newlineFormats).forEach(([offsetStr, fmt]) => {
              const offset = parseInt(offsetStr, 10);
              if (
                Object.keys(fmt).length > 0 &&
                offset < newText.length &&
                newText[offset] === "\n"
              ) {
                editor.formatText(idx + offset, 1, fmt, "api");
              }
            });
          }
        }

        logEditorFormatStatus("AFTER surgical replacements");

        try {
          await handleSaveDocFromHook();
          console.log("✅ [DocAsTemplate] Changes saved to DB after Apply");
          toast({
            title: "Changes applied and saved",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        } catch (err) {
          console.error("❌ [DocAsTemplate] Save failed after Apply:", err);
          toast({
            title: "Changes applied but save failed",
            description: "Refresh may lose changes. Try saving manually.",
            status: "warning",
            duration: 6000,
            isClosable: true,
          });
        }
      }
      // If acceptedChanges is empty (all changes rejected), we leave the editor
      // untouched.  Do NOT call setContents(markdownToQuillDelta(acceptedText))
      // here — that would strip all Quill formatting from unchanged lines.
    }

    releaseDocProcessingLock();
    setIsDiffViewerOpen(false);
    stripDocAsTemplateNavigationState();
  };

  // Cancel the diff viewer and unlock the document
  const handleCancelDiff = () => {
    releaseDocProcessingLock();
    setIsDiffViewerOpen(false);
    stripDocAsTemplateNavigationState();
  };

  /* REMOVED - Now in useDocumentOperations hook:
  const handleRemoveDuplicates = async () => {
    const editor = editorRef.current?.getEditor(0);
    if (!editor) {
      console.log('Editor not available for duplicate removal');
      return;
    }

    try {
      // Get current content as delta to preserve formatting
      const currentDelta = editor.getContents();
      console.log('🔍 Starting simple duplicate removal - find first duplicate and cut everything after...');

      if (!currentDelta || !currentDelta.ops) {
        console.log('No content to process');
        return;
      }

      // Convert to text for line-by-line analysis
      const fullText = editor.getText();
      const lines = fullText.split('\n');
      
      console.log(`📊 Analyzing ${lines.length} lines for duplicates...`);
      
      // Simple approach: find the first line, then find where it repeats, cut everything after
      let firstDuplicateIndex = -1;
      
      console.log(`🔍 Looking for first line and where it repeats...`);
      
      // Get the first non-empty line
      let firstLine = '';
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.length > 0) {
          firstLine = line;
          console.log(`📝 First line found: "${firstLine}"`);
          break;
        }
      }
      
      if (firstLine.length === 0) {
        console.log('No content found in document');
        return;
      }
      
      // Look for the first line to repeat
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Skip empty lines
        if (line.length === 0) continue;
        
        // Check if this line matches the first line
        if (line === firstLine) {
          console.log(`🎯 Found duplicate of first line at line ${i + 1}: "${line}"`);
          firstDuplicateIndex = i;
          break;
        }
      }

      if (firstDuplicateIndex === -1) {
        toast({
          title: "No Duplicates Found",
          description: "No duplicate content was found in this document.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Get the text content that should be kept (up to the duplicate line)
      const cleanLines = lines.slice(0, firstDuplicateIndex);
      const cleanText = cleanLines.join('\n');
      
      console.log(`✂️ Cutting document at line ${firstDuplicateIndex + 1}, keeping ${cleanLines.length} lines`);
      
      // Find the operation that contains the end of our clean text
      let cutIndex = -1;
      let accumulatedText = '';
      
      for (let i = 0; i < currentDelta.ops.length; i++) {
        const op = currentDelta.ops[i];
        
        if (op.insert && typeof op.insert === 'string') {
          accumulatedText += op.insert;
          
          // Check if we've reached the end of our clean text
          if (accumulatedText.includes(cleanText) && accumulatedText.length >= cleanText.length) {
            cutIndex = i + 1; // Include this operation
            break;
          }
        }
      }

      if (cutIndex === -1) {
        console.log('Could not find cut point in delta operations');
        return;
      }

      // Keep only operations up to the cut point
      const cleanOps = currentDelta.ops.slice(0, cutIndex);
      
      console.log(`✂️ Cutting document at operation ${cutIndex}, keeping ${cleanOps.length} operations`);
      
      // Create new delta with clean operations (preserving all formatting)
      const cleanDelta = {
        ...currentDelta,
        ops: cleanOps
      };

      // Update the editor with clean content while preserving formatting
      editor.setContents(cleanDelta, 'api');

      // Show success message
      const removedOps = currentDelta.ops.length - cleanOps.length;
      toast({
        title: "Duplicates Removed",
        description: `Successfully removed ${removedOps} duplicate operations starting from operation ${cutIndex + 1}.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      console.log(`✅ Simple duplicate removal completed. Removed ${removedOps} operations (${Math.round((removedOps / currentDelta.ops.length) * 100)}% reduction).`);

      // Auto-save the cleaned document after a short delay
      setTimeout(() => {
        handleSaveDoc();
      }, 1000);

    } catch (error) {
      console.error('Error removing duplicates:', error);
      toast({
        title: "Error",
        description: "Failed to remove duplicates. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };
  */

  // updateDocumentTable is now provided by useDocumentOperations hook
  // const updateDocumentTable = updateDocumentTableFromHook; // Already available from hook

  /** Handle selection changes to update active section */
  const handleSelectionChange = (range) => {
    if (!range) return;

    if (!editorRef.current) {
      console.warn("EditorRef is null, cannot handle selection change");
      return;
    }

    const position = range.index;
    sections.forEach((section) => {
      if (editorRef.current && editorRef.current.getBoundsForSection) {
        const [startIndex, endIndex] = editorRef.current.getBoundsForSection(
          0,
          section.title,
        );
        if (position >= startIndex && position <= endIndex) {
          setActiveSection(section.id);
        }
      }
    });
  };

  /** Handle voice typing transcription results.
   *  Receives (text, { isFinal, keepInterim }). Interim text is inserted at
   *  the cursor and tracked so subsequent interim events can replace it.
   *  Final segments commit and clear the tracking. keepInterim=true (sent on
   *  stop) drops the tracking but leaves the rendered text in place. */
  const voiceInterimRangeRef = useRef(null);
  const handleVoiceTypingResult = useCallback((transcriptText, opts = {}) => {
    if (opts.keepInterim) {
      voiceInterimRangeRef.current = null;
      return;
    }
    const isFinal = opts.isFinal !== false;
    const editor = editorRef.current?.getEditor(0);
    if (!editor) return;

    if (voiceInterimRangeRef.current) {
      const { index, length } = voiceInterimRangeRef.current;
      if (length > 0) {
        try { editor.deleteText(index, length, "user"); } catch (_) {}
      }
      voiceInterimRangeRef.current = null;
    }

    if (!transcriptText) return;

    const selection = editor.getSelection();
    const cursorPosition = selection ? selection.index : editor.getLength();

    if (isFinal) {
      const capitalized = capitalizeDictatedSegment(editor, cursorPosition, transcriptText);
      const text = capitalized + " ";
      editor.insertText(cursorPosition, text, "user");
      editor.setSelection(cursorPosition + text.length);
    } else {
      editor.insertText(cursorPosition, transcriptText, "user");
      voiceInterimRangeRef.current = {
        index: cursorPosition,
        length: transcriptText.length,
      };
      editor.setSelection(cursorPosition + transcriptText.length);
    }
  }, []);

  // fetchUpdatedDocument is now provided by useDocumentOperations hook
  // const fetchUpdatedDocument = fetchUpdatedDocumentFromHook; // Already available from hook

  /** Initial document fetch */
  const fetchDocument = async (filePath, retryCount = 0) => {
    if (!filePath || typeof filePath !== "string" || !filePath.trim()) {
      console.warn(
        "fetchDocument: skipped — filePath is missing or empty (e.g. still loading doc from share link)",
      );
      if (setIsLoadingDocument) setIsLoadingDocument(false);
      return;
    }
    const supabase = await getSupabase();
    try {
      if (!effectiveDate || effectiveDate.trim() === "") {
        console.error("effectiveDate is null, undefined, or empty");
        setIsLoadingDocument(false);
        return;
      }

      // Prevent duplicate content loading attempts
      if (contentLoadAttempts.current >= maxContentLoadAttempts) {
        console.log(
          "🔒 Maximum content load attempts reached, skipping to prevent duplication",
        );
        logDuplicationDebug("fetch-aborted-max-attempts", {
          reason:
            "contentLoadAttempts reached max — stopping further fetch/setContents to avoid stacked loads (historically caused visible duplication).",
          attempts: contentLoadAttempts.current,
          maxAttempts: maxContentLoadAttempts,
          filePath,
        });
        setIsLoadingDocument(false);
        return;
      }

      // Set loading state at the start
      if (retryCount === 0) {
        setIsLoadingDocument(true);
        // Disable all editors during loading
        quillRefs.current.forEach((quill) => {
          if (quill) {
            quill.enable(false);
          }
        });
      }

      const skipJsonStorageFetch =
        isProbablyJsonStoragePath(filePath) &&
        (skipLargeStorageJsonFetchRef.current ||
          forceOversizeNewDocumentRef.current);

      let storageData = null;
      let storageError = null;
      let preflightTrustSize = false;

      if (skipJsonStorageFetch) {
        storageError = { message: "skipped_large_json_user_new" };
        console.log(
          "📄 [FetchDoc] Skipping storage download (new doc after oversize or navigation flag)",
        );
      } else if (isProbablyJsonStoragePath(filePath)) {
        const pf = await preflightRestrictedStorageJsonSize(
          supabase,
          bucketName_files,
          filePath,
        );
        if (pf.status === "too_large") {
          setStorageOversizeMessage(pf.message);
          setStorageOversizeModalOpen(true);
          setIsLoadingDocument(false);
          quillRefs.current.forEach((quill) => {
            if (quill) quill.enable(true);
          });
          return;
        }
        if (pf.status === "not_found") {
          storageError = { message: "Object not found" };
          console.log("📄 [FetchDoc] Preflight: object not found");
        } else {
          const dl = await supabase.storage
            .from(bucketName_files)
            .download(filePath);
          storageData = dl.data;
          storageError = dl.error;
          if (pf.status === "ok") {
            preflightTrustSize = true;
          }
          console.log(
            `📄 [FetchDoc] Storage download — error: ${storageError?.message || "none"}, hasData: ${!!storageData}`,
          );
        }
      } else {
        const dl = await supabase.storage
          .from(bucketName_files)
          .download(filePath);
        storageData = dl.data;
        storageError = dl.error;
        console.log(
          `📄 [FetchDoc] Storage download — error: ${storageError?.message || "none"}, hasData: ${!!storageData}`,
        );
      }

      if (
        !preflightTrustSize &&
        !storageError &&
        storageData &&
        isProbablyJsonStoragePath(filePath)
      ) {
        const sizeCheck = checkFrontendStorageJsonBlobSize(storageData);
        if (!sizeCheck.ok) {
          setStorageOversizeMessage(sizeCheck.message);
          setStorageOversizeModalOpen(true);
          setIsLoadingDocument(false);
          quillRefs.current.forEach((quill) => {
            if (quill) quill.enable(true);
          });
          return;
        }
      }

      contentLoadAttempts.current += 1;
      console.log(
        "Fetching document from:",
        filePath,
        `(attempt ${contentLoadAttempts.current}/${maxContentLoadAttempts})`,
      );
      console.log("📄 [FetchDoc] State at fetch time:", {
        passedFilePath,
        urlDocumentId,
        correctedType,
        effectiveDate,
        extractedName,
        hasLocationState: !!location.state,
        createNew,
        useMostRecentMatch,
      });

      const editor = editorRef.current?.getEditor(0);
      if (!editor) {
        if (retryCount < 5) {
          console.log(
            `Editor not ready, retrying in 500ms (attempt ${retryCount + 1}/5)`,
          );
          setTimeout(() => fetchDocument(filePath, retryCount + 1), 500);
          return;
        } else {
          console.error("Editor not ready after 5 attempts, giving up");
          setDocumentLoaded(true);
          setContentLoadingComplete(true);
          setIsLoadingDocument(false);
          quillRefs.current.forEach((q) => {
            if (q) q.enable(true);
          });
          toast?.({
            title: "Editor not ready",
            description: "Try refreshing the page if the document stays locked.",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
          return;
        }
      }

      let shouldUseTemplate = false;

      // Magic Notes (Vita-Med Drive): never load a patient template — start blank or load existing
      if (isMagicNotes && (storageError || !storageData)) {
        console.log("Magic Notes: new file — starting with blank editor");
        setDocumentLoaded(true);
        setContentLoadingComplete(true);
        setIsLoadingDocument(false);
        quillRefs.current.forEach((quill) => {
          if (quill) quill.enable(true);
        });
        return;
      }

      // Check if this is a new document OR if it's empty
      if (storageError || !storageData) {
        console.log(
          `📄 [FetchDoc] Storage error or no data — setting shouldUseTemplate=true. Error: ${storageError?.message}`,
        );
        shouldUseTemplate = true;
      } else {
        // Check if existing document is empty
        const fileContent = await storageData.text();
        const parsedData = JSON.parse(fileContent);
        console.log(
          `📄 [FetchDoc] Loaded file — ops count: ${parsedData.delta?.ops?.length}, first op preview: ${JSON.stringify(parsedData.delta?.ops?.[0])?.substring(0, 100)}`,
        );

        // Enhanced empty document check
        const isEmpty =
          !parsedData.delta ||
          !parsedData.delta.ops ||
          parsedData.delta.ops.length === 0 ||
          (parsedData.delta.ops.length === 1 &&
            (!parsedData.delta.ops[0].insert ||
              parsedData.delta.ops[0].insert.trim() === ""));

        // For FFP/FFS documents, be more lenient with empty check to prevent duplication
        // If document has any content beyond just a title, consider it not empty
        const hasSubstantialContent =
          parsedData.delta &&
          parsedData.delta.ops &&
          parsedData.delta.ops.length > 1;

        // Override isEmpty for FFP/FFS/SOC/STU if document has substantial content
        if (
          (correctedType === "FFP" ||
            correctedType === "FFS" ||
            correctedType === "SO" ||
            correctedType === "ST" ||
            correctedType === "SOC" ||
            correctedType === "STU") &&
          hasSubstantialContent
        ) {
          console.log(
            "🔍 FFP/FFS: Document has substantial content, overriding isEmpty check",
          );
          shouldUseTemplate = false;
        }

        console.log("Document content check:", {
          hasData: !!parsedData.delta,
          ops: parsedData.delta?.ops,
          isEmpty: isEmpty,
          hasSubstantialContent: hasSubstantialContent,
          correctedType: correctedType,
          shouldUseTemplate: shouldUseTemplate,
        });

        // Magic Notes: never use template, even if the stored doc is empty
        if (isMagicNotes && shouldUseTemplate) {
          console.log(
            "Magic Notes: stored file is empty — keeping blank editor",
          );
          setDocumentLoaded(true);
          setContentLoadingComplete(true);
          setIsLoadingDocument(false);
          quillRefs.current.forEach((quill) => {
            if (quill) quill.enable(true);
          });
          return;
        }

        // Force template usage for NP and NS documents only if they are empty or new
        // Skip for Magic Notes — always start blank
        if (
          !isMagicNotes &&
          (correctedType === "NP" || correctedType === "NS") &&
          (isEmpty || createNew)
        ) {
          console.log(
            "🆕 NP/NS document - creating new document with template (empty or new)",
          );
          shouldUseTemplate = true;
        } else if (
          !isMagicNotes &&
          createNew &&
          !(
            (correctedType === "SO" ||
              correctedType === "ST" ||
              correctedType === "SOC" ||
              correctedType === "STU") &&
            hasSubstantialContent
          )
        ) {
          console.log(
            "🆕 Creating new document for existing patient - forcing template usage",
          );
          shouldUseTemplate = true;
        } else if (
          !isMagicNotes &&
          isEmpty &&
          !(
            (correctedType === "FFP" ||
              correctedType === "FFS" ||
              correctedType === "SO" ||
              correctedType === "ST" ||
              correctedType === "SOC" ||
              correctedType === "STU") &&
            hasSubstantialContent
          )
        ) {
          shouldUseTemplate = true;
        } else {
          // Check if the patient type has changed and we need to use a different template
          const currentPatientType = correctedType;
          const firstInsert = parsedData.delta?.ops?.[0]?.insert;
          const documentContent =
            typeof firstInsert === "string" ? firstInsert : "";

          console.log("🔍 Checking for type change:", {
            currentPatientType,
            documentContent: documentContent.substring(0, 200) + "...",
            documentOps: parsedData.delta?.ops?.length,
          });

          // Debug: Log the file path and content to understand the mismatch
          console.log("🔍 DEBUGGING FILE CONTENT MISMATCH:");
          console.log("   File Path:", filePath);
          console.log("   Expected Patient:", extractedName, validId);
          console.log(
            "   Document Content Preview:",
            documentContent.substring(0, 300),
          );
          console.log("   User Info:", userInfo);
          console.log("   Generated Filename:", displayTitle);

          // If document exists and has content, load it
          console.log("🔍 Existing document found with content - loading it");

          // For FFP/FFS documents, check if this is a duplicate load to prevent content duplication
          if (correctedType === "FFP" || correctedType === "FFS") {
            // Check if document already has substantial content (more than just title)
            if (
              parsedData.delta &&
              parsedData.delta.ops &&
              parsedData.delta.ops.length > 1
            ) {
              console.log(
                "🔍 FFP/FFS: Document already has content, skipping FFP logic to prevent duplication",
              );
              console.log(
                "🔍 FFP/FFS: Existing content ops:",
                parsedData.delta.ops.length,
              );

              // Check content loading lock to prevent race conditions
              if (contentLoadingLock.current) {
                console.log(
                  "🔒 Content loading lock active, skipping duplicate load",
                );
                logDuplicationDebug("skipped-concurrent-load", {
                  reason:
                    "contentLoadingLock already held — second FFP/FFS load skipped. If the first path still applies template or Yjs later applies remote state, you can see duplicated blocks.",
                  branch: "FFP_FFS",
                  filePath,
                });
                return;
              }

              // Add minimal delay for FFP/FFS documents to ensure content is fully processed
              console.log(
                "🔍 FFP/FFS: Adding minimal delay to prevent race conditions...",
              );
              await new Promise((resolve) => setTimeout(resolve, 500));

              contentLoadingLock.current = true;

              // Update title in FFP/FFS documents when loading (skip for combined documents and NP/NS)
              if (
                !isCombined &&
                (correctedType === "FFP" || correctedType === "FFS")
              ) {
                const currentTime =
                  patientScheduledTime ||
                  navbarTime ||
                  new Date().toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  });

                let titleUpdated = false;
                parsedData.delta.ops.forEach((op) => {
                  if (op.insert && typeof op.insert === "string") {
                    // Replace document title with current patient info
                    const titlePattern =
                      /^\d{1,2}:\d{2}\s+[^-\n]+-\s+\d+\s+\w+$/m;
                    if (titlePattern.test(op.insert)) {
                      // Use generateDocFilename to ensure consistency with document title
                      const titleUserInfo = {
                        time_: currentTime,
                        [prompts[0][0]]: extractedName,
                        [colid]: emailForDocTitle,
                        EHR_ID: ehrIdForDocTitle,
                        [prompts[getTypeFieldIndex()][0]]: correctedType,
                      };
                      const newTitle = generateDocFilename(titleUserInfo);
                      op.insert = op.insert.replace(titlePattern, newTitle);
                      titleUpdated = true;
                      console.log(
                        "📝 FFP/FFS Title Update (Load): Updated document title:",
                        {
                          original: op.insert.substring(0, 50),
                          updated: newTitle,
                        },
                      );
                    }
                  }
                });

                if (titleUpdated) {
                  console.log(
                    "📝 FFP/FFS Title Update (Load): Title updated, saving document...",
                  );
                  // Save the updated content
                  setTimeout(() => {
                    handleSaveDoc();
                  }, 500);
                }
              } else if (isCombined) {
                console.log(
                  "📝 FFP/FFS Title Update (Load): Skipping title update for combined document to preserve original headers",
                );
              } else {
                console.log(
                  "📝 NP/NS Title Update (Load): Skipping title update for NP/NS document to preserve original time",
                );
              }

              // Load the existing content and return early
              editor.setContents(parsedData.delta, "api");
              try {
                const plain = editor.getText?.() ?? "";
                const dup = analyzeTextDuplication(plain);
                if (dup.suspected) {
                  logDuplicationDebug("suspected-duplicate-after-storage-load", {
                    ...dup,
                    reason: `${dup.reason} (detected immediately after setContents from storage, FFP/FFS branch).`,
                    branch: "FFP_FFS",
                    filePath,
                    parsedOps: parsedData.delta?.ops?.length,
                  });
                }
              } catch (_) {
                /* ignore */
              }
              setDocumentLoaded(true);
              setIsLoadingDocument(false);
              // Enable editor after content is loaded
              quillRefs.current.forEach((quill) => {
                if (quill) {
                  quill.enable(true);
                }
              });
              contentLoadingLock.current = false; // Release lock
              setContentLoadingComplete(true); // Mark content loading as complete

              return;
            }
          }
          // For non-FFP/FFS documents, load the content normally

          // For non-FFP/FFS documents, load the content normally
          // Check content loading lock to prevent race conditions
          if (contentLoadingLock.current) {
            console.log(
              "🔒 Content loading lock active, skipping duplicate load",
            );
            logDuplicationDebug("skipped-concurrent-load", {
              reason:
                "contentLoadingLock already held — non-FFP/FFS load skipped to avoid parallel setContents (race with large JSON or template apply).",
              branch: "non_FFP",
              filePath,
            });
            return;
          }
          contentLoadingLock.current = true;

          editor.setContents(parsedData.delta, "api");
          try {
            const plain = editor.getText?.() ?? "";
            const dup = analyzeTextDuplication(plain);
            if (dup.suspected) {
              logDuplicationDebug("suspected-duplicate-after-storage-load", {
                ...dup,
                reason: `${dup.reason} (detected immediately after setContents from storage, non-FFP branch).`,
                branch: "non_FFP",
                filePath,
                parsedOps: parsedData.delta?.ops?.length,
              });
            }
          } catch (_) {
            /* ignore */
          }
          setDocumentLoaded(true);
          setIsLoadingDocument(false);
          // Enable editor after content is loaded
          quillRefs.current.forEach((quill) => {
            if (quill) {
              quill.enable(true);
            }
          });

          // Release lock and mark content loading as complete immediately
          contentLoadingLock.current = false;
          setContentLoadingComplete(true);

          return;
        }
      }

      console.log("🔍 Final shouldUseTemplate decision:", {
        shouldUseTemplate,
        correctedType,
      });

      // Check if user chose to use template after empty document prompt
      if (shouldUseTemplate && isWaitingForTemplateDecision === false) {
        console.log(
          "🔍 User chose to use template after empty document prompt",
        );
        // Create backup before applying template (if enabled)
        // Versioning system handles backup logic
        {
          try {
            const currentContent = editor.getContents();
            if (
              currentContent &&
              currentContent.ops &&
              currentContent.ops.length > 0
            ) {
              const dataToBackup = {
                sections,
                content: currentContent,
                delta: currentContent,
                lastUpdated: new Date().toISOString(),
              };
            }
          } catch (backupError) {
            console.warn("⚠️ Pre-template backup failed:", backupError);
          }
        }

        // Use the actual template key. SOC/STU are aliases for SO/ST templates.
        let patientType = getTemplateTypeForCurrentDocument();

        // If createNew is true for FFP/FFS, convert to NP/NS respectively
        if (createNew && patientType === "FFP") {
          console.log("🔍 FFP/FFS: createNew=true for FFP, converting to NP");
          patientType = "NP";
        } else if (createNew && patientType === "FFS") {
          console.log("🔍 FFP/FFS: createNew=true for FFS, converting to NS");
          patientType = "NS";
        }

        console.log("Using template for type:", patientType);

        // For FFP/FFS/SO/ST, fetch the last visit's document.
        // FFP/FFS: copy unless user explicitly chose "create new".
        // SOC/STU: copy ONLY when useMostRecentMatch is explicitly true (set by popup);
        //   never copy on refresh or re-navigation so saved content is never overwritten.
        if (
          ((correctedType === "FFP" || correctedType === "FFS") &&
            !createNew) ||
          ((correctedType === "SO" ||
            correctedType === "ST" ||
            correctedType === "SOC" ||
            correctedType === "STU") &&
            useMostRecentMatch)
        ) {
          console.log(
            "🔍 FFP/FFS/SOC/STU: Attempting to copy last visit document",
          );

          // CRITICAL: Check if document already has content to prevent duplication
          const currentContent = editor.getContents();
          if (
            currentContent &&
            currentContent.ops &&
            currentContent.ops.length > 1
          ) {
            console.log(
              "🔍 FFP/FFS: Document already has content, skipping FFP logic to prevent duplication",
            );
            console.log(
              "🔍 FFP/FFS: Current content ops:",
              currentContent.ops.length,
            );
            setIsLoadingDocument(false);
            quillRefs.current.forEach((quill) => {
              if (quill) {
                quill.enable(true);
              }
            });
            return; // Exit early to prevent duplication
          }
          console.log("🔍 FFP/FFS: Patient details:", {
            extractedName,
            validId,
            effectiveDate,
            patientType,
          });

          // Validate required data for FFP/FFS
          if (!extractedName || extractedName.trim() === "") {
            console.error(
              "❌ FFP/FFS: No patient name provided - cannot search for previous visits",
            );
            setIsLoadingDocument(false);
            quillRefs.current.forEach((quill) => {
              if (quill) {
                quill.enable(true);
              }
            });
            toast({
              title: "Missing Patient Information",
              description:
                "Patient name is required to find previous visits for FFP patients.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            return; // Exit early - don't load any template
          }

          try {
            // Get the patient's last visit document using validId instead of id
            console.log("🔍 FFP/FFS: Searching for last visit with params:", {
              colid,
              validId,
              currentDate: effectiveDate,
              patientType,
            });

            // Set timeout to show message if search takes too long
            const timeoutId = setTimeout(() => {
              if (isLoadingDocument) {
                console.log(
                  "⏱️ FFP/FFS: Search timeout after 30 seconds - no record found",
                );
                setIsLoadingDocument(false);
                quillRefs.current.forEach((quill) => {
                  if (quill) quill.enable(true);
                });
                toast({
                  title: "No Record Found",
                  description: `Could not find previous visit record within 30 seconds.`,
                  status: "info",
                  duration: 3000,
                  isClosable: true,
                });
                setTimeout(() => {
                  toast({
                    title: "Initializing Blank Document",
                    description: `Initializing a blank document.`,
                    status: "success",
                    duration: 4000,
                    isClosable: true,
                  });
                }, 800);
                setContentLoadingComplete(true);
              }
            }, 30000);

            // For FFP/FFS, we need to search by patient name since validId might be UUID
            // Try multiple approaches to find the last visit
            let lastVisitData = null;
            let lastVisitError = null;

            // First try: search by patient name (most reliable for FFP/FFS)
            console.log(
              "🔍 FFP/FFS: Searching by patient name:",
              extractedName,
            );
            const { data: nameSearchData, error: nameSearchError } =
              await supabase
                .from(tableName)
                .select("*")
                .eq(prompts[0][0], extractedName) // Search by patient name field
                .lt("date_", effectiveDate) // Only get visits BEFORE current date
                .order("date_", { ascending: false }) // Order by date descending to get most recent
                .order("created_at", { ascending: false }) // Secondary sort by created_at
                .limit(10);

            // A shared name is not a shared chart: when this visit and the
            // candidate both carry a real EHR ID they have to agree, or an FFP
            // visit silently carries a DIFFERENT patient's note forward. Same
            // rule as utils/priorVisitMatch, used by the medstudent clinic and
            // the collab server's room seeding.
            const nameMatches = (nameSearchData || []).filter((row) =>
              isSamePatientRow(
                { patient_name: extractedName, EHR_ID: ehrIdForDocTitle },
                { patient_name: row[prompts[0][0]], EHR_ID: row.EHR_ID },
              ),
            );

            if (nameMatches.length > 0) {
              lastVisitData = [nameMatches[0]];
              console.log("🔍 FFP/FFS: Found last visit by name search");
            } else {
              // Second try: search by colid (email) if validId looks like an email
              if (validId && validId.includes("@")) {
                console.log("🔍 FFP/FFS: Searching by email:", validId);
                const { data: emailSearchData, error: emailSearchError } =
                  await supabase
                    .from(tableName)
                    .select("*")
                    .eq(colid, validId) // Search by email field
                    .lt("date_", effectiveDate) // Only get visits BEFORE current date
                    .order("date_", { ascending: false }) // Order by date descending to get most recent
                    .order("created_at", { ascending: false }) // Secondary sort by created_at
                    .limit(1);

                if (emailSearchData && emailSearchData.length > 0) {
                  lastVisitData = emailSearchData;
                  lastVisitError = emailSearchError;
                  console.log("🔍 FFP/FFS: Found last visit by email search");
                } else {
                  lastVisitError = emailSearchError;
                }
              } else {
                // Third try: search by ID field if validId is numeric
                if (validId && !isNaN(validId)) {
                  console.log("🔍 FFP/FFS: Searching by ID:", validId);
                  const { data: idSearchData, error: idSearchError } =
                    await supabase
                      .from(tableName)
                      .select("*")
                      .eq("id", validId) // Search by ID field
                      .lt("date_", effectiveDate) // Only get visits BEFORE current date
                      .order("date_", { ascending: false }) // Order by date descending to get most recent
                      .order("created_at", { ascending: false }) // Secondary sort by created_at
                      .limit(1);

                  if (idSearchData && idSearchData.length > 0) {
                    lastVisitData = idSearchData;
                    lastVisitError = idSearchError;
                    console.log("🔍 FFP/FFS: Found last visit by ID search");
                  } else {
                    lastVisitError = idSearchError;
                  }
                } else {
                  lastVisitError = nameSearchError;
                }
              }
            }

            clearTimeout(timeoutId); // Clear timeout if search completes

            console.log("🔍 FFP/FFS: Last visit search results:", {
              data: lastVisitData,
              error: lastVisitError,
              count: lastVisitData?.length,
            });

            if (lastVisitError) {
              console.error(
                "🔍 FFP/FFS: Error searching for last visit:",
                lastVisitError,
              );
              throw lastVisitError;
            }

            if (lastVisitData && lastVisitData.length > 0) {
              const lastVisit = lastVisitData[0];
              console.log("🔍 FFP/FFS: Found last visit:", {
                date: lastVisit.date_,
                id: lastVisit[colid],
                name: lastVisit[prompts[0][0]],
                created_at: lastVisit.created_at,
              });
              console.log(
                "📋 FFP/FFS: Content will be loaded from visit on:",
                lastVisit.date_,
                "with ID:",
                lastVisit.id || lastVisit[colid],
              );

              const lastVisitDate = lastVisit.date_;
              const lastVisitYear = lastVisitDate.split("-")[0];

              // Debug: Log the last visit record details
              console.log("🔍 FFP/FFS: Last visit record details:", {
                date: lastVisit.date_,
                name: lastVisit[prompts[0][0]],
                email: lastVisit[colid],
                id: lastVisit.id,
                created_at: lastVisit.created_at,
                time_: lastVisit.time_,
                type: lastVisit.type,
              });

              // Construct the filename using generateDocFilename to match the actual file format
              // Format: "10:50 Li Garrido, DIego  - 123 FFP.json"
              const lastVisitUserInfo = {
                time_: lastVisit.time_ || "",
                [prompts[0][0]]: lastVisit[prompts[0][0]] || "",
                EHR_ID:
                  lastVisit.EHR_ID || lastVisit[colid] || lastVisit.id || "",
                [prompts[getTypeFieldIndex()][0]]:
                  lastVisit.type || correctedType || "",
              };
              const lastVisitFileNameBase =
                generateDocFilename(lastVisitUserInfo);
              const lastVisitFileName = `${lastVisitFileNameBase}.json`;
              let lastVisitPath = await generateMedADocsPath(
                lastVisitYear,
                lastVisitDate,
                lastVisitFileName,
              );

              // First, try to look up the actual stored path from the documents table
              // (the generated path may differ from the actual stored filename format)
              try {
                const meetingNameForLookup = lastVisit[prompts[0][0]] || "";
                const { data: docRow } = await supabase
                  .from("documents")
                  .select("path")
                  .ilike("path", `%${lastVisitDate}%`)
                  .ilike("path", `%${meetingNameForLookup}%`)
                  .order("latest_update", { ascending: false })
                  .limit(1)
                  .maybeSingle();
                if (docRow?.path) {
                  console.log(
                    "🔍 FFP/FFS: Resolved actual path from documents table:",
                    docRow.path,
                  );
                  lastVisitPath = docRow.path;
                }
              } catch (lookupErr) {
                console.warn(
                  "🔍 FFP/FFS: documents table lookup failed, using generated path:",
                  lookupErr,
                );
              }

              console.log("🔍 FFP/FFS: Attempting to copy document:", {
                //
                from: lastVisitPath, //
                to: filePath, //
                lastVisitDate, //
                currentDate: effectiveDate, //
                lastVisitFileName, //
                lastVisitYear, //
                lastVisitUserInfo, //
                lastVisitEmail: lastVisit[colid], //
                lastVisitName: lastVisit[prompts[0][0]], //
              }); //

              // First try to download the last visit's document
              let lastDocData = null;
              let downloadError = null;

              let directDownload = null;
              let directError = null;
              let directPfTrust = false;
              let visitPreflightOversize = false;

              const visitPf = await preflightRestrictedStorageJsonSize(
                supabase,
                bucketName_files,
                lastVisitPath,
              );
              if (visitPf.status === "too_large") {
                toast({
                  title: "Previous visit document too large",
                  description: visitPf.message,
                  status: "error",
                  duration: 9000,
                  isClosable: true,
                });
                visitPreflightOversize = true;
                downloadError = new Error("JSON exceeds editor size limit");
              } else if (visitPf.status === "not_found") {
                directError = { message: "Object not found" };
              } else {
                const dl = await supabase.storage
                  .from(bucketName_files)
                  .download(lastVisitPath);
                directDownload = dl.data;
                directError = dl.error;
                if (visitPf.status === "ok") {
                  directPfTrust = true;
                }
              }

              if (directError && !visitPreflightOversize) {
                console.warn(
                  "🔍 FFP/FFS: Direct download failed, trying to list files in directory:",
                  directError,
                );

                // Fallback: List files in the directory and find matching file
                // Derive directory from the already schema-aware lastVisitPath instead of rebuilding it
                const directoryPath = lastVisitPath.substring(
                  0,
                  lastVisitPath.lastIndexOf("/"),
                );
                const { data: files, error: listError } = await supabase.storage
                  .from(bucketName_files)
                  .list(directoryPath);

                if (!listError && files && files.length > 0) {
                  console.log(
                    "🔍 FFP/FFS: Found files in directory:",
                    files.map((f) => f.name),
                  );

                  // Try to find a file that matches the patient name and EHR_ID
                  const patientName = lastVisit[prompts[0][0]];
                  const ehrId = lastVisit.EHR_ID || lastVisit[colid] || "";

                  // Look for files that contain the patient name and EHR_ID
                  const matchingFile = files.find((file) => {
                    const fileName = file.name.toLowerCase();
                    const nameMatch =
                      patientName &&
                      fileName.includes(patientName.toLowerCase().trim());
                    const idMatch =
                      ehrId &&
                      (fileName.includes(`-${ehrId}`) ||
                        fileName.includes(`- ${ehrId}`));
                    return nameMatch && (idMatch || !ehrId); // Match name, and ID if available
                  });

                  if (matchingFile) {
                    console.log(
                      "🔍 FFP/FFS: Found matching file:",
                      matchingFile.name,
                    );
                    const foundFilePath = `${directoryPath}/${matchingFile.name}`;
                    const ffPf = await preflightRestrictedStorageJsonSize(
                      supabase,
                      bucketName_files,
                      foundFilePath,
                    );
                    let foundFileData = null;
                    let foundFileError = null;
                    let foundPfTrust = false;
                    if (ffPf.status === "too_large") {
                      toast({
                        title: "Previous visit document too large",
                        description: ffPf.message,
                        status: "error",
                        duration: 9000,
                        isClosable: true,
                      });
                      downloadError = new Error(
                        "JSON exceeds editor size limit",
                      );
                    } else if (ffPf.status === "not_found") {
                      foundFileError = { message: "not found" };
                    } else {
                      const fdl = await supabase.storage
                        .from(bucketName_files)
                        .download(foundFilePath);
                      foundFileData = fdl.data;
                      foundFileError = fdl.error;
                      if (ffPf.status === "ok") {
                        foundPfTrust = true;
                      }
                    }

                    if (!foundFileError && foundFileData) {
                      const sizeCheck =
                        !foundPfTrust &&
                        isProbablyJsonStoragePath(foundFilePath)
                          ? checkFrontendStorageJsonBlobSize(foundFileData)
                          : { ok: true };
                      if (!sizeCheck.ok) {
                        toast({
                          title: "Previous visit document too large",
                          description: sizeCheck.message,
                          status: "error",
                          duration: 9000,
                          isClosable: true,
                        });
                        downloadError = new Error(
                          "JSON exceeds editor size limit",
                        );
                      } else {
                        lastDocData = foundFileData;
                        console.log(
                          "🔍 FFP/FFS: Successfully downloaded file using fallback method",
                        );
                        console.log(
                          "📋 FFP/FFS: Content loaded from visit:",
                          lastVisit.date_,
                          "file:",
                          matchingFile.name,
                        );
                      }
                    } else {
                      downloadError = foundFileError;
                      console.error(
                        "🔍 FFP/FFS: Error downloading found file:",
                        foundFileError,
                      );
                    }
                  } else {
                    downloadError = directError;
                    console.error(
                      "🔍 FFP/FFS: No matching file found in directory",
                    );
                  }
                } else {
                  downloadError = directError;
                  console.error(
                    "🔍 FFP/FFS: Error listing files in directory:",
                    listError,
                  );
                }
              } else if (
                directDownload &&
                isProbablyJsonStoragePath(lastVisitPath) &&
                !directPfTrust
              ) {
                const sizeCheck =
                  checkFrontendStorageJsonBlobSize(directDownload);
                if (!sizeCheck.ok) {
                  toast({
                    title: "Previous visit document too large",
                    description: sizeCheck.message,
                    status: "error",
                    duration: 9000,
                    isClosable: true,
                  });
                  downloadError = new Error("JSON exceeds editor size limit");
                } else {
                  lastDocData = directDownload;
                }
              } else {
                lastDocData = directDownload;
              }

              if (downloadError || !lastDocData) {
                console.error(
                  "🔍 FFP/FFS: Error downloading last visit document:",
                  downloadError,
                );
                console.error(
                  "🔍 FFP/FFS: File path that failed:",
                  lastVisitPath,
                );
                console.error("🔍 FFP/FFS: Last visit record details:", {
                  //
                  id: lastVisit.id, //
                  ehr_id: lastVisit.EHR_ID, //
                  email: lastVisit[colid], //
                  name: lastVisit[prompts[0][0]], //
                  date: lastVisit.date_, //
                  time_: lastVisit.time_, //
                  type: lastVisit.type, //
                  generatedFilename: lastVisitFileName, //
                  generatedPath: lastVisitPath, //
                }); //
                console.log(
                  "🔍 FFP/FFS: Cannot copy last visit document, initializing blank document instead",
                );

                // Document not found - prompt user for template
                console.warn(
                  "⚠️ FFP/FFS: Document not found, prompting user for template",
                );
                setIsWaitingForTemplateDecision(true);
                setIsTemplatePromptOpen(true);
                setIsLoadingDocument(false);
                // Don't initialize empty content yet - wait for user decision
                return;
              }

              if (lastDocData) {
                // Parse the document to update age
                const docText = await lastDocData.text();
                let docJson = JSON.parse(docText);

                console.log(
                  "📅 FFP/FFS Age Update: Recalculating age for copied document",
                );

                // Get patient DOB to calculate current age
                let calculatedAge = "___";
                try {
                  const { data: patientRecord, error: dobError } =
                    await supabase
                      .from(tableName)
                      .select("dob")
                      .eq(prompts[0][0], extractedName)
                      .order("created_at", { ascending: false })
                      .limit(1)
                      .single();

                  if (patientRecord && patientRecord.dob) {
                    calculatedAge = calculateAge(
                      patientRecord.dob,
                      effectiveDate,
                    ).toString();
                    console.log(
                      "📅 FFP/FFS Age Update: Age as of",
                      effectiveDate,
                      ":",
                      calculatedAge,
                      "from DOB:",
                      patientRecord.dob,
                    );
                  } else {
                    console.warn(
                      "📅 FFP/FFS Age Update: No DOB found for patient",
                    );
                  }
                } catch (error) {
                  console.warn(
                    "📅 FFP/FFS Age Update: Error fetching DOB:",
                    error,
                  );
                }

                // Update age and title in the document content (skip for combined documents)
                if (docJson.delta && docJson.delta.ops) {
                  let updatedCount = 0;

                  // Get the current time for the new document title
                  const currentTime =
                    patientScheduledTime ||
                    navbarTime ||
                    new Date().toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    });

                  docJson.delta.ops.forEach((op) => {
                    if (op.insert && typeof op.insert === "string") {
                      const originalInsert = op.insert;

                      // Replace document title with current patient info (only for FFP/FFS documents, skip for combined documents)
                      if (
                        !isCombined &&
                        (correctedType === "FFP" || correctedType === "FFS")
                      ) {
                        // Pattern: "13:13 Kamil, Aheed - 5997 FFP" -> "13:13 Kamil, Aheed - 5997 FFP" (with current time)
                        const titlePattern =
                          /^\d{1,2}:\d{2}\s+[^-\n]+-\s+\d+\s+\w+$/m;
                        if (titlePattern.test(op.insert)) {
                          // Use generateDocFilename to ensure consistency with document title
                          const titleUserInfo = {
                            time_: currentTime,
                            [prompts[0][0]]: extractedName,
                            [colid]: emailForDocTitle,
                            EHR_ID: ehrIdForDocTitle,
                            [prompts[getTypeFieldIndex()][0]]: correctedType,
                          };
                          const newTitle = generateDocFilename(titleUserInfo);
                          op.insert = op.insert.replace(titlePattern, newTitle);
                          console.log(
                            "📝 FFP/FFS Title Update: Updated document title:",
                            {
                              original: originalInsert.substring(0, 50),
                              updated: newTitle,
                            },
                          );
                        }
                      } else if (isCombined) {
                        console.log(
                          "📝 FFP/FFS Title Update: Skipping title update for combined document to preserve original headers",
                        );
                      } else {
                        console.log(
                          "📝 NP/NS Title Update: Skipping title update for NP/NS document to preserve original time",
                        );
                      }

                      // Replace various age patterns:
                      // "45 year old", "45 year-old", "45-year-old", "45 yo", "45y/o"
                      op.insert = op.insert.replace(
                        /\b\d+\s*(-?\s*year\s*-?\s*old)\b/gi,
                        `${calculatedAge} year old`,
                      );
                      op.insert = op.insert.replace(
                        /\b\d+\s*(-?\s*y\.?o\.?)\b/gi,
                        `${calculatedAge} yo`,
                      );
                      op.insert = op.insert.replace(
                        /\b\d+\s*(-?\s*y\/o)\b/gi,
                        `${calculatedAge} y/o`,
                      );

                      // Replace age in "This is a ___ year old" format
                      op.insert = op.insert.replace(
                        /\b___\s+year\s+old\b/gi,
                        `${calculatedAge} year old`,
                      );

                      // Replace [age] placeholder if it exists
                      op.insert = op.insert.replace(/\[age\]/g, calculatedAge);

                      if (originalInsert !== op.insert) {
                        updatedCount++;
                        console.log(
                          "📅 FFP/FFS Age Update: Replaced in text:",
                          {
                            original: originalInsert.substring(0, 100),
                            updated: op.insert.substring(0, 100),
                          },
                        );
                      }
                    }
                  });
                  console.log(
                    `📅 FFP/FFS Age Update: Updated age in ${updatedCount} locations in document content`,
                  );
                }

                // Add new date section for FFP/FFS rescheduled patients
                console.log(
                  "📅 FFP/FFS: Adding new date section for rescheduled patient",
                );

                // Ensure docJson.content is a string, initialize if not
                if (typeof docJson.content !== "string") {
                  console.log(
                    "📅 FFP/FFS: docJson.content is not a string, attempting to convert/initialize.",
                  );
                  if (
                    docJson.content === null ||
                    typeof docJson.content === "undefined"
                  ) {
                    docJson.content = ""; // Initialize as empty string
                  } else if (
                    typeof docJson.content === "object" &&
                    docJson.content !== null
                  ) {
                    // If it's an object, try to stringify it, or set to empty string if it's not a simple object
                    try {
                      docJson.content = JSON.stringify(docJson.content);
                      console.log(
                        "📅 FFP/FFS: docJson.content converted from object to string.",
                      );
                    } catch (e) {
                      console.error(
                        "📅 FFP/FFS: Error stringifying docJson.content object:",
                        e,
                      );
                      docJson.content = ""; // Fallback to empty string
                    }
                  }
                }

                // Find "Heart and lung sounds" text and insert date section before it
                // This places the date section between HPI content and "Heart and lung sounds"
                if (docJson.delta && docJson.delta.ops) {
                  // Create the new date section
                  const dateSectionOp = {
                    attributes: {
                      underline: true,
                      background: "transparent",
                      color: "#0000ff",
                      bold: true,
                      header: 3,
                    },
                    insert: "[date_mdy]:\n",
                  };

                  // Add spacing after date section
                  const spacingOp = {
                    insert: "\n\n",
                  };

                  // First, try to find "Heart and lung sounds" text
                  let heartLungIndex = -1;
                  console.log(
                    '📅 FFP/FFS: Searching for "Heart and lung sounds" text in delta ops...',
                  );

                  for (let i = 0; i < docJson.delta.ops.length; i++) {
                    const op = docJson.delta.ops[i];
                    if (op.insert && typeof op.insert === "string") {
                      // Check if this operation contains "Heart and lung" text
                      if (/Heart.*lung/i.test(op.insert)) {
                        heartLungIndex = i;
                        console.log(
                          `📅 FFP/FFS: Found "Heart and lung sounds" at index ${i}:`,
                          {
                            insert: op.insert.substring(0, 100),
                            index: i,
                          },
                        );
                        break;
                      }
                    }
                  }

                  if (heartLungIndex !== -1) {
                    // Insert date section before "Heart and lung sounds"
                    docJson.delta.ops.splice(
                      heartLungIndex,
                      0,
                      dateSectionOp,
                      spacingOp,
                    );
                    console.log(
                      '📅 FFP/FFS: Added new date section before "Heart and lung sounds" at index:',
                      heartLungIndex,
                    );
                  } else {
                    // Fallback: look for existing date sections
                    let lastDateSectionIndex = -1;
                    console.log(
                      '📅 FFP/FFS: "Heart and lung sounds" not found, searching for existing date sections...',
                    );

                    for (let i = docJson.delta.ops.length - 1; i >= 0; i--) {
                      const op = docJson.delta.ops[i];
                      if (op.insert && typeof op.insert === "string") {
                        // Check if this operation contains a date pattern
                        if (/\d{1,2}\/\d{1,2}\/\d{4}/.test(op.insert)) {
                          lastDateSectionIndex = i;
                          console.log(
                            `📅 FFP/FFS: Found date pattern in op at index ${i}`,
                          );
                          break;
                        }
                      }
                    }

                    if (lastDateSectionIndex !== -1) {
                      // Insert before the first date section (so new date appears first)
                      docJson.delta.ops.splice(
                        lastDateSectionIndex,
                        0,
                        dateSectionOp,
                        spacingOp,
                      );
                      console.log(
                        "📅 FFP/FFS: Added new date section before existing date section at index:",
                        lastDateSectionIndex,
                      );
                    } else {
                      // Final fallback: add at the end if nothing found
                      docJson.delta.ops.push(dateSectionOp);
                      docJson.delta.ops.push(spacingOp);
                      console.log(
                        "📅 FFP/FFS: No insertion point found, added at end of document",
                      );
                    }
                  }

                  console.log(
                    "📅 FFP/FFS: Date section op:",
                    JSON.stringify(dateSectionOp, null, 2),
                  );
                }

                // Also update the content field to include the date section after the last date section
                console.log(
                  "📅 FFP/FFS: Content field before adding date section:",
                  {
                    hasContent: !!docJson.content,
                    contentType: typeof docJson.content,
                    contentLength: docJson.content ? docJson.content.length : 0,
                  },
                );

                if (docJson.content && typeof docJson.content === "string") {
                  // First, try to find "Heart and lung sounds" text
                  console.log(
                    '📅 FFP/FFS: Searching for "Heart and lung sounds" in content...',
                  );
                  const heartLungMatch =
                    docJson.content.match(/Heart.*lung.*sounds/i);

                  if (heartLungMatch) {
                    // Insert date section before "Heart and lung sounds"
                    const insertIndex = heartLungMatch.index;
                    const beforeInsert = docJson.content.substring(
                      0,
                      insertIndex,
                    );
                    const afterInsert = docJson.content.substring(insertIndex);
                    docJson.content =
                      beforeInsert + "\n\n[date_mdy]:\n\n" + afterInsert;
                    console.log(
                      '📅 FFP/FFS: Updated content field with date section before "Heart and lung sounds"',
                    );
                  } else {
                    // Fallback: look for existing date sections
                    console.log(
                      '📅 FFP/FFS: "Heart and lung sounds" not found, searching for date patterns...',
                    );
                    const datePattern = /\d{1,2}\/\d{1,2}\/\d{4}:/g;
                    let lastMatch = null;
                    let match;

                    // Find the last occurrence of a date pattern
                    while (
                      (match = datePattern.exec(docJson.content)) !== null
                    ) {
                      lastMatch = match;
                    }

                    if (lastMatch) {
                      // Insert before the first date section (so new date appears first)
                      const insertIndex = lastMatch.index;
                      const beforeInsert = docJson.content.substring(
                        0,
                        insertIndex,
                      );
                      const afterInsert =
                        docJson.content.substring(insertIndex);
                      docJson.content =
                        beforeInsert + "\n\n[date_mdy]:\n\n" + afterInsert;
                      console.log(
                        "📅 FFP/FFS: Updated content field with date section before existing date section",
                      );
                    } else {
                      // Final fallback: add at the end if nothing found
                      docJson.content = docJson.content + "\n\n[date_mdy]:\n\n";
                      console.log(
                        "📅 FFP/FFS: No insertion point found in content, added at end",
                      );
                    }
                  }

                  console.log(
                    "📅 FFP/FFS: Content after adding date section:",
                    docJson.content.substring(docJson.content.length - 100),
                  );
                } else {
                  console.log(
                    "📅 FFP/FFS: Content field is missing or not a string, cannot add date section",
                  );
                }

                // Replace [date_mdy] placeholder with actual date in M/D/YYYY format
                console.log(
                  "📅 FFP/FFS: Replacing [date_mdy] placeholder with actual date",
                );
                const formatDateToMDY = (dateString) => {
                  // Parse date string directly to avoid timezone issues
                  const [year, month, day] = dateString.split("-");
                  return `${parseInt(month)}/${parseInt(day)}/${year}`;
                };

                // Format date to MM/DD/YYYY with leading zeros
                const formatDateToMMDDYYYY = (dateString) => {
                  const [year, month, day] = dateString.split("-");
                  return `${String(parseInt(month)).padStart(2, "0")}/${String(parseInt(day)).padStart(2, "0")}/${year}`;
                };

                console.log(
                  "📅 FFP/FFS: effectiveDate before formatting:",
                  effectiveDate,
                );
                const formattedDate = formatDateToMDY(effectiveDate);
                const formattedDateMMDDYYYY =
                  formatDateToMMDDYYYY(effectiveDate);
                console.log("📅 FFP/FFS: Formatted date:", formattedDate);
                console.log(
                  "📅 FFP/FFS: Formatted date MM/DD/YYYY:",
                  formattedDateMMDDYYYY,
                );

                // Replace in delta ops
                if (docJson.delta && docJson.delta.ops) {
                  docJson.delta.ops.forEach((op) => {
                    if (op.insert && typeof op.insert === "string") {
                      op.insert = op.insert
                        .replace(/\[date_mdy\]/g, formattedDate)
                        .replace(/\[MM\/DD\/YYYY\]/g, formattedDateMMDDYYYY);
                    }
                  });
                  console.log(
                    "📅 FFP/FFS: Replaced [date_mdy] and [MM/DD/YYYY] in delta ops",
                  );
                }

                // Replace in content field
                console.log("📅 FFP/FFS: Content field check:", {
                  hasContent: !!docJson.content,
                  contentType: typeof docJson.content,
                  contentLength: docJson.content ? docJson.content.length : 0,
                });

                if (docJson.content && typeof docJson.content === "string") {
                  const beforeReplace = docJson.content;
                  docJson.content = docJson.content
                    .replace(/\[date_mdy\]/g, formattedDate)
                    .replace(/\[MM\/DD\/YYYY\]/g, formattedDateMMDDYYYY);
                  console.log(
                    "📅 FFP/FFS: Replaced [date_mdy] and [MM/DD/YYYY] in content field",
                  );
                  console.log(
                    "📅 FFP/FFS: Content before replacement:",
                    beforeReplace.substring(beforeReplace.length - 100),
                  );
                  console.log(
                    "📅 FFP/FFS: Content after replacement:",
                    docJson.content.substring(docJson.content.length - 100),
                  );
                } else {
                  console.log(
                    "📅 FFP/FFS: Content field is missing or not a string, skipping content replacement",
                  );
                }

                // Convert back to Blob for upload
                const updatedDocBlob = new Blob([JSON.stringify(docJson)], {
                  type: "application/json",
                });

                // Upload the updated content to the new location with upsert
                const { error: uploadError } = await supabase.storage
                  .from(bucketName_files)
                  .upload(filePath, updatedDocBlob, {
                    upsert: true,
                    contentType: "application/json",
                  });

                if (uploadError) {
                  console.error(
                    "🔍 FFP/FFS: Error uploading document:",
                    uploadError,
                  );
                  throw uploadError;
                }

                console.log(
                  "✅ FFP/FFS: Successfully copied document with updated age to:",
                  filePath,
                );
                console.log(
                  "📋 FFP/FFS: Document content was loaded from visit on:",
                  lastVisitDate,
                  "with record ID:",
                  lastVisit.id,
                );
                console.log("🔍 FFP/FFS: Document content preview:", {
                  hasDelta: !!docJson.delta,
                  deltaOps: docJson.delta?.ops?.length,
                  firstOp: docJson.delta?.ops?.[0],
                  contentLength: docJson.delta?.ops?.[0]?.insert?.length || 0,
                });

                // Check if document content is empty or minimal
                const isDocumentEmpty = () => {
                  if (!docJson.delta || !docJson.delta.ops) return true;

                  // Get all text content
                  let totalTextLength = 0;
                  docJson.delta.ops.forEach((op) => {
                    if (op.insert && typeof op.insert === "string") {
                      // Remove whitespace and newlines for length check
                      const text = op.insert.replace(/\s+/g, " ").trim();
                      totalTextLength += text.length;
                    }
                  });

                  // Consider document empty if less than 50 characters of actual content
                  // (excluding just date headers like "12/20/2025:")
                  return totalTextLength < 50;
                };

                const isEmpty = isDocumentEmpty();

                if (isEmpty) {
                  console.warn(
                    "⚠️ FFP/FFS: Document content is empty or minimal, prompting user for template",
                  );
                  setIsWaitingForTemplateDecision(true);
                  setIsTemplatePromptOpen(true);
                  // Clear loading + latch so the startup overlay and pointer-events block do not
                  // stick until refresh (template modal still gates next steps).
                  setDocumentLoaded(true);
                  setContentLoadingComplete(true);
                  setIsLoadingDocument(false);
                  {
                    const allowEdit =
                      userRole != null &&
                      (!isInGroup(userRole, 'tenantSchema') ||
                        tenantShareAllowsDocumentEdit(tenantRowAccess));
                    quillRefs.current.forEach((q) => {
                      if (q && typeof q.enable === "function") q.enable(allowEdit);
                    });
                  }
                  // Don't load empty content yet - wait for user decision
                  return;
                }

                // Load the copied document into the editor
                editor.setContents(docJson.delta, "api");
                setDocumentLoaded(true);
                setIsLoadingDocument(false);
                // Enable editor after content is loaded
                quillRefs.current.forEach((quill) => {
                  if (quill) {
                    quill.enable(true);
                  }
                });

                // CRITICAL: Mark content loading as complete so WebSocket can connect
                // Add a small delay to ensure editor content is fully rendered
                setTimeout(() => {
                  setContentLoadingComplete(true);
                  console.log(
                    "✅ FFP/FFS: Content loading marked as complete, WebSocket can now connect",
                  );
                }, 300);

                console.log(
                  "🔍 FFP/FFS: Document loaded into editor, generating sections...",
                );

                // Generate sections from the copied content
                const lines = editor.getLines();
                const newSections = [];

                lines.forEach((line) => {
                  const text = line.domNode?.innerText.trim();
                  const format = editor.getFormat(editor.getIndex(line));

                  if (format.header) {
                    const sectionId = `section-${newSections.length}`;
                    newSections.push({
                      id: sectionId,
                      title: text,
                      level: format.header,
                      isEditing: false,
                      createdAt: new Date().toISOString(),
                      lastupdated: new Date().toISOString(),
                    });
                  }
                });

                console.log(
                  "🔍 FFP/FFS: Generated sections from copied content:",
                  newSections,
                );
                setSections(newSections);

                // Save the new document immediately
                setTimeout(() => {
                  console.log("🔍 FFP/FFS: Saving copied document...");
                  handleSaveDoc();
                }, 500);

                console.log(
                  "🔍 FFP/FFS: Returning early to prevent template application",
                );
                // Return early to prevent template application
                return;
              }
            } else {
              console.log("🔍 FFP/FFS: No previous visits found for patient");
              console.log(
                "⚠️ FFP/FFS: Cannot create FFP document without previous visit - prompting for template",
              );
              setIsWaitingForTemplateDecision(true);
              setIsTemplatePromptOpen(true);
              setIsLoadingDocument(false);
              // Don't initialize empty content yet - wait for user decision
              return;
            }
          } catch (error) {
            console.error(
              "🔍 FFP/FFS: Error copying last visit document:",
              error,
            );
            console.log(
              "⚠️ FFP/FFS: Cannot copy previous visit - prompting for template",
            );
            setIsWaitingForTemplateDecision(true);
            setIsTemplatePromptOpen(true);
            setIsLoadingDocument(false);
            // Don't initialize empty content yet - wait for user decision
            return;
          }
        }

        // Use template if not FFP/FFS (FFP/FFS should never reach here)
        console.log(
          "🔍 Template fallback: Available templates:",
          Object.keys(documentTemplates),
        );
        console.log("🔍 Template fallback: Looking for template:", patientType);
        console.log(
          "🔍 Template fallback: Template exists:",
          !!documentTemplates[patientType],
        );
        console.log(
          "🔍 Template fallback: Template data:",
          documentTemplates[patientType],
        );
        console.log("🔍 Template fallback: All templates:", documentTemplates);

        // Safety check: FFP/FFS should never reach template fallback (unless createNew converted them to NP/NS)
        if (
          (correctedType === "FFP" || correctedType === "FFS") &&
          !createNew
        ) {
          console.error(
            "❌ CRITICAL ERROR: FFP/FFS patient reached template fallback - this should never happen!",
          );
          setIsLoadingDocument(false);
          quillRefs.current.forEach((quill) => {
            if (quill) {
              quill.enable(true);
            }
          });
          toast({
            title: "Critical Error",
            description: `FFP/FFS patient ${extractedName} reached template fallback. This indicates a bug in the FFP logic.`,
            status: "error",
            duration: 10000,
            isClosable: true,
          });
          return; // Exit early - don't load any template
        }

        if (documentTemplates[patientType]) {
          // Get template data
          const templateData = documentTemplates[patientType];

          console.log(
            "🔍 Template fallback: Applying template for patient type:",
            patientType,
          );
          console.log("🔍 Template fallback: Template data structure:", {
            hasDelta: !!templateData.delta,
            hasContent: !!templateData.content,
            deltaOps: templateData.delta?.ops?.length,
            contentOps: templateData.content?.ops?.length,
          });

          // Handle both template formats (delta.ops and content.ops)
          let deltaContent;
          if (templateData.delta && templateData.delta.ops) {
            // New format with delta field
            deltaContent = JSON.parse(JSON.stringify(templateData.delta)); // Deep clone
            console.log("🔍 Using delta format template");
          } else if (templateData.content && templateData.content.ops) {
            // Old format with content.ops
            deltaContent = JSON.parse(JSON.stringify(templateData.content)); // Deep clone
            console.log("🔍 Using content format template");
          }

          if (deltaContent && deltaContent.ops) {
            // Get patient DOB for age calculation - handle case where patientData might not be loaded yet
            let calculatedAge = "";

            try {
              // Always fetch DOB from database to avoid patientData timing issues
              console.log(
                "🔍 Age calculation: Fetching DOB from database for patient:",
                extractedName,
              );
              const supabase = await getSupabase();
              const { data: patientRecord, error: fetchError } = await supabase
                .from(tableName)
                .select("dob")
                .eq(prompts[0][0], extractedName)
                .order("created_at", { ascending: false })
                .limit(1)
                .single();

              console.log("🔍 Age calculation: Database query result:", {
                patientRecord,
                fetchError,
              });

              if (patientRecord && patientRecord.dob) {
                console.log(
                  "🔍 Age calculation: Using database DOB:",
                  patientRecord.dob,
                );
                const age = calculateAge(patientRecord.dob, effectiveDate);
                calculatedAge = age ? age.toString() : "___";
                console.log(
                  "🔍 Age calculation: Age as of",
                  effectiveDate,
                  ":",
                  calculatedAge,
                );
              } else {
                console.log("🔍 Age calculation: No DOB found in database");
                calculatedAge = "___";
              }
            } catch (error) {
              console.warn(
                "Could not fetch patient DOB for age calculation:",
                error,
              );
              calculatedAge = "___"; // Fallback to placeholder
            }

            // Replace placeholders in all operations
            deltaContent.ops.forEach((op, index) => {
              if (op.insert && typeof op.insert === "string") {
                // Use patient scheduled time if available, otherwise navbar time for document content
                const patientTime =
                  patientScheduledTime ||
                  navbarTime ||
                  new Date().toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  });

                // Use EHR_ID (id) for document headings, not schedule ID (validId)
                // Use the same logic as generateDocFilename: EHR_ID first, then fallback
                const ehrIdForHeading = id || ""; // id is EHR_ID from location.state
                op.insert = op.insert
                  .replace(/\[NAME\]/g, extractedName || "")
                  .replace(/\[ID\]/g, ehrIdForHeading)
                  .replace(/\[Patient Name\]/g, extractedName || "")
                  .replace(/\[MRN\]/g, ehrIdForHeading)
                  .replace(/\[time\]/g, patientTime)
                  .replace(/\[age\]/g, calculatedAge)
                  .replace(/\[date\]/g, effectiveDate)
                  // Custom date format for M/D/YYYY
                  .replace(
                    /\[date_mdy\]/g,
                    (() => {
                      const date = new Date(effectiveDate);
                      const month = date.getMonth() + 1;
                      const day = date.getDate();
                      const year = date.getFullYear();
                      return `${month}/${day}/${year}`;
                    })(),
                  )
                  // Replace [MM/DD/YYYY] with formatted date (parse directly to avoid timezone issues)
                  .replace(
                    /\[MM\/DD\/YYYY\]/g,
                    (() => {
                      const [year, month, day] = effectiveDate.split("-");
                      return `${String(parseInt(month)).padStart(2, "0")}/${String(parseInt(day)).padStart(2, "0")}/${year}`;
                    })(),
                  )
                  // Replace age patterns in HPI section
                  .replace(/___ year old/g, `${calculatedAge} year old`)
                  .replace(/\b___\b(?=\s*year\s*old)/g, calculatedAge);
              }
            });

            editor.setContents(deltaContent, "api");
            console.log("Applied template for:", patientType);
            setLoadedTemplateType(patientType);
            setDocumentLoaded(true);
            setIsLoadingDocument(false);
            // Enable editor after content is loaded
            quillRefs.current.forEach((quill) => {
              if (quill) {
                quill.enable(true);
              }
            });

            // Generate sections from the template
            const lines = editor.getLines();
            const newSections = [];

            // Get current user for template sections
            const currentUser = await getCurrentUser();

            lines.forEach((line) => {
              const text = line.domNode?.innerText.trim();
              const format = editor.getFormat(editor.getIndex(line));

              if (format.header) {
                const sectionId = `section-${newSections.length}`;
                newSections.push({
                  id: sectionId,
                  title: text,
                  level: format.header,
                  isEditing: false,
                  createdAt: new Date().toISOString(),
                  lastupdated: new Date().toISOString(),
                  createdBy: currentUser.name,
                  lastUpdatedBy: currentUser.name,
                });
              }
            });

            console.log("Generated sections from template:", newSections);
            setSections(newSections);

            // Mark content loading as complete for template-based documents
            setContentLoadingComplete(true);
            console.log(
              "✅ Content loading marked as complete for template document",
            );

            // Save the new template content immediately
            setTimeout(() => {
              handleSaveDoc();
            }, 500);
          } else {
            console.log(
              "Template found but no valid content format:",
              patientType,
            );
            editor.setContents([]);
          }
        } else {
          console.log("No template found for type:", patientType);

          // For NP patients, try to load NP template directly
          if (patientType === "NP" && documentTemplates["NP"]) {
            console.log("🔧 NP patient - applying NP template directly");
            const templateData = documentTemplates["NP"];
            if (templateData.delta && templateData.delta.ops) {
              // Apply placeholder replacements for NP template
              const deltaContent = JSON.parse(
                JSON.stringify(templateData.delta),
              );

              // Get patient DOB for age calculation - handle case where patientData might not be loaded yet
              let calculatedAge = "";

              try {
                // Always fetch DOB from database to avoid patientData timing issues
                console.log(
                  "🔍 Age calculation: Fetching DOB from database for patient:",
                  extractedName,
                );
                const supabase = await getSupabase();
                const { data: patientRecord, error: fetchError } =
                  await supabase
                    .from(tableName)
                    .select("dob")
                    .eq(prompts[0][0], extractedName)
                    .order("created_at", { ascending: false })
                    .limit(1)
                    .single();

                console.log("🔍 Age calculation: Database query result:", {
                  patientRecord,
                  fetchError,
                });

                if (patientRecord && patientRecord.dob) {
                  console.log(
                    "🔍 Age calculation: Using database DOB:",
                    patientRecord.dob,
                  );
                  calculatedAge = calculateAge(
                    patientRecord.dob,
                    effectiveDate,
                  );
                  console.log(
                    "🔍 Age calculation: Age as of",
                    effectiveDate,
                    ":",
                    calculatedAge,
                  );
                } else {
                  console.log("🔍 Age calculation: No DOB found in database");
                }
              } catch (error) {
                console.warn(
                  "Could not fetch patient DOB for age calculation:",
                  error,
                );
                calculatedAge = ""; // Fallback to empty string
              }

              // If no age could be calculated, use a placeholder
              if (!calculatedAge) {
                calculatedAge = "___";
                console.log(
                  "🔍 Age calculation: Using placeholder ___ for missing age",
                );
              }

              // Replace placeholders in all operations
              if (deltaContent.ops) {
                deltaContent.ops.forEach((op, index) => {
                  if (op.insert && typeof op.insert === "string") {
                    op.insert = op.insert
                      .replace(/\[NAME\]/g, extractedName || "")
                      .replace(/\[ID\]/g, id || "")
                      .replace(/\[Patient Name\]/g, extractedName || "")
                      .replace(/\[MRN\]/g, id || "")
                      .replace(/\[time\]/g, effectiveTime || "")
                      .replace(/\[age\]/g, calculatedAge)
                      .replace(/\[date\]/g, effectiveDate);
                    // Replace the title line using generateDocFilename so type, time, and ID always match the navbar title
                    op.insert = op.insert.replace(
                      /^\d{1,2}:\d{2}\s[^\n]+-\s\S+\s\S+$/m,
                      displayTitle,
                    );
                    op.insert = op.insert
                      // Replace [MM/DD/YYYY] with formatted date (parse directly to avoid timezone issues)
                      .replace(
                        /\[MM\/DD\/YYYY\]/g,
                        (() => {
                          const [year, month, day] = effectiveDate.split("-");
                          return `${String(parseInt(month)).padStart(2, "0")}/${String(parseInt(day)).padStart(2, "0")}/${year}`;
                        })(),
                      );
                  }
                });
              }
              editor.setContents(deltaContent, "api");
              console.log("Applied NP template for NP patient");
              setLoadedTemplateType("NP");
              setDocumentLoaded(true);
              setIsLoadingDocument(false);
              // Enable editor after content is loaded
              quillRefs.current.forEach((quill) => {
                if (quill) {
                  quill.enable(true);
                }
              });

              // Save the new template content immediately
              setTimeout(() => {
                handleSaveDoc();
              }, 500);
            } else {
              console.log("NP template found but no valid content format");
              editor.setContents([]);
            }
          } else if (documentTemplates["NP"]) {
            console.log("🔧 Falling back to NP template");
            const templateData = documentTemplates["NP"];
            if (templateData.delta && templateData.delta.ops) {
              // Apply placeholder replacements for NP template fallback
              const deltaContent = JSON.parse(
                JSON.stringify(templateData.delta),
              );

              // Get patient DOB for age calculation - handle case where patientData might not be loaded yet
              let calculatedAge = "";

              try {
                // Always fetch DOB from database to avoid patientData timing issues
                console.log(
                  "🔍 Age calculation: Fetching DOB from database for patient:",
                  extractedName,
                );
                const supabase = await getSupabase();
                const { data: patientRecord, error: fetchError } =
                  await supabase
                    .from(tableName)
                    .select("dob")
                    .eq(prompts[0][0], extractedName)
                    .order("created_at", { ascending: false })
                    .limit(1)
                    .single();

                console.log("🔍 Age calculation: Database query result:", {
                  patientRecord,
                  fetchError,
                });

                if (patientRecord && patientRecord.dob) {
                  console.log(
                    "🔍 Age calculation: Using database DOB:",
                    patientRecord.dob,
                  );
                  calculatedAge = calculateAge(
                    patientRecord.dob,
                    effectiveDate,
                  );
                  console.log(
                    "🔍 Age calculation: Age as of",
                    effectiveDate,
                    ":",
                    calculatedAge,
                  );
                } else {
                  console.log("🔍 Age calculation: No DOB found in database");
                }
              } catch (error) {
                console.warn(
                  "Could not fetch patient DOB for age calculation:",
                  error,
                );
                calculatedAge = ""; // Fallback to empty string
              }

              // If no age could be calculated, use a placeholder
              if (!calculatedAge) {
                calculatedAge = "___";
                console.log(
                  "🔍 Age calculation: Using placeholder ___ for missing age",
                );
              }

              // Replace placeholders in all operations
              if (deltaContent.ops) {
                deltaContent.ops.forEach((op, index) => {
                  if (op.insert && typeof op.insert === "string") {
                    op.insert = op.insert
                      .replace(/\[NAME\]/g, extractedName || "")
                      .replace(/\[ID\]/g, id || "")
                      .replace(/\[Patient Name\]/g, extractedName || "")
                      .replace(/\[MRN\]/g, id || "")
                      .replace(/\[time\]/g, effectiveTime || "")
                      .replace(/\[age\]/g, calculatedAge)
                      .replace(/\[date\]/g, effectiveDate);
                    // Replace the title line using generateDocFilename so type, time, and ID always match the navbar title
                    op.insert = op.insert.replace(
                      /^\d{1,2}:\d{2}\s[^\n]+-\s\S+\s\S+$/m,
                      displayTitle,
                    );
                    op.insert = op.insert
                      // Replace [MM/DD/YYYY] with formatted date (parse directly to avoid timezone issues)
                      .replace(
                        /\[MM\/DD\/YYYY\]/g,
                        (() => {
                          const [year, month, day] = effectiveDate.split("-");
                          return `${String(parseInt(month)).padStart(2, "0")}/${String(parseInt(day)).padStart(2, "0")}/${year}`;
                        })(),
                      );
                  }
                });
              }
              editor.setContents(deltaContent, "api");
              console.log("Applied NP template as fallback");
              setLoadedTemplateType("NP");
              setDocumentLoaded(true);
              setIsLoadingDocument(false);
              // Enable editor after content is loaded
              quillRefs.current.forEach((quill) => {
                if (quill) {
                  quill.enable(true);
                }
              });

              // Save the new template content immediately
              setTimeout(() => {
                handleSaveDoc();
              }, 500);
            } else {
              console.log("NP template found but no valid content format");
              editor.setContents([]);
            }
          } else {
            console.log("No NP template available as fallback");
            editor.setContents([]);
          }
        }
      } else {
        // Load existing content
        const fileContent = await storageData.text();
        const parsedData = JSON.parse(fileContent);
        if (editor && parsedData.delta) {
          console.log("Loading stored content");
          editor.setContents(parsedData.delta, "api");
          setDocumentLoaded(true);
          setIsLoadingDocument(false);
          // Enable editor after content is loaded
          quillRefs.current.forEach((quill) => {
            if (quill) {
              quill.enable(true);
            }
          });

          // Use the same comprehensive section scanning logic as scanForSections
          console.log(
            "🔍 Running comprehensive section scan after content load...",
          );
          setTimeout(() => scanForSections(), 100);

          // Additional immediate scan after a short delay to ensure DOM is ready
          setTimeout(() => {
            console.log(
              "🔍 Running immediate section scan after DOM update...",
            );
            scanForSections();
          }, 300);
        }
      } // Close the if (shouldUseTemplate) block

      if (!shouldUseTemplate) {
        // Load existing content
        const fileContent = await storageData.text();
        const parsedData = JSON.parse(fileContent);
        if (editor && parsedData.delta) {
          console.log("Loading stored content");
          editor.setContents(parsedData.delta, "api");
          setDocumentLoaded(true);
          setIsLoadingDocument(false);
          // Enable editor after content is loaded
          quillRefs.current.forEach((quill) => {
            if (quill) {
              quill.enable(true);
            }
          });

          // Use the same comprehensive section scanning logic as scanForSections
          console.log(
            "🔍 Running comprehensive section scan after content load...",
          );
          setTimeout(() => scanForSections(), 100);

          // Additional immediate scan after a short delay to ensure DOM is ready
          setTimeout(() => {
            console.log(
              "🔍 Running immediate section scan after DOM update...",
            );
            scanForSections();
          }, 300);
        }
      }

      // Get patient time
      try {
        const { data: patientData, error } = await supabase
          .from(tableName)
          .select("time_")
          .eq(colid, validId)
          .eq("date_", effectiveDate)
          .maybeSingle();

        if (error) {
          console.warn("⚠️ Could not fetch patient time:", error.message);
          // For FFP patients, validId might be a numeric ID, not an email
          // Try alternative query if the first one fails
          if (validId && !validId.includes("@")) {
            console.log("🔄 Trying alternative query for numeric ID:", validId);
            const { data: altPatientData } = await supabase
              .from(tableName)
              .select("time_")
              .eq("id", validId)
              .eq("date_", effectiveDate)
              .maybeSingle();

            if (altPatientData?.time_) {
              setTime(altPatientData.time_);
            }
          }
        } else if (patientData?.time_) {
          setTime(patientData.time_);
        }
      } catch (error) {
        console.warn("⚠️ Error fetching patient time:", error);
      }
    } catch (err) {
      const message = err?.message || String(err);
      console.error("Error fetching document:", message, err);
      if (toast) {
        toast({
          title: "Error loading document",
          description: message.includes("replace")
            ? "Document path was not ready yet. If you opened a share link, try refreshing once."
            : message,
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      }
      setIsLoadingDocument(false);
      quillRefs.current.forEach((quill) => {
        if (quill) quill.enable(true);
      });
    }
  };

  // **Effects**

  useEffect(() => {
    if (joinDocId && !filePath) return;
    if (filePath) fetchDocument(filePath);
    fetchNames(effectiveDate, tableName, setPatients);
  }, [id, filePath, joinDocId, documentTypeDefaultReadyKey]);

  useEffect(() => {
    console.log(
      `🔧 [pages effect] firing — pages.length=${pages.length}, quillRefs[0]=${!!quillRefs.current[0]}`,
    );
    pages.forEach((_, index) => {
      initializeQuill(index);
    });

    // Scan for sections after editors are initialized with content stability check
    // This prevents premature scanning before content is fully loaded
    checkContentStability(() => scanForSections(), 1500);

    // Additional fallback: scan sections after a longer delay to ensure they appear on page refresh
    setTimeout(() => {
      if (sections.length === 0) {
        console.log(
          "🔍 Fallback section scan - no sections found, scanning again...",
        );
        scanForSections();
      }
    }, 3000);
  }, [pages]);

  // Handle pasting AI-generated content
  useEffect(() => {
    if (
      pasteAIGeneratedContent &&
      aiGeneratedContent &&
      !hasPastedAIContentRef.current &&
      editorRef.current &&
      documentLoaded &&
      contentLoadingComplete
    ) {
      const editor = editorRef.current.getEditor(0);
      if (editor) {
        console.log("🔍 Pasting AI-generated content into editor");
        hasPastedAIContentRef.current = true; // Mark as pasted to prevent re-pasting

        // Convert plain text to Quill delta format
        // Quill delta format: each line becomes an insert operation
        const lines = aiGeneratedContent.split("\n");
        const deltaOps = [];

        lines.forEach((line, index) => {
          deltaOps.push({ insert: line });
          // Add newline after each line except the last one
          if (index < lines.length - 1) {
            deltaOps.push({ insert: "\n" });
          }
        });

        const delta = { ops: deltaOps };

        // Set the content (this will replace existing content)
        editor.setContents(delta, "api");

        // Trigger save after a short delay
        setTimeout(() => {
          if (handleSaveDoc) {
            handleSaveDoc();
          }
        }, 500);

        // Show success message
        toast({
          title: "Content Pasted",
          description:
            "AI-generated content from forms has been pasted into the document",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  }, [
    pasteAIGeneratedContent,
    aiGeneratedContent,
    documentLoaded,
    contentLoadingComplete,
    toast,
  ]);

  // Loading: disable editor. Tenant viewer (read-only): quill.enable(false) still allows select/copy.
  // userRole == null means we haven't fetched the role yet — keep the editor
  // disabled in that window so a viewer who refreshes the page doesn't get a
  // brief edit window before view-only permissions kick in.
  useEffect(() => {
    const allowEdit =
      userRole != null &&
      (!isInGroup(userRole, 'tenantSchema') ||
        tenantShareAllowsDocumentEdit(tenantRowAccess));
    const enabled = !isLoadingDocument && allowEdit && !safeShareMode;
    console.log("🔐 [QuillEnable]", enabled, {
      isLoading: isLoadingDocument,
      allowEdit,
      isTenant: isInGroup(userRole, 'tenantSchema'),
      userRole,
      tenantLevel: tenantRowAccess?.level,
      quillCount: quillRefs.current.filter(Boolean).length,
    });
    const apply = () => {
      quillRefs.current.forEach((quill) => {
        if (quill && typeof quill.enable === "function") {
          quill.enable(enabled);
        }
      });
    };
    apply();
    // Quill instances can mount after this effect runs, and QuillBinding / other
    // collaboration setup can toggle contenteditable after our apply runs. Retry
    // over a wider window so late-arriving Quills and post-init overrides get
    // resynced. Also set up a MutationObserver that watches ql-editor's
    // contenteditable attribute and corrects drift whenever the desired state is
    // "enabled" — this is the failsafe for the "badge says Editor but can't type"
    // case where something downstream disables Quill after we enabled it.
    const raf = requestAnimationFrame(apply);
    const timers = [0, 100, 250, 500, 1000, 2000, 4000].map((ms) =>
      setTimeout(apply, ms),
    );
    const observers = [];
    if (enabled) {
      quillRefs.current.forEach((quill) => {
        if (!quill || !quill.root) return;
        // Cap re-enable attempts to avoid a feedback loop if some other code
        // keeps forcing contenteditable back to false.
        let fixes = 0;
        const observer = new MutationObserver(() => {
          if (fixes >= 5) return;
          if (quill.root.getAttribute("contenteditable") === "false") {
            fixes += 1;
            console.log(
              "🔐 [QuillSync] contenteditable drifted to false — re-enabling (attempt",
              fixes,
              ")",
            );
            try {
              quill.enable(true);
            } catch (err) {
              console.warn("🔐 [QuillSync] re-enable failed:", err);
            }
          }
        });
        observer.observe(quill.root, {
          attributes: true,
          attributeFilter: ["contenteditable"],
        });
        observers.push(observer);
      });
    } else {
      // Failsafe for viewers/load-window: document fetch handlers call
      // quill.enable(true) on completion paths (e.g. fetchDocument success,
      // template apply). When the viewer's role/access hasn't resolved yet,
      // those callbacks would otherwise grant a brief edit window. Watch for
      // drift to contenteditable="true" and snap back.
      quillRefs.current.forEach((quill) => {
        if (!quill || !quill.root) return;
        let fixes = 0;
        const observer = new MutationObserver(() => {
          if (fixes >= 10) return;
          if (quill.root.getAttribute("contenteditable") === "true") {
            fixes += 1;
            try {
              quill.enable(false);
            } catch (err) {
              console.warn("🔐 [QuillSync] re-disable failed:", err);
            }
          }
        });
        observer.observe(quill.root, {
          attributes: true,
          attributeFilter: ["contenteditable"],
        });
        observers.push(observer);
      });
    }
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      observers.forEach((o) => o.disconnect());
    };
  }, [
    isLoadingDocument,
    userRole,
    tenantRowAccess,
    pages.length,
    documentLoaded,
    contentLoadingComplete,
    safeShareMode,
  ]);

  useEffect(() => {
    console.log("Updated document ID:", documentId);
  }, [documentId]);

  // Reset documentLoaded when filePath changes
  useEffect(() => {
    setHasRenderedInitialDocument(false);
    setDocumentLoaded(false);
    setIsLoadingDocument(true); // Set loading state when filePath changes
    setContentLoadingComplete(false); // Reset content loading state
    contentLoadingLock.current = false; // Reset content loading lock
    websocketSetupLock.current = false; // Reset WebSocket setup lock
    delayedScanExecuted.current = false; // Reset delayed scan flag
    contentLoadAttempts.current = 0; // Reset content load attempts
    skipLargeStorageJsonFetchRef.current = !!skipLargeStorageJsonFetch;
    forceOversizeNewDocumentRef.current = false;
    setStorageOversizeModalOpen(false);
    setStorageOversizeMessage("");
    // Disable editors during loading
    quillRefs.current.forEach((quill) => {
      if (quill) {
        quill.enable(false);
      }
    });
    // Clear content stability check timeout
    if (contentStabilityCheck.current) {
      clearTimeout(contentStabilityCheck.current);
      contentStabilityCheck.current = null;
    }
  }, [filePath, skipLargeStorageJsonFetch]);

  // Latch the first fully ready state so startup loading does not flash/unflash.
  useEffect(() => {
    if (
      !hasRenderedInitialDocument &&
      documentLoaded &&
      contentLoadingComplete &&
      !isLoadingDocument
    ) {
      setHasRenderedInitialDocument(true);
    }
  }, [
    hasRenderedInitialDocument,
    documentLoaded,
    contentLoadingComplete,
    isLoadingDocument,
  ]);

  // Save document when WebSocket connection is lost (but only for real disconnections)
  useEffect(() => {
    if (!isConnected && connectionStatus === "disconnected") {
      const editor = editorRef.current?.getEditor(0);
      if (editor && editor.getLength() > 1) {
        console.group("💾 Auto-Save Triggered by Disconnection");
        console.log("Time:", new Date().toISOString());
        console.log("Reason: WebSocket connection lost");
        console.log("Document:", filePath);
        console.log("Content Length:", editor.getLength());
        console.log("Action: Saving document locally");
        console.groupEnd();
        handleSaveDoc();
      }
    }
  }, [isConnected, connectionStatus]);

  // Add cleanup effect
  useEffect(() => {
    return () => {
      setIsUnmounting(true);
      isMountedRef.current = false; // Mark component as unmounted

      if (typeof quillImageDomCleanupRef.current === "function") {
        try {
          quillImageDomCleanupRef.current();
        } catch (e) {
          console.warn("Error removing Quill image DOM listeners:", e);
        }
        quillImageDomCleanupRef.current = null;
      }

      // Remove all text-change event listeners
      textChangeHandlersRef.current.forEach(({ quill, handler, type }) => {
        try {
          if (quill && typeof quill.off === "function") {
            quill.off("text-change", handler);
          }
        } catch (error) {
          console.warn(`Error removing ${type} handler:`, error);
        }
      });
      textChangeHandlersRef.current = [];

      // Clear content stability check timeout
      if (contentStabilityCheck.current) {
        clearTimeout(contentStabilityCheck.current);
      }

      // Save one last time if editor exists
      try {
        if (editorRef.current && editorRef.current.getEditor) {
          const editor = editorRef.current.getEditor(0);
          if (editor) {
            handleSaveDoc();
          }
        }
      } catch (error) {
        console.warn("Error during final save on unmount:", error);
      }
    };
  }, []);

  // Initialize checkbox settings based on live transcription setting
  useEffect(() => {
    if (validId && !userManuallyChangedAutoSave) {
      const liveTranscriptionSetting = getLiveTranscriptionSetting(validId);
      const savedAutoSaveSetting = getAutoSaveSetting(validId);

      console.log("📝 Med-A Docs: Checkbox initialization:", {
        validId,
        liveTranscriptionSetting,
        savedAutoSaveSetting,
        userManuallyChangedAutoSave,
      });

      // Set all checkboxes to the live transcription setting
      setLiveTranscriptionEnabled(liveTranscriptionSetting);
      setLiveFactExtractionEnabled(liveTranscriptionSetting);

      // Only set auto-save if it hasn't been explicitly set by the user
      if (savedAutoSaveSetting === null) {
        setAutoSaveSettingState(liveTranscriptionSetting);
        if (validId && liveTranscriptionSetting !== undefined) {
          setAutoSaveSetting(validId, liveTranscriptionSetting);
        }
      } else {
        setAutoSaveSettingState(savedAutoSaveSetting);
        if (validId && savedAutoSaveSetting !== undefined) {
          setAutoSaveSetting(validId, savedAutoSaveSetting);
        }
      }
    }
  }, [validId, userManuallyChangedAutoSave]); // Only run when validId changes or user hasn't manually changed auto-save

  // Initialize versioning system when document is loaded (used for version history + vN / Out of date UI)
  useEffect(() => {
    if (!filePath || !documentLoaded) return;
    if (versioningInstanceRef.current) return;
    console.log("🔄 Initializing versioning system for document:", filePath);
    const vs = createVersioningSystem(filePath, editorRef);
    versioningInstanceRef.current = vs;
    setVersioningSystem(vs);
    const setupTimer = setTimeout(async () => {
      vs.setupEventListeners();
      await vs.setCurrentUser();
    }, 1000);
    return () => {
      clearTimeout(setupTimer);
      vs.destroy();
      versioningInstanceRef.current = null;
    };
  }, [filePath, documentLoaded]);

  // Schedule Pre/Post appointment versions on the day of the appointment.
  // Runs once per document open. Only fires when an appointment date+time is known.
  // The backend worker fires the actual version at the configured offset before/after.
  useEffect(() => {
    if (!filePath || !documentLoaded || !versioningSystem) return;
    if (!date_ || !time_) return; // not an appointment-based document open

    const scheduleAppointmentVersions = async () => {
      try {
        const supabase = await getSupabase();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) return;

        let userName = session.user.email?.split("@")[0] || "";
        try {
          const { data: ud } = await getSupabaseAdmin()
            .from("users")
            .select("name")
            .eq("id", session.user.id)
            .single();
          if (ud?.name) userName = ud.name;
        } catch {
          /* use email fallback */
        }

        // Determine label prefix based on role
        let labelType = "Clinic";
        if (normalizeRole(userRole) === "student") {
          labelType = "Class";
        } else if (
          normalizeRole(userRole) === "socialworker" ||
          normalizeRole(userRole) === "other"
        ) {
          labelType = "Meeting";
        }

        const backendUrl = getBackendURL();
        const res = await fetch(`${backendUrl}/api/versioning/schedule/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            file_path: filePath,
            appointment_date: date_,
            appointment_time: navbarTime || time_,
            schema: supabaseSchema,
            user_id: session.user.id,
            user_name: userName,
            user_email: session.user.email || "",
            pre_label: `Pre-${labelType}`,
            post_label: `Post-${labelType}`,
          }),
        });
        if (!res.ok) {
          console.warn(
            "[versioning] Failed to schedule appointment versions:",
            res.status,
          );
        }
      } catch (e) {
        console.warn("[versioning] Error scheduling appointment versions:", e);
      }
    };

    scheduleAppointmentVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, documentLoaded, versioningSystem]);

  // Navbar: last saved version number vs current editor text (Out of date when they differ)
  useEffect(() => {
    if (!versioningSystem || !documentLoaded) return;
    setDocumentVersionStatus({
      loading: true,
      label: null,
      isOutOfDate: false,
      isStaleVersion: false,
      savedNumber: null,
    });
    versioningSystem.setVersionDisplayListener((state) => {
      setDocumentVersionStatus(state);
    });
    const t = setTimeout(() => {
      versioningSystem.syncVersionBaselineFromServer();
    }, 1200);
    return () => {
      clearTimeout(t);
      versioningSystem.setVersionDisplayListener(null);
    };
  }, [versioningSystem, documentLoaded, filePath]);

  // Ensure text wrapping is always applied (ResizableSplitter will handle width constraints)
  useEffect(() => {
    const documentContainer =
      document.querySelector(".bg-white.min-h.max-w-full") ||
      document.querySelector(
        '[class*="bg-white"][class*="min-h"][class*="max-w-full"]',
      ) ||
      document.querySelector(".main-content-area .bg-white");

    if (!documentContainer || documentContainer.dataset.wrappingApplied) return;

    // Apply text wrapping constraints (same as ResizableSplitter) - only apply once.
    // Keep overflow visible in both axes so wheel events bubble to the scrolling
    // ancestor (.flex.flex-col.flex-1.h-full). Setting overflow-x: hidden would
    // force overflow-y from visible to auto (per CSS spec), making this element
    // capture the wheel and block user scroll.
    documentContainer.style.wordWrap = "break-word";
    documentContainer.style.overflowWrap = "break-word";
    documentContainer.style.wordBreak = "break-word";
    documentContainer.style.overflow = "visible";
    documentContainer.style.boxSizing = "border-box";
    documentContainer.dataset.wrappingApplied = "true";

    // Apply to all child elements that might contain text (only once)
    const applyToElement = (el) => {
      if (
        !el ||
        el.tagName === "SCRIPT" ||
        el.tagName === "STYLE" ||
        el.dataset.wrappingApplied
      )
        return;

      el.style.maxWidth = "100%";
      el.style.boxSizing = "border-box";

      const isTextContainer =
        el.classList.contains("ql-editor") ||
        el.classList.contains("ql-container") ||
        el.classList.contains("document-page") ||
        el.classList.contains("viewable-doc-container") ||
        el.id === "viewable-doc" ||
        el.hasAttribute("contenteditable") ||
        el.tagName === "P" ||
        el.tagName === "DIV" ||
        el.tagName === "SPAN";

      if (isTextContainer) {
        el.style.width = "100%";
        // Do NOT set overflow-x: hidden here — combined with default overflow-y:
        // visible, CSS converts overflow-y to auto, turning each text container
        // into a wheel-event trap that blocks scroll chaining to the outer
        // .flex.flex-col.flex-1.h-full scroll container. word-break +
        // overflow-wrap + max-width already prevent horizontal text overflow.
        el.style.wordWrap = "break-word";
        el.style.overflowWrap = "break-word";
        el.style.wordBreak = "break-word";
        if (el.style.whiteSpace === "pre" || el.style.whiteSpace === "nowrap") {
          el.style.whiteSpace = "pre-wrap";
        }
      }

      el.dataset.wrappingApplied = "true";

      if (el.children && el.children.length > 0 && el.children.length < 100) {
        Array.from(el.children).forEach((child) => applyToElement(child));
      }
    };

    // Apply to known containers
    const knownContainers = [
      documentContainer.querySelector(".viewable-doc-container"),
      documentContainer.querySelector(".document-page"),
      documentContainer.querySelector(".ql-editor"),
      documentContainer.querySelector(".ql-container"),
      documentContainer.querySelector("#viewable-doc"),
      documentContainer.querySelector('[class*="ql-editor"]'),
      documentContainer.querySelector('[class*="document-page"]'),
    ].filter(Boolean);

    knownContainers.forEach(applyToElement);
    Array.from(documentContainer.children).forEach((child) => {
      if (!knownContainers.includes(child)) {
        applyToElement(child);
      }
    });
  }, []); // Only run once on mount

  // Cleanup versioning system on unmount
  useEffect(() => {
    return () => {
      if (versioningSystem) {
        console.log("🧹 Cleaning up versioning system on unmount");
        versioningSystem.destroy();
        versioningInstanceRef.current = null;
      }
    };
  }, [versioningSystem]); // Include versioningSystem in dependencies to capture current value

  // **Render**

  return (
    <div className="flex flex-col bg-gray-100">
      {!hasRenderedInitialDocument && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #E2E8F0",
                borderTop: "4px solid #4A5568",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 16px",
              }}
            />
            <div
              style={{
                fontSize: "16px",
                color: "#4A5568",
                fontWeight: "500",
              }}
            >
              Loading...
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "#A0AEC0",
                marginTop: "6px",
              }}
            >
              This may take a moment for larger documents or if your connection
              is slow.
            </div>
          </div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
      {/* Navbar */}
      <div
        className="document-navbar"
        style={{ visibility: hasRenderedInitialDocument ? "visible" : "hidden" }}
      >
        <DocumentNavBar
          documentTitle={displayTitle}
          path={filePath}
          editorRef={editorRef}
          userInfo={userInfo}
          onUserAvatarClick={scrollToUserCursor}
          userColors={userColors}
          currentPatientId={actualPatientId || validId}
          patientName={extractedName}
          scheduleDate={effectiveDate}
          scheduleTime={effectiveTime}
          documentReadOnly={tenantDocReadOnly}
          shareRoleLevel={
            isInGroup(userRole, 'tenantSchema') ? tenantRowAccess?.level : null
          }
          onSaveDoc={handleSaveDoc}
          appointmentVersionInfo={appointmentVersionInfo}
          documentVersionStatus={documentVersionStatus}
          onCreateVersion={
            versioningSystem
              ? (name) =>
                  versioningSystem.createVersion(
                    name,
                    "MANUALLY CREATED BY USER",
                  )
              : null
          }
          versioningSystem={versioningSystem}
        />
        <div
          className="toolbar-container"
          style={{
            pointerEvents: isLoadingDocument ? "none" : "auto",
            opacity: isLoadingDocument ? 0.5 : 1,
            alignItems: "flex-start",
          }}
        >
          <div style={{ flex: "1 1 auto", minWidth: 0 }}>
            <TextEditor
              ref={editorRef}
              showToolbarOnly={true}
              quillRefs={quillRefs}
              containerRefs={containerRefs}
              pages={pages}
              onVoiceTypingResult={handleVoiceTypingResult}
              applyFormatToMultiSelections={applyFormatToMultiSelections}
              multiSelections={multiSelections}
              voiceTypingEnabled={!tenantDocReadOnly}
              onCommentButtonClick={handleToolbarCommentClick}
              safeShareMode={safeShareMode}
              safeShareMethod={safeShareMethod}
              isDeidentifying={isDeidentifying}
              onToggleSafeShare={toggleSafeShareMode}
              onSafeShareMethodChange={setSafeShareMethod}
            />
          </div>
          <div
            className="toolbar-actions"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              flex: "1 1 340px",
              minWidth: "min(340px, 100%)",
              maxWidth: "100%",
              marginLeft: "auto",
              marginRight: "10px",
              alignItems: "stretch",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: "6px",
                width: "100%",
              }}
            >
              <Text
                fontSize="xs"
                color="gray.600"
                fontWeight="semibold"
                textAlign="right"
                width="100%"
                minWidth={0}
                lineHeight="shorter"
                noOfLines={2}
                title={`Template: ${loadedTemplateType || getTemplateTypeForCurrentDocument()}`}
              >
                Template:{" "}
                {loadedTemplateType || getTemplateTypeForCurrentDocument()}
              </Text>
              {!isMagicNotes && (
                <Menu closeOnSelect={false} placement="bottom-end">
                  <Box
                    position="relative"
                    display="inline-block"
                    flexShrink={0}
                  >
                    <MenuButton
                      as={ChakraButton}
                      size="xs"
                      colorScheme="gray"
                      rightIcon={<ChevronDownIcon boxSize={3} />}
                      isDisabled={isLoadingDocument}
                      px={2}
                    >
                      Document tools
                    </MenuButton>
                    {shareBadgeCount > 0 &&
                      userRole != null &&
                      isInGroup(userRole, 'tenantSchema') &&
                      tenantRowAccess.canShareRow && (
                        <Box
                          position="absolute"
                          top="-6px"
                          right="-6px"
                          bg="teal.500"
                          color="white"
                          borderRadius="full"
                          w="16px"
                          h="16px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="9px"
                          fontWeight="bold"
                          pointerEvents="none"
                        >
                          {shareBadgeCount}
                        </Box>
                      )}
                  </Box>
                  <MenuList zIndex={2000} minW="240px">
                    <Box
                      px={2}
                      py={2}
                      borderBottomWidth="1px"
                      borderColor="gray.100"
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <Text fontSize="xs" fontWeight="semibold" mb={1} color="gray.600">
                        Recording
                      </Text>
                      <Box
                        display="inline-flex"
                        sx={{
                          "& > div": {
                            minH: "auto !important",
                            p: "0 !important",
                            display: "flex !important",
                            alignItems: "center !important",
                          },
                          "& > div > button": {
                            size: "xs !important",
                            px: "6px !important",
                            fontSize: "11px !important",
                            minW: "0 !important",
                            h: "24px !important",
                            lineHeight: "1.2 !important",
                            whiteSpace: "nowrap !important",
                          },
                          "& button": {
                            size: "xs !important",
                            px: "6px !important",
                            fontSize: "11px !important",
                            minW: "0 !important",
                            h: "24px !important",
                            lineHeight: "1.2 !important",
                            whiteSpace: "nowrap !important",
                          },
                        }}
                      >
                        <AudioRecorder
                          state={userInfo || patientData || {}}
                          refId={actualPatientId || validId || id}
                          session={false}
                          popDeps={{
                            setShowPersistentComponent,
                            setCondition,
                            setStateData,
                            setCondFunc,
                          }}
                          override={false}
                          setSession={setSession}
                          autoSaveSetting={autoSaveSetting}
                          liveTranscriptionEnabled={liveTranscriptionEnabled}
                          onLiveTranscriptionChange={
                            handleLiveTranscriptionChange
                          }
                          liveFactExtractionEnabled={liveFactExtractionEnabled}
                          onLiveFactExtractionChange={
                            handleLiveFactExtractionChange
                          }
                          onAutoSaveChange={handleAutoSaveChange}
                          userRole={userRole}
                          compactEmbed
                        />
                      </Box>
                    </Box>
                    <MenuDivider />
                    <MenuItem
                      fontSize="sm"
                      closeOnSelect
                      onClick={togglePatientList}
                      isDisabled={isLoadingDocument}
                    >
                      Show {getLabel(userRole, 'entityPlural')}
                    </MenuItem>
                    {!isCombined && (
                      <MenuItem
                        fontSize="sm"
                        closeOnSelect
                        icon={<FontAwesomeIcon icon={faCopy} />}
                        onClick={openCopyFromOlderModal}
                        isDisabled={isLoadingDocument || tenantDocReadOnly}
                        title={
                          tenantDocReadOnly
                            ? "Not available with viewer (read-only) access"
                            : "Copy from a previous date"
                        }
                      >
                        Copy older
                      </MenuItem>
                    )}
                    <Tooltip
                      label="Read-only while viewing as another user"
                      isDisabled={!isImpersonating()}
                      hasArrow
                    >
                      <MenuItem
                        fontSize="sm"
                        closeOnSelect
                        onClick={() => {
                          if (tenantDocReadOnly) return;
                          handleRemoveDuplicates();
                        }}
                        isDisabled={
                          isLoadingDocument ||
                          tenantDocReadOnly ||
                          isImpersonating()
                        }
                        title={
                          tenantDocReadOnly
                            ? "Not available with viewer (read-only) access"
                            : "Remove duplicate content from this document"
                        }
                      >
                        Remove duplicates
                      </MenuItem>
                    </Tooltip>
                    {userRole != null &&
                      isInGroup(userRole, 'tenantSchema') &&
                      tenantRowAccess.canShareRow && (
                        <MenuItem
                          fontSize="sm"
                          closeOnSelect
                          icon={<FontAwesomeIcon icon={faShare} />}
                          isDisabled={isLoadingDocument || !validId}
                          title={`Share this ${getLabel(userRole, 'entity')?.toLowerCase() || "row"} with another user`}
                          onClick={async () => {
                            const supabase = await getSupabase();
                            const {
                              data: { session },
                            } = await supabase.auth.getSession();
                            if (!session?.user?.id) return;
                            const { data: row, error } = await supabase
                              .from(tableName)
                              .select("*")
                              .eq("id", validId)
                              .single();
                            if (error || !row) {
                              toast({
                                title: "Could not load row to share",
                                status: "error",
                              });
                              return;
                            }
                            setShareModalCurrentUserId(session.user.id);
                            // eslint-disable-next-line no-console
                            console.log(
                              "[EditableDocumentPage DEBUG] setShareSchedule",
                              {
                                rowKeys: row ? Object.keys(row) : null,
                                user_id: row?.user_id,
                                id: row?.id,
                              },
                            );
                            setShareSchedule(row);
                            setShareModalOpen(true);
                          }}
                        >
                          Share{" "}
                          {getLabel(userRole, 'entity')?.toLowerCase() || "row"}
                        </MenuItem>
                      )}
                    {!isCombined && (
                      <Tooltip
                        label="Read-only while viewing as another user"
                        isDisabled={!isImpersonating()}
                        hasArrow
                      >
                        <MenuItem
                          fontSize="sm"
                          closeOnSelect
                          onClick={handleDeleteDocumentAndLoadTemplate}
                          isDisabled={
                            isLoadingDocument ||
                            tenantDocReadOnly ||
                            isImpersonating()
                          }
                          title={
                            tenantDocReadOnly
                              ? "Not available with viewer (read-only) access"
                              : "Delete the saved document and reload the default template"
                          }
                        >
                          Reload template
                        </MenuItem>
                      </Tooltip>
                    )}
                    <Box
                      px={2}
                      py={2}
                      borderTopWidth="1px"
                      borderColor="gray.100"
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <Text fontSize="xs" fontWeight="semibold" mb={1} color="gray.600">
                        Combine
                      </Text>
                      <Box
                        display="inline-flex"
                        sx={{
                          "& > button": {
                            size: "xs !important",
                            px: "6px !important",
                            fontSize: "11px !important",
                            minW: "0 !important",
                            width: "auto !important",
                            h: "24px !important",
                            lineHeight: "1.2 !important",
                            whiteSpace: "nowrap !important",
                          },
                        }}
                      >
                        <CombineDocument date_={effectiveDate} id={id} />
                      </Box>
                    </Box>
                  </MenuList>
                </Menu>
              )}
            </div>
            {/* Primary row: Forms / Process forms, Saved */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              {!isMagicNotes &&
                userRole != null &&
                !isInGroup(userRole, 'tenantSchema') && (
                  <FormsButtons
                    hasQualifyingForms={hasQualifyingForms}
                    isCheckingQualifyingForms={isCheckingQualifyingForms}
                    onViewForms={handleViewForms}
                    onProcessForms={handleGenerateFromForms}
                    size="xs"
                    px={2}
                    debugLabel="EditableDocumentPage"
                    debugPatientId={
                      actualPatientId || validId || id || uuid
                    }
                    debugPatientName={extractedName || "(none)"}
                    debugScheduledDate={effectiveDate || "(none)"}
                  />
                )}
              <div
                className="save-status"
                style={{ marginLeft: "auto", flexShrink: 0 }}
              >
                {isSaving ? (
                  <>
                    <Spinner size="sm" style={{ marginRight: "4px" }} />
                    <Text color="red.500" fontSize="sm">
                      Saving
                    </Text>
                  </>
                ) : saveSuccess ? (
                  <Text color="green.500" fontSize="sm">
                    Saved
                  </Text>
                ) : null}
                <Tooltip label="Open collaboration debug panel" fontSize="xs" ml={2}>
                  <IconButton
                    aria-label="Debug collaboration"
                    icon={<FontAwesomeIcon icon={faBug} />}
                    size="xs"
                    variant="ghost"
                    colorScheme="gray"
                    onClick={() => setIsDebugPanelOpen(true)}
                  />
                </Tooltip>
              </div>
            </div>
            {/* Secondary row: Share document link, reports */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                flexWrap: "wrap",
                width: "100%",
                justifyContent: "flex-start",
              }}
            >
              {!isMagicNotes && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    flexWrap: "wrap",
                    justifyContent: "flex-start",
                  }}
                >
                  {documentRowId && isDriveDocOwner && (
                    <Tooltip
                      label="Read-only while viewing as another user"
                      isDisabled={!isImpersonating()}
                      hasArrow
                    >
                      <ChakraButton
                        size="xs"
                        colorScheme="teal"
                        variant="outline"
                        leftIcon={<FontAwesomeIcon icon={faShare} />}
                        title="Share this document with a user or copy a link to invite"
                        isDisabled={isLoadingDocument || isImpersonating()}
                        px={2}
                        onClick={() => {
                          setShareDocumentLink("");
                          setShareDocumentModalOpen(true);
                        }}
                      >
                        Share document
                      </ChakraButton>
                    </Tooltip>
                  )}
                  {hasReports && (
                    <ChakraButton
                      size="xs"
                      colorScheme="blue"
                      onClick={(e) => {
                        e.stopPropagation();
                        const reportsPath =
                          patientData?.[data_column] ||
                          patientData?.[file_reference_column];
                        const dateSpecificPath = getDateSpecificReportsPath(
                          reportsPath,
                          effectiveDate,
                        );
                        handleViewReports(dateSpecificPath);
                      }}
                      title="View patient reports for this date"
                      isDisabled={isLoadingDocument}
                      px={2}
                    >
                      View Reports
                    </ChakraButton>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Multi-selection indicator */}
          {showMultiSelectIndicator && (
            <div
              style={{
                position: "fixed",
                top: "10px",
                right: "10px",
                backgroundColor: "#3182ce",
                color: "white",
                padding: "8px 12px",
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: "bold",
                zIndex: 1000,
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                animation: "pulse 1s infinite",
              }}
            >
              Multi-Select Mode Active ({multiSelections.length} selections)
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div
        className="document-sidebar main-content-area"
        style={{ visibility: hasRenderedInitialDocument ? "visible" : "hidden" }}
      >
        <button onClick={toggleSidebar} className="sidebar-toggle-button">
          <FontAwesomeIcon
            icon={isSidebarVisible ? faChevronLeft : faBars}
            className="mr-1"
          />
        </button>

        <div
          className={`flex flex-col duration-300 fixed ${
            isSidebarVisible ? "translate-x-0" : "-translate-x-full"
          }`}
          style={{
            width: "220px",
            top: "160px", // Start right under the navbar
            height: "calc(100vh - 160px)", // Full height minus navbar
            overflow: "hidden", // Prevent overflow to allow proper scrolling inside
            zIndex: 0,
            backgroundColor: "transparent",
            pointerEvents: "none", // Will be overridden by SideBar's pointerEvents: auto
          }}
        >
          {isSidebarVisible && (
            <SideBar
              sections={sections}
              activeSection={activeSection}
              onSectionClick={handleSectionClick}
              toggleSidebar={toggleSidebar}
              isCombined={isCombined}
            />
          )}
        </div>

        <div
          className="flex flex-col flex-1"
          style={{
            marginLeft: isSidebarVisible ? "215px" : "0",
            transition: "margin-left 0.3s ease-in-out",
            backgroundColor: "transparent",
            overflow: "visible",
            pointerEvents: isLoadingDocument ? "none" : "auto",
          }}
        >
          <div
            className="flex justify-start py-2 flex-1"
            style={{
              overflow: "visible",
              pointerEvents: isLoadingDocument ? "none" : "auto",
            }}
          >
            <div
              className="rounded-md w-full"
              style={{
                overflow: "visible",
                pointerEvents: isLoadingDocument ? "none" : "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: "24px",
                  width: "100%",
                  overflow: "visible",
                }}
              >
                <div
                  className="bg-white min-h max-w-full"
                  style={{
                    overflow: "visible",
                    pointerEvents: isLoadingDocument ? "none" : "auto",
                    position: "relative",
                    zIndex: 5,
                    flex: "1 1 auto",
                    maxWidth: "850px",
                  }}
                >
                  <TextEditor
                    ref={editorRef}
                    showEditorOnly={true}
                    quillRefs={quillRefs}
                    containerRefs={containerRefs}
                    pages={pages}
                    onVoiceTypingResult={handleVoiceTypingResult}
                    multiSelections={multiSelections}
                    voiceTypingEnabled={!tenantDocReadOnly}
                  />

                  {/* Portal comment bubbles and active comment cards to their respective page wrappers */}
                  {commentBubblePositions.map((pos) => {
                    const pageIdx = pos.comment.selection_range?.pageIndex || 0;
                    const targetContainer = containerRefs.current[pageIdx];
                    if (!targetContainer) return null;
                    
                    const isActive = activeCommentId === pos.id;
                    
                    return createPortal(
                      isActive ? (
                        <div
                          key={pos.id}
                          id={`comment-card-${pos.id}`}
                          style={{
                            position: 'absolute',
                            top: `${pos.cardTop}px`,
                            left: `${pos.cardLeft}px`,
                            zIndex: 100,
                            width: '260px',
                            background: '#fff',
                            padding: '12px',
                            borderRadius: '8px',
                            border: '1px solid #ced4da',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#495057' }}>
                              {pos.comment.user_name || 'User'}
                            </span>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResolveComment(pos.comment);
                                }}
                                style={{
                                  fontSize: '9px',
                                  background: '#ebfbee',
                                  color: '#2b8a3e',
                                  border: '1px solid #b2f2bb',
                                  borderRadius: '4px',
                                  padding: '2px 6px',
                                  cursor: 'pointer',
                                }}
                              >
                                Resolve
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveCommentId(null);
                                }}
                                style={{
                                  fontSize: '10px',
                                  background: 'none',
                                  color: '#868e96',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '2px 4px',
                                }}
                                title="Minimize"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                          <p style={{ fontSize: '12px', color: '#212529', whiteSpace: 'pre-wrap', margin: 0 }}>
                            {pos.comment.content}
                          </p>
                        </div>
                      ) : (
                        <button
                          key={pos.id}
                          onClick={() => {
                            setActiveCommentId(pos.id);
                          }}
                          style={{
                            position: 'absolute',
                            top: `${pos.top}px`,
                            left: `${pos.left}px`,
                            zIndex: 10,
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: pos.comment.highlight_color || '#FEF08A',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                            cursor: 'pointer',
                            border: 'none',
                            transition: 'transform 0.2s, background-color 0.2s',
                          }}
                          title={pos.comment.content}
                          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.15)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1.0)'; }}
                        >
                          <FontAwesomeIcon icon={faComment} style={{ fontSize: '11px' }} />
                        </button>
                      ),
                      targetContainer
                    );
                  })}

                  {/* Portal new comment input window directly to its page wrapper */}

                  {isAddingComment && commentFloatPosition && selectedRange && (() => {
                    const pageIdx = selectedRange.pageIndex || 0;
                    const targetContainer = containerRefs.current[pageIdx];
                    if (!targetContainer) return null;
                    return createPortal(
                      <div
                        className="comment-float-input"
                        style={{
                          position: "absolute",
                          top: `${commentFloatPosition.top}px`,
                          left: `${commentFloatPosition.left}px`,
                          zIndex: 100,
                          width: "260px",
                          background: "#fff",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "1px solid #2196f3",
                          boxShadow: "0 4px 12px rgba(33, 150, 243, 0.2)",
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        <p style={{ fontSize: "10px", color: "#6c757d", margin: 0 }}>
                          New Comment on: "{selectedText.length > 25 ? selectedText.substring(0, 22) + "..." : selectedText}"
                        </p>
                        <textarea
                          placeholder="Add a comment..."
                          value={newCommentText}
                          onChange={(e) => setNewCommentText(e.target.value)}
                          autoFocus
                          style={{
                            width: "100%",
                            fontSize: "12px",
                            padding: "6px",
                            borderRadius: "4px",
                            border: "1px solid #ced4da",
                            minHeight: "50px",
                            resize: "none",
                            outline: "none",
                          }}
                        />
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span style={{ fontSize: "10px", color: "#6c757d" }}>Highlight:</span>
                          <div style={{ display: "flex", gap: "4px" }}>
                            {HIGHLIGHT_COLORS.map((color) => (
                              <button
                                key={color}
                                onClick={() => setSelectedHighlightColor(color)}
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  borderRadius: "50%",
                                  background: color,
                                  border: selectedHighlightColor === color ? "2px solid #2196f3" : "1px solid #ced4da",
                                  cursor: "pointer",
                                  padding: 0,
                                  transition: "transform 0.1s",
                                }}
                                title={color}
                              />
                            ))}
                          </div>
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px" }}>
                          <button
                            onClick={() => {
                              setIsAddingComment(false);
                              setNewCommentText("");
                              setSelectedHighlightColor('#FEF08A');
                            }}
                            style={{
                              fontSize: "10px",
                              background: "none",
                              border: "none",
                              color: "#868e96",
                              cursor: "pointer",
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveComment}
                            style={{
                              fontSize: "10px",
                              background: "#2196f3",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              cursor: "pointer",
                            }}
                          >
                            Comment
                          </button>
                        </div>
                      </div>,
                      targetContainer
                    );
                  })()}

                  {isInGroup(userRole, 'tenantSchema') &&
                    !tenantShareAllowsDocumentEdit(tenantRowAccess) &&
                    !isLoadingDocument && (
                      <div
                        aria-hidden
                        style={{
                          position: "absolute",
                          inset: 0,
                          zIndex: 20,
                          background: "rgba(255,255,255,0.2)",
                          pointerEvents: "none",
                          cursor: "text",
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          paddingTop: "24px",
                        }}
                      >
                        <Text
                          bg="white"
                          px={4}
                          py={2}
                          borderRadius="md"
                          boxShadow="md"
                          fontSize="sm"
                          color="gray.700"
                          pointerEvents="auto"
                        >
                          Read-only — select text to copy; editing is disabled.
                        </Text>
                      </div>
                    )}
                </div>

                {/* Document Comments Side Panel */}
                {showCommentsSidebar && (
                  <div
                    className="comments-sidebar"
                    style={{
                      width: "320px",
                      flex: "0 0 320px",
                      padding: "16px",
                      background: "#f8f9fa",
                      border: "1px solid #e9ecef",
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      maxHeight: "calc(100vh - 180px)",
                      position: "sticky",
                      top: "140px",
                      overflowY: "auto",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                      <h3
                        style={{
                          fontSize: "15px",
                          fontWeight: "bold",
                          color: "#495057",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <FontAwesomeIcon icon={faComment} /> Comments
                      </h3>
                      <button
                        onClick={() => setShowCommentsSidebar(false)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#999",
                          fontSize: "14px",
                          padding: "4px"
                        }}
                        title="Hide comments"
                      >
                        ✕
                      </button>
                    </div>

                    {comments.length === 0 ? (
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#868e96",
                          textAlign: "center",
                          marginTop: "24px",
                          lineHeight: "1.5",
                        }}
                      >
                        No comments yet.<br />Highlight text in the document and click the comment bubble to add one.
                      </p>
                    ) : (
                      comments.map((comment) => (
                        <div
                          key={comment.id}
                          id={`comment-card-${comment.id}`}
                          onClick={() => {
                            if (comment.selection_range) {
                              const { index, length, pageIndex } = comment.selection_range;
                              const quill = quillRefs.current[pageIndex || 0];
                              if (quill) {
                                quill.focus();
                                let targetIndex = index;
                                let targetLength = length;
                                if (comment.highlight_text) {
                                  const text = quill.getText();
                                  const sub = text.substring(targetIndex, targetIndex + targetLength);
                                  if (sub !== comment.highlight_text) {
                                    let foundIdx = text.indexOf(comment.highlight_text);
                                    while (foundIdx !== -1) {
                                      const format = quill.getFormat(foundIdx, 1);
                                      if (format && format.background) {
                                        targetIndex = foundIdx;
                                        targetLength = comment.highlight_text.length;
                                        break;
                                      }
                                      foundIdx = text.indexOf(comment.highlight_text, foundIdx + 1);
                                    }
                                  }
                                }
                                // Scroll and center in the page viewport
                                const leaf = quill.getLeaf(targetIndex);
                                if (leaf && leaf[0]) {
                                  let domNode = leaf[0].domNode;
                                  if (domNode.nodeType === Node.TEXT_NODE) {
                                    domNode = domNode.parentElement;
                                  }
                                  if (domNode && typeof domNode.scrollIntoView === 'function') {
                                    domNode.scrollIntoView({
                                      behavior: 'smooth',
                                      block: 'center',
                                      inline: 'nearest'
                                    });
                                  }
                                }
                                setActiveCommentId(comment.id);
                              }
                            }
                          }}
                          style={{
                            background:
                              comment.status === "resolved"
                                ? "#f8f9fa"
                                : comment.id === activeCommentId
                                ? "#e7f5ff"
                                : "#fff",
                            padding: "12px",
                            borderRadius: "8px",
                            border: comment.id === activeCommentId ? "1px solid #74c0fc" : "1px solid #e9ecef",
                            opacity: comment.status === "resolved" ? 0.6 : 1,
                            boxShadow: comment.id === activeCommentId ? "0 2px 8px rgba(34, 139, 230, 0.15)" : "0 1px 3px rgba(0,0,0,0.02)",
                            position: "relative",
                            transition: "background-color 0.2s, border-color 0.2s",
                            cursor: "pointer",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: "6px",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <div
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  borderRadius: "50%",
                                  background: "#e7f5ff",
                                  color: "#228be6",
                                  fontSize: "11px",
                                  fontWeight: "bold",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {(comment.user_name || "A")
                                  .substring(0, 1)
                                  .toUpperCase()}
                              </div>
                              <div>
                                <p
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    color: "#495057",
                                  }}
                                >
                                  {comment.user_name || "User"}
                                </p>
                                <p style={{ fontSize: "9px", color: "#adb5bd" }}>
                                  {new Date(comment.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: "2px" }}>
                              {comment.status === "open" && (
                                <ChakraButton
                                  size="xs"
                                  variant="outline"
                                  colorScheme="green"
                                  h="20px"
                                  px="6px"
                                  fontSize="9px"
                                  onClick={() => handleResolveComment(comment)}
                                >
                                  Resolve
                                </ChakraButton>
                              )}
                              <IconButton
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                h="20px"
                                w="20px"
                                icon={<FontAwesomeIcon icon={faTrash} />}
                                onClick={() => handleDeleteComment(comment)}
                              />
                            </div>
                          </div>
                          <p
                            style={{
                              fontSize: "12.5px",
                              color: "#343a40",
                              marginTop: "4px",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {comment.content}
                          </p>
                          {comment.highlight_text && (
                            <p
                              style={{
                                fontSize: "10.5px",
                                color: "#868e96",
                                fontStyle: "italic",
                                background: comment.highlight_color ? `${comment.highlight_color}40` : "#f8f9fa",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                marginTop: "6px",
                                borderLeft: "2px solid #ced4da",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={comment.highlight_text}
                            >
                              "{comment.highlight_text}"
                            </p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              
            </div>
          </div>
        </div>

        <RightSideBar
          names={patients}
          togglePatientList={togglePatientList}
          isPatientListOpen={isPatientListOpen}
          navigate={navigate}
          date={effectiveDate}
          editorRef={editorRef}
          date_={effectiveDate}
          id={id}
          userInfo={userInfo}
          validId={validId}
          liveTranscriptionEnabled={liveTranscriptionEnabled}
          setLiveTranscriptionEnabled={setLiveTranscriptionEnabled}
          liveFactExtractionEnabled={liveFactExtractionEnabled}
          setLiveFactExtractionEnabled={setLiveFactExtractionEnabled}
          autoSaveSetting={autoSaveSetting}
          setAutoSaveSetting={setAutoSaveSettingState}
          setShowPersistentComponent={setShowPersistentComponent}
          setStateData={setStateData}
          setRefId={setRefId}
          setOverride={setOverride}
          setUserManuallyChangedAutoSave={setUserManuallyChangedAutoSave}
          handleLiveTranscriptionChange={handleLiveTranscriptionChange}
          handleLiveFactExtractionChange={handleLiveFactExtractionChange}
          handleAutoSaveChange={handleAutoSaveChange}
          setCondition={setCondition}
          setSession={setSession}
          setCondFunc={setCondFunc}
          patientData={patientData}
          actualPatientId={actualPatientId}
          condition={condition}
          isMagicNotes={isMagicNotes}
          filePath={filePath}
          viewerTimezone={viewerTimezone}
        />
      </div>

      {/* Template Prompt Modal */}
      {/* Share row with other users (tenant: student, socialworker, other) */}
      <ShareRowModal
        isOpen={shareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
          setShareSchedule(null);
        }}
        title={`Share ${shareSchedule?.[nameField] || getLabel(userRole, 'entity')?.toLowerCase() || "visit"} with user`}
        loading={shareModalLoading}
        users={shareModalUsers}
        currentUserId={shareModalCurrentUserId}
        ownerId={shareSchedule?.user_id}
        alreadySharedIds={shareModalAlreadySharedIds}
        onShare={handleAddSharedUser}
        onRemove={handleRemoveSharedUser}
        removingId={shareModalRemovingId}
        sharedUserRoles={shareModalSharedUserRoles}
        onRoleChange={handleUpdateShareModalRole}
        updatingRoleId={shareModalUpdatingRoleId}
      />

      {/* Share document (drive / Magic Notes): search user or copy link */}
      <ShareRowModal
        isOpen={shareDocumentModalOpen}
        onClose={() => {
          setShareDocumentModalOpen(false);
          setShareDocumentLink("");
        }}
        title="Share document"
        loading={shareDocumentLoading}
        users={shareDocumentUsers}
        currentUserId={shareDocumentCurrentUserId}
        alreadySharedIds={shareDocumentAlreadySharedIds}
        onShare={handleAddSharedUserDocument}
        onRemove={handleRemoveSharedUserDocument}
        removingId={shareDocumentRemovingId}
        allowAllRoles={true}
        footerExtra={
          documentRowId ? (
            <HStack mr={3} spacing={2}>
              <ChakraButton
                size="sm"
                variant="outline"
                leftIcon={<FontAwesomeIcon icon={faCopy} />}
                onClick={() => {
                  const base =
                    window.location.origin +
                    window.location.pathname.replace(/[^/]*$/, "");
                  const link = `${base}edit-document?docId=${documentRowId}`;
                  setShareDocumentLink(link);
                  navigator.clipboard.writeText(link).then(
                    () =>
                      toast({
                        title: "Link copied to clipboard",
                        status: "success",
                        duration: 2000,
                        isClosable: true,
                      }),
                    () =>
                      toast({
                        title: "Could not copy link",
                        status: "error",
                        isClosable: true,
                      }),
                  );
                }}
              >
                Copy link
              </ChakraButton>
            </HStack>
          ) : null
        }
      />

      <Modal
        isOpen={storageOversizeModalOpen}
        onClose={() => setStorageOversizeModalOpen(false)}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>File too large to load</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={2}>
              The document in storage exceeds the size limit, so it was not
              downloaded. This avoids loading very large JSON into the browser.
            </Text>
            <Text fontSize="sm" color="gray.600">
              {storageOversizeMessage}
            </Text>
            <Text mt={3} fontSize="sm">
              Create a new document from the usual template for this visit type?
              Saving later will replace the oversized file at this path when you
              save.
            </Text>
          </ModalBody>
          <ModalFooter>
            <ChakraButton
              variant="ghost"
              mr={3}
              onClick={() => {
                setStorageOversizeModalOpen(false);
                setDocumentLoaded(true);
                setContentLoadingComplete(true);
                setIsLoadingDocument(false);
                try {
                  editorRef.current?.getEditor(0)?.setContents([], "api");
                } catch (_) {
                  /* ignore */
                }
                quillRefs.current.forEach((quill) => {
                  if (quill) quill.enable(true);
                });
              }}
            >
              Cancel
            </ChakraButton>
            <ChakraButton
              colorScheme="blue"
              onClick={() => {
                setStorageOversizeModalOpen(false);
                forceOversizeNewDocumentRef.current = true;
                if (filePath) {
                  fetchDocument(filePath, 0);
                }
              }}
            >
              Create new
            </ChakraButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isTemplatePromptOpen}
        onClose={() => setIsTemplatePromptOpen(false)}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Document Not Found or Empty</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              The previous visit document could not be found or is empty. Would
              you like to create a new document from a template?
            </Text>
          </ModalBody>
          <ModalFooter>
            <ChakraButton
              variant="ghost"
              mr={3}
              onClick={() => {
                setIsTemplatePromptOpen(false);
                // Initialize empty document
                const editor = editorRef.current?.getEditor(0);
                if (editor) {
                  editor.setContents({ ops: [{ insert: "\n" }] }, "api");
                  setLoadedTemplateType("Blank");
                  setDocumentLoaded(true);
                }
                setIsLoadingDocument(false);
                quillRefs.current.forEach((quill) => {
                  if (quill) {
                    quill.enable(true);
                  }
                });
                setContentLoadingComplete(true);
              }}
            >
              No, Start Blank
            </ChakraButton>
            <ChakraButton
              colorScheme="blue"
              onClick={async () => {
                setIsTemplatePromptOpen(false);
                setShouldUseTemplate(true);
                setIsWaitingForTemplateDecision(false);

                // Apply template directly
                const editor = editorRef.current?.getEditor(0);
                if (!editor) {
                  console.error(
                    "Editor not available for template application",
                  );
                  return;
                }

                // Calculate age if DOB is available
                let calculatedAge = "___";
                try {
                  if (patientData && patientData.dob) {
                    calculatedAge = calculateAge(
                      patientData.dob,
                      effectiveDate,
                    ).toString();
                  } else {
                    // Try to fetch DOB from database
                    const supabase = await getSupabase();
                    const { data: patientRecord } = await supabase
                      .from(tableName)
                      .select("dob")
                      .eq(prompts[0][0], extractedName)
                      .order("created_at", { ascending: false })
                      .limit(1)
                      .single();

                    if (patientRecord && patientRecord.dob) {
                      calculatedAge = calculateAge(
                        patientRecord.dob,
                        effectiveDate,
                      ).toString();
                    }
                  }
                } catch (error) {
                  console.warn("Error calculating age for template:", error);
                }

                // Get the actual template key. SOC/STU are aliases for SO/ST templates.
                const patientType = getTemplateTypeForCurrentDocument();
                const template = documentTemplates[patientType];

                if (!template) {
                  console.error(`Template not found for type: ${patientType}`);
                  toast({
                    title: "Template Not Found",
                    description: `No template available for type: ${patientType}`,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                  });
                  // Initialize empty document as fallback
                  editor.setContents({ ops: [{ insert: "\n" }] }, "api");
                  setDocumentLoaded(true);
                  setIsLoadingDocument(false);
                  quillRefs.current.forEach((quill) => {
                    if (quill) quill.enable(true);
                  });
                  setContentLoadingComplete(true);
                  return;
                }

                // Apply template
                console.log(`Applying template for type: ${patientType}`);
                const deltaContent = template.delta || { ops: [] };

                // Replace placeholders in template
                deltaContent.ops.forEach((op, index) => {
                  if (op.insert && typeof op.insert === "string") {
                    const patientTime =
                      patientScheduledTime ||
                      navbarTime ||
                      new Date().toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      });
                    const ehrIdForHeading = id || "";
                    op.insert = op.insert
                      .replace(/\[NAME\]/g, extractedName || "")
                      .replace(/\[ID\]/g, ehrIdForHeading)
                      .replace(/\[Patient Name\]/g, extractedName || "")
                      .replace(/\[MRN\]/g, ehrIdForHeading)
                      .replace(/\[time\]/g, patientTime)
                      .replace(/\[age\]/g, calculatedAge || "___");
                  }
                });

                editor.setContents(deltaContent, "api");
                setLoadedTemplateType(patientType);
                setDocumentLoaded(true);
                setIsLoadingDocument(false);
                quillRefs.current.forEach((quill) => {
                  if (quill) quill.enable(true);
                });
                setContentLoadingComplete(true);

                // Generate sections from template
                setTimeout(() => {
                  const lines = editor.getLines();
                  const newSections = [];
                  lines.forEach((line) => {
                    const text = line.domNode?.innerText.trim();
                    const format = editor.getFormat(editor.getIndex(line));
                    if (format.header) {
                      const sectionId = `section-${newSections.length}`;
                      newSections.push({
                        id: sectionId,
                        title: text,
                        level: format.header,
                        isEditing: false,
                        createdAt: new Date().toISOString(),
                        lastupdated: new Date().toISOString(),
                      });
                    }
                  });
                  setSections(newSections);
                  handleSaveDoc();
                }, 500);
              }}
            >
              Yes, Use Template
            </ChakraButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Password Modal */}
      <PasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => {
          setIsPasswordModalOpen(false);
          setPendingAction(null);
        }}
        onSuccess={() => {
          if (pendingAction) {
            pendingAction();
            setPendingAction(null);
          }
        }}
        title="Enable Live Transcription"
      />

      {/* Process form Prompt Editor Modal (shared component) */}
      <ProcessFormPromptEditor
        isOpen={isPromptEditorOpen}
        onClose={() => setIsPromptEditorOpen(false)}
        onRun={handleFormProcessingRun}
        onDocAsTemplate={
          // Only expose for medical/clinical roles
          isInGroup(userRole, "clinical")
            ? handleDocAsTemplateRun
            : undefined
        }
        patientType={currentPatientType}
        initialData={promptEditorInitialData}
        toast={toast}
      />

      {/* Doc-as-Template Diff Viewer */}
      <DocDiffViewer
        isOpen={isDiffViewerOpen}
        onClose={handleCancelDiff}
        originalText={diffOriginalText}
        newText={diffNewText}
        isLoading={isGeneratingDiff}
        onApply={handleApplyDiffChanges}
      />

      {/* Document processing lock overlay */}
      {isDocProcessingLocked && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={9999}
          pointerEvents="all"
          display="flex"
          alignItems="flex-start"
          justifyContent="center"
          pt="60px"
          cursor="not-allowed"
        >
          <Box
            bg="blue.600"
            color="white"
            px={5}
            py={2}
            borderRadius="md"
            boxShadow="lg"
            display="flex"
            alignItems="center"
            gap={3}
            cursor="default"
          >
            <Spinner size="sm" color="white" />
            <Text fontSize="sm" fontWeight="medium">
              Document is locked — AI is processing forms using doc as template
            </Text>
          </Box>
        </Box>
      )}

      {/* Forms Modal - Using shared FormsModal component */}
      <FormsModal
        isOpen={isFormsModalOpen}
        onClose={() => setIsFormsModalOpen(false)}
        patientId={actualPatientId || validId || id || uuid}
        onProcessForms={(qualifyingFormLinks) => {
          console.log(
            "🔍 EditableDocumentPage: Processing qualifying forms from modal:",
            qualifyingFormLinks,
          );
          handleGenerateFromForms(qualifyingFormLinks);
        }}
      />

      {/* Reports Modal - handled by ResultsTable component (same as PatientPage) */}
      {modalIsOpen && (
        <ResultsTable
          results={[]}
          setResults={() => {}}
          prompts={prompts}
          selectedRows={{}}
          handleCheckboxChange={() => {}}
          handleViewJSONFiles={() => {}}
          handleViewFolderFiles={handleViewReports}
          currentFolderPath={currentFolderPath}
          previousFolderPath={previousFolderPath}
          handleGoUpOneLevel={handleGoUpOneLevel}
          handleGoDownOneLevel={handleGoDownOneLevel}
          supabaseUrl={supabaseUrl}
          bucketName_files={bucketName_files}
          modalIsOpen={modalIsOpen}
          setModalIsOpen={setModalIsOpen}
          currentFolderFiles={currentFolderFiles}
          showCheckboxes={false}
          showFiles={true}
          onProcessReport={handleProcessReport}
          viewerTimezone={viewerTimezone}
        />
      )}

      {/* Process Report Prompt Editor Modal (shared component) */}
      {isReportPromptEditorOpen && (
        <ProcessReportPromptEditor
          isOpen={true}
          onClose={() => setIsReportPromptEditorOpen(false)}
          onRun={handleReportProcessingRun}
          reportType={currentReportType}
          initialData={reportPromptEditorInitialData}
          toast={toast}
        />
      )}

      {/* Copy from Older Doc Modal */}
      <Modal
        isOpen={copyFromOlderOpen}
        onClose={() => setCopyFromOlderOpen(false)}
        isCentered
        size="sm"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Copy older notes</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            {copyFromOlderLoading ? (
              <HStack spacing={3} py={2}>
                <Spinner size="sm" />
                <Text>Searching…</Text>
              </HStack>
            ) : copyFromOlderMatch ? (
              <>
                <Text>
                  Notes from <strong>{convertDate(copyFromOlderMatch.row.date_)}</strong>
                  . Copy in?
                </Text>
                {/* Which chart it landed on. The search checks the name (and
                    the EHR ID when both rows carry a real one), but the user
                    still gets to see it before overwriting anything. */}
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {formatPatientLabel({
                    patient_name: copyFromOlderMatch.row[prompts[0][0]],
                    EHR_ID: copyFromOlderMatch.row.EHR_ID,
                  })}
                </Text>
              </>
            ) : (
              <Text>No older notes found.</Text>
            )}
          </ModalBody>
          {!copyFromOlderLoading && (
            <ModalFooter>
              {copyFromOlderMatch ? (
                <>
                  <ChakraButton
                    colorScheme="blue"
                    mr={3}
                    onClick={handleCopyFromOlderConfirm}
                  >
                    Copy
                  </ChakraButton>
                  <ChakraButton onClick={() => setCopyFromOlderOpen(false)}>
                    No
                  </ChakraButton>
                </>
              ) : (
                <ChakraButton onClick={() => setCopyFromOlderOpen(false)}>
                  Close
                </ChakraButton>
              )}
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>

      {/* Collaboration Debug Panel Modal */}
      <CollaborationDebugPanel
        isOpen={isDebugPanelOpen}
        onClose={() => setIsDebugPanelOpen(false)}
        connectionStatus={connectionStatus}
        isConnected={isConnected}
        filePath={filePath}
        presenceList={[]}
      />
    </div>
  );
}

// RightSideBar component has been moved to ../components/RightSideBar.js
